/**
 * Babylon.js stylized gray-box renderer. Pure consumer of sim state — no game
 * logic. Third-person follow camera above and behind the robot, framed in the
 * lower third (portrait). Lab levels keep their fixed-yaw camera; the district
 * mission (ADR-022) follows the CONTROL-FRAME yaw (the road direction), which
 * the sim already smooths.
 *
 * Mission world: the road network (bands + roundabout ring) is drawn as flat
 * ribbons from the same geometry the sim's signed-distance field uses, with
 * curbs that open wherever another road piece overlaps (junctions, ring
 * mouths). A voxel city fills everything off the road. All static geometry is
 * batched with thin instances / merged vertex buffers (a handful of draw
 * calls for ~5000 boxes) and frozen. Turn guidance (ADR-022): painted
 * chevrons before each junction and a floating arrow at the maneuver point.
 *
 * The robot carries its delivery box on top with two hands and a small corgi
 * tail; the tail and grip animate with the robot's "mood". All character /
 * vehicle meshes are cheap procedural modules (no skeletons).
 */

// Deep imports keep the mobile bundle small (root "@babylonjs/core" defeats tree-shaking).
import type { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";
import type { Skeleton } from "@babylonjs/core/Bones/skeleton";
import { HDRCubeTexture } from "@babylonjs/core/Materials/Textures/hdrCubeTexture";
import { PhotoDome } from "@babylonjs/core/Helpers/photoDome";
import { DefaultRenderingPipeline } from "@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/defaultRenderingPipeline";
import { ImageProcessingConfiguration } from "@babylonjs/core/Materials/imageProcessingConfiguration";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { CascadedShadowGenerator } from "@babylonjs/core/Lights/Shadows/cascadedShadowGenerator";
// Side-effect registration: without it the generator builds but the scene
// never renders the shadow map, and Babylon throws on the first frame.
import "@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent";
import { Engine } from "@babylonjs/core/Engines/engine";
import { Scene } from "@babylonjs/core/scene";
import { Matrix, Quaternion, Vector3, Vector4 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { FreeCamera } from "@babylonjs/core/Cameras/freeCamera";
import {
  DEFAULT_LENS,
  SENSOR,
  cameraPoseById,
  isCameraId,
  lensProfile,
  type CameraId,
} from "../core/cameraFrame";
import {
  CUBE_FACE_TIERS,
  attachFisheye,
  type FisheyeRig,
  type OpticsDescription,
  type OpticsQuality,
} from "./fisheye";
import { LoadAssetContainerAsync } from "@babylonjs/core/Loading/sceneLoader";
import type { AssetContainer } from "@babylonjs/core/assetContainer";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
// Side effect: registers the glTF 2.0 loader the Bulldog model needs.
import "@babylonjs/loaders/glTF/2.0";
// Registers .splat/.ply/.spz Gaussian-splat file loading (ADR-069): the world
// can be a neural reconstruction of the real street instead of the gray-box.
import "@babylonjs/loaders/SPLAT/splatFileLoader";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
// The StandardMaterial shader, imported STATICALLY so it lands in the main
// bundle.
//
// Babylon pulls these in with `import()` when a material is first drawn. In a
// production build that makes them separate chunks — ~140 KB across four files
// — fetched over the network AFTER the game is already running: measured at
// 375 ms after the game object existed, on a throttled mobile connection. Each
// arrival blocks the first draw of whatever needed it, which is felt as the
// picture hitching over and over during the opening seconds.
//
// It is invisible on a fast link, which is exactly why it read as "smooth on
// the machine itself, juddery over the tunnel" and survived several rounds of
// looking at the renderer.
import "@babylonjs/core/Shaders/default.vertex";
import "@babylonjs/core/Shaders/default.fragment";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import "@babylonjs/core/Meshes/thinInstanceMesh";
import "@babylonjs/core/Meshes/instancedMesh"; // side effect: Mesh.createInstance
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { CreateCylinder } from "@babylonjs/core/Meshes/Builders/cylinderBuilder";
import { CreateGround } from "@babylonjs/core/Meshes/Builders/groundBuilder";
import { CreateSphere, CreateSphereVertexData } from "@babylonjs/core/Meshes/Builders/sphereBuilder";
import { CreateTorus } from "@babylonjs/core/Meshes/Builders/torusBuilder";
import type { AgentModel, AgentType } from "../sim/agents";
import { PED_RADIUS, ROAD_EDGE_LINE_INSET_M } from "../sim/constants";
import type { DecorDef, MissionBuild, PropDef } from "../sim/mission";
import { streetTreePlacements, TREE_TRUNK_RADIUS_M, TREE_VERGE_WIDTH_M, TREE_KERB_WIDTH_M } from "../sim/trees";
import { pieceSdf, type BandPiece, type RingPiece, type RoadNetwork, type RoadPiece, pieceForward } from "../sim/network";
import { wrapAngle } from "../core/vec";
import type { DeliveryPath } from "../sim/path";
import type { Simulation } from "../sim/simulation";
import { CorridorLayer } from "./corridor";
import { variedColor, actorMotionVariation, idleGesture, type ActorMotionVariation } from "./variation";
import { DEFAULT_APPEARANCE_THEME, appearanceTheme, appearanceConfigHash, normalizeAppearanceTheme, themeAssetPath, themeColor, type AppearanceThemeId, type AppearanceManifest } from "./themes";

import { neighborhoodRoundedBox, buildNeighborhoodRobot, neighborhoodHouse } from './neighborhood';
import { buildAdventureBenchmarkWorld } from './adventureWorld';
import { buildAdventureScooter, type AdventureActor } from './adventureCast';
import { buildAdventurePedestrian } from './adventurePedestrian';
import { buildAdventureRobot } from './adventureRobot';
import { buildAdventureParkedCar } from './adventureParkedCar';
import { buildAdventureParkedScooter } from './adventureParkedScooter';
import { SSAO2RenderingPipeline } from "@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/ssao2RenderingPipeline";
import { AdventureMaterials } from './adventureMaterials';

type ThemeState = { id: AppearanceThemeId; lite: boolean; pending: Set<string>; failed: Set<string> };
const SCENE_THEME = new WeakMap<Scene, ThemeState>();
function themeState(scene: Scene): ThemeState { return SCENE_THEME.get(scene)!; }
function texturePath(scene: Scene, url: string): string | null {
  const key = url.split("textures/")[1] ?? url;
  const path = themeAssetPath(themeState(scene).id, "textures", key);
  return path ? `${import.meta.env.BASE_URL}${path}` : null;
}
function themedTexture(scene: Scene, url: string, onError?: () => void): Texture {
  const state = themeState(scene);
  state.pending.add(url);
  const texture = new Texture(url, scene, false, true, Texture.TRILINEAR_SAMPLINGMODE,
    () => state.pending.delete(url), () => {
      state.pending.delete(url); state.failed.add(url);
      // Baked Adventure chunks share the prototype texture. A failed atlas
      // must also release those references so their vertex-color fallback draws.
      if (state.id === "neighborhood") for (const material of scene.materials) {
        if (material instanceof StandardMaterial && material.diffuseTexture === texture) material.diffuseTexture = null;
      }
      onError?.();
    });
  return texture;
}


/** Camera offsets relative to the robot (m). Tuned for portrait lower-third framing. */
/**
 * Which camera renders. `follow` is the participant's third-person POV; the
 * five named views mount the REAL robot's cameras at their surveyed
 * extrinsics and render through the lens's own projection — that is what a
 * perception model would actually be fed.
 */
export type CameraView = "follow" | CameraId;

/** A panorama world as pano_world.py writes it (ADR-088). */
export interface PanoWorld {
  schema?: string;
  id?: string;
  /** URL prefix the station images hang off. */
  base: string;
  source?: string;
  /** Height the capture camera rode at, metres. */
  camHeight?: number;
  /** Radius of the sphere the photograph is painted on, metres. */
  radius?: number;
  length_m?: number;
  stations: { img: string; x: number; z: number; yaw: number | null }[];
}

/** The two playable viewpoints. Distinct from CameraView, which selects the
 * research sensor rig. */
export type PlayView = "third" | "first";

/**
 * Rendering budget. `auto` (the default) detects a software rasterizer and
 * drops to the `lite` tier; `full` and `lite` pin it either way (`?gfx=`),
 * which is also the lever for judging the lite tier's picture side by side.
 */
export type GfxTier = "auto" | "full" | "lite";

/**
 * Is WebGL here software-rasterized (SwiftShader, llvmpipe, Basic Render)?
 *
 * Asked of a throwaway context BEFORE the engine exists, because the one
 * choice that matters most — MSAA — is fixed at context creation and cannot
 * be toggled afterwards. Same probe recipe as PerfOverlay.describeDevice,
 * which exists for the same reason: a software rasterizer behaves nothing
 * like a GPU, and the renderer has to know which world it is in.
 */
function isSoftwareRenderer(): boolean {
  try {
    const c = document.createElement("canvas");
    const gl = c.getContext("webgl2") ?? c.getContext("webgl");
    if (!gl) return false;
    const dbg = gl.getExtension("WEBGL_debug_renderer_info");
    const name = String(
      dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER),
    );
    return /swiftshader|llvmpipe|softpipe|basic render|software/i.test(name);
  } catch {
    return false;
  }
}

/** A camera on the robot's own hull needs a near plane inside it. */
const ONBOARD_MIN_Z = 0.04;
const ONBOARD_MAX_Z = 600;

/**
 * The driver's-eye camera.
 *
 * Deliberately NOT the onboard sensor rig (`?view=front`): that reproduces the
 * RouteCAM's fisheye for capturing training frames, and a barrel-distorted
 * 103-degree lens is miserable to steer with. This is the same viewpoint —
 * the real front camera's height from config/robot_cameras.json — through an
 * ordinary rectilinear projection at a game field of view.
 *
 * Raised a little above the sensor and tilted down, because a lens at 0.65 m
 * on a machine 0.9 m long spends most of its frame looking at its own nose.
 */
/**
 * How far the robot drops into a hole, and how far over it goes (ADR-043).
 *
 * The drop is the wheel radius: the wheel is in, the axle is at the lip. The
 * pitch and roll are deliberately modest — this is a delivery robot settling
 * into a pothole, not a car rolling down a bank — but enough that the frame
 * plainly lurches.
 */
const PIT_DROP_M = 0.142;
const PIT_PITCH_RAD = 0.32;
const PIT_ROLL_RAD = 0.42;

/** Grass strip outside the kerb, and street-tree spacing along it (m). */
const VERGE_W = TREE_VERGE_WIDTH_M;
/** Boulevard furniture (ADR-053): lamp spacing, hedge run, footway depth. */
const LAMP_SPACING = 26;
const HEDGE_SEG = 2.6;
const PAVE_W = 2.4;
const LAMP_TINT: Rgb = [0.32, 0.33, 0.36];
const HEDGE_TINT: Rgb = [0.3, 0.45, 0.24];
const TREE_TINTS: readonly Rgb[] = [
  [1, 1, 1],
  [0.9, 0.96, 0.88],
  [0.82, 0.9, 0.8],
];

const FPV_HEIGHT = 0.95;
/**
 * Where the eye goes in a PHOTOGRAPHIC world, and why it is not the same place.
 *
 * 0.95 m is a comfortable driver's eye for a built world, which has geometry
 * everywhere and does not care where you look from. A reconstruction does care:
 * it only knows what a camera saw, and this robot's camera sits at 0.648 m. Two
 * measurements, taken together, say the sim was looking from somewhere the data
 * never was:
 *
 *   height  the eye sat 30 cm ABOVE every training view, and on a scene whose
 *           geometry is supported near the ground that is a real extrapolation
 *   field   the sim showed 66 deg vertical and 98 deg horizontal against the
 *           camera's 55.5 and 86.2, so a twelve-degree band all the way round
 *           was invention
 *
 * Rendering a held-out frame at a real camera pose came out clearly better than
 * the same scene in the sim, and this is the difference. A photographic map may
 * declare the camera that made it, and then the sim looks from exactly there.
 */
const PHOTOGRAPHIC_EYE = { height: 0.648, fov: 0.969 };
const FPV_FORWARD = 0.35;
const FPV_FOV = 1.15;
/**
 * How far the driver's-eye camera looks down, by shape of screen.
 *
 * One fixed angle cannot serve both. Babylon's `fov` is VERTICAL, so a tall
 * phone frame covers far more sky than a wide desktop one at the same pitch:
 * 0.22 rad framed the phone nicely and left a laptop staring at six metres of
 * its own tarmac. Interpolated on the aspect ratio, and recomputed on resize,
 * because a phone rotating is the same problem arriving mid-run.
 */
const FPV_PITCH_PORTRAIT = 0.22;
const FPV_PITCH_LANDSCAPE = 0.08;

const CAM_UP = 4.8;
const CAM_BACK = 8.0;
const LOOK_AHEAD = 5.5;
const LOOK_UP = 0.4;
/** The mission runs at 7 m/s down 100+ m streets: keep the horizon far. */
const FAR_CLIP = 260;
const NEAR_CLIP = 0.4;
/** Agents farther than this from the robot are not drawn (render-only LOD). */
const AGENT_DRAW_DIST = 90;

/** Painted chevrons before a turn (m relative to the maneuver point) and the
 * research-zone exclusion (no guidance paint at s < 52; the zone ends at 50). */
/**
 * Ground markings (map authoring): a dashed centre line on two-way roads,
 * arrows along one-way roads, and an edge line inside each kerb. Painted
 * from the same centreline the SDF uses, and skipped wherever another road
 * piece overlaps so junctions and roundabout mouths stay clear.
 */
const DASH_LEN = 2.0;
const DASH_GAP = 2.6;
const DASH_HALF_W = 0.08;
const ARROW_SPACING = 14;
const EDGE_INSET = ROAD_EDGE_LINE_INSET_M;
const EDGE_HALF_W = 0.06;
/** Marking paint sits just above its road so it never z-fights. */
const PAINT_LIFT = 0.006;
/** Arrow outline for a one-way lane, pointing +z, in metres. */
const LANE_ARROW: readonly P2[] = [
  { x: 0, z: 1.5 },
  { x: -0.55, z: 0.55 },
  { x: -0.18, z: 0.55 },
  { x: -0.18, z: -1.5 },
  { x: 0.18, z: -1.5 },
  { x: 0.18, z: 0.55 },
  { x: 0.55, z: 0.55 },
];
const LANE_ARROW_TRIS: readonly number[] = [0, 1, 2, 0, 2, 3, 0, 3, 4, 0, 4, 5, 0, 5, 6];

const CHEVRON_OFFSETS: readonly number[] = [-22, -18, -14, -10, -6];
const CHEVRON_MIN_S = 52;
const ARROW_HEIGHT = 3.0;
/** The floating arrow stands this far PAST the turn point so it never sits on
 * top of the robot while it is turning. */
const ARROW_PAST_M = 4.0;
/** Camera yaw smoothing (s). Long enough that a 90 deg junction turn sweeps
 * rather than snaps, short enough to keep the road ahead in frame. */
const CAM_TAU = 0.45;
/** Height of the roundabout marker above the island. */
const ROUNDABOUT_MARK_Y = 7.5;

/** Voxel city: grid step, box footprint, bounds padding, road keep-out. */
const CITY_CELL = 3;
const CITY_FOOTPRINT = 2.6;
const CITY_PAD = 12;
const CITY_MIN_SDF = 1.6;
/** Only a two-row street front is built (beyond it the fog does the work):
 * a full-map voxel city was ~4000 boxes rasterized every frame. */
const CITY_MAX_SDF = 9.5;
/** Buildings are batched per spatial chunk so off-screen chunks frustum-cull. */
const CITY_CHUNK = 36;
const DECOR_KEEPOUT = 2.8;

const CURB_LEN = 1.5;
/** Half-width of the sun's shadow box, and its map resolution (ADR-060). */
const SHADOW_SPAN = 34;
const SHADOW_MAP_PX = 2048;
const CURB_SIZE = TREE_KERB_WIDTH_M;
/** Painted kerb + gutter pan, straight off the OCP reference clips. */
const KERB_PAINT: Rgb = [0.9, 0.89, 0.86];
const KERB_CAP: Rgb = [0.74, 0.73, 0.71];
const GUTTER: Rgb = [0.66, 0.66, 0.65];
const GUTTER_W = 0.42;
/** The terracotta border course along the kerb edge of every OCP footway. */
const PAVE_TRIM: Rgb = [0.62, 0.42, 0.36];
const PAVE_TRIM_W = 0.5;
/** Tree pit soil and the wooden tripod that holds a young trunk up. */
const TREE_PIT: Rgb = [0.3, 0.26, 0.22];
const STAKE: Rgb = [0.46, 0.36, 0.24];
/** Cast-iron gully cover, set in the gutter every so often. */
const DRAIN: Rgb = [0.26, 0.26, 0.27];
/** Curb segments whose midpoint pushed this far outward lies inside another road are skipped. */
const CURB_PROBE = 0.25;

type Rgb = readonly [number, number, number];
const ROAD_STREET: Rgb = [0.55, 0.56, 0.59];
/** Lane paint. Off-white so it reads on grey without glaring. */
const ROAD_PAINT: Rgb = [0.88, 0.88, 0.85];
/** Worn road-marking yellow, sampled from the OCP centre lines. */
const CENTRE_PAINT: Rgb = [0.82, 0.68, 0.25];
/** Zebra crossings (ADR-054): stripe pitch, band depth, junction setback. */
const ZEBRA_STRIPE = 0.42;
const ZEBRA_DEPTH = 2.4;
const ZEBRA_SETBACK = 3.4;
const ZEBRA_MIN_ROAD = 14;
const ROAD_ALLEY: Rgb = [0.6, 0.6, 0.61];
const CURB: Rgb = [0.75, 0.75, 0.77];
const GROUND: Rgb = [0.7, 0.69, 0.66];
const ISLAND: Rgb = [0.6, 0.63, 0.57];
/**
 * How long each vehicle model really is, in metres (ADR-058).
 *
 * The sim's footprint is a physics abstraction — a moving motorbike is ONE
 * circle of radius 0.42, so scaling its model to that made a 0.84 m bike
 * with a doll on it, while the same bike parked (half-depth 0.9) rendered at
 * 1.8 m with a full-sized rider. Two sizes of person on the same street, from
 * the same asset. The picture is scaled from the real vehicle instead, and
 * the collision box goes on being whatever the sim needs.
 */
const MODEL_LENGTH_M: Record<string, number> = {
  bike_hondawave: 1.9,
  bike_hondaab: 1.9,
  bike_hondaactiva: 1.85,
  bike_exciter150: 2.0,
  scooter: 1.75,
  car_vf8: 4.75,
  truck: 4.9,
};

/** Ground-floor shopfronts: three signboard colourways, so a terrace is not
 * one shop photocopied down the street (ADR-057). */
const SHOPFRONTS = ["storefront.jpg", "storefront_b.jpg", "storefront_c.jpg"] as const;
const CITY_TINTS: readonly Rgb[] = [
  [0.64, 0.65, 0.68],
  [0.72, 0.69, 0.63],
  [0.57, 0.59, 0.64],
];
const ACCENT: Rgb = [0.96, 0.66, 0.16];
const DEST: Rgb = [0.25, 0.65, 0.4];
const SKIN: Rgb = [0.78, 0.66, 0.56];
const WHEEL: Rgb = [0.2, 0.2, 0.22];
const CAR_TINTS: readonly Rgb[] = [
  [0.62, 0.6, 0.58],
  [0.5, 0.56, 0.64],
  [0.7, 0.66, 0.6],
];
const CAR_CABIN: Rgb = [0.45, 0.48, 0.52];
const BUS_GLASS: Rgb = [0.32, 0.38, 0.45];
const BUS_SKIRT: Rgb = [0.3, 0.31, 0.34];
const GUIDE_NEAR: Rgb = [0.95, 0.82, 0.2];
const GUIDE_FAR: Rgb = [0.4, 0.8, 0.95];
const TRUCK_TINTS: readonly Rgb[] = [
  [0.72, 0.44, 0.24],
  [0.35, 0.42, 0.55],
  [0.55, 0.56, 0.58],
];
const BUS_TINTS: readonly Rgb[] = [
  [0.82, 0.72, 0.24],
  [0.3, 0.5, 0.72],
  [0.76, 0.4, 0.28],
];
const BRAKE: Rgb = [0.55, 0.12, 0.1];
const HEADLIGHT: Rgb = [0.82, 0.82, 0.72];
const INDICATOR: Rgb = [0.75, 0.5, 0.15];
const GROUP_SHIRTS: readonly Rgb[] = [
  [0.58, 0.56, 0.5],
  [0.42, 0.52, 0.62],
  [0.62, 0.45, 0.4],
  [0.46, 0.58, 0.48],
  [0.6, 0.55, 0.35],
];

/** Materials are cached per scene by colour: rebuilding a level (Retry,
 * Tweak restart) reuses them instead of leaking ~200 StandardMaterials. */
const SCENE_VARIATION = new WeakMap<Scene, number>();
function vary(scene: Scene, rgb: Rgb, id: string, role = ''): Rgb {
  return variedColor(rgb, SCENE_VARIATION.get(scene) ?? 1, themeState(scene).id, id, role);
}
function functionalColor(rgb: Rgb): boolean {
  return [ROAD_PAINT, CENTRE_PAINT, BRAKE, HEADLIGHT, INDICATOR, GUIDE_NEAR, GUIDE_FAR, ACCENT, DEST].includes(rgb);
}
const MAT_CACHE = new WeakMap<Scene, Map<string, StandardMaterial>>();
/**
 * A tiled photographic surface (ADR-047). `periodM` is how many metres one
 * repeat of the texture covers; the geometry's UVs are world metres, so the
 * scale is just its inverse. Tint multiplies the photo, which is how the
 * street and the alley share one asphalt while reading differently.
 */
/**
 * The one soft-shadow material. Built on the diffuse-alpha recipe the face
 * decal and the trees use, because it is the only alpha path with a working
 * example in this scene: the opacityTexture route rendered the blob first as
 * a solid slab (frozen defines), then as nothing at all.
 */
function blobShadowMat(scene: Scene): StandardMaterial {
  const existing = scene.getMaterialByName("blobShadowMat");
  if (existing) return existing as StandardMaterial;
  const m = new StandardMaterial("blobShadowMat", scene);
  const path = texturePath(scene, "textures/blobshadow.png");
  if (!path) { m.alpha = 0; return m; }
  const t = themedTexture(scene, path);
  t.hasAlpha = true;
  m.diffuseTexture = t;
  m.diffuseColor = Color3.Black();
  m.emissiveColor = Color3.Black();
  m.specularColor = Color3.Black();
  m.disableLighting = true;
  m.useAlphaFromDiffuseTexture = true;
  m.transparencyMode = 2;
  return m;
}

const SURFACE_TEXTURES = new WeakMap<Scene, Map<string, Texture>>();
function texMat(scene: Scene, name: string, url: string, periodM: number, tint: Rgb, fallback?: () => void): StandardMaterial {
  tint = vary(scene, tint, name, /pavers/.test(url) ? 'ground' : '');
  const path = texturePath(scene, url);
  if (!path) {
    const role = /grass|tree/.test(url) ? "vegetation" : /pavers/.test(url) ? "ground" : "buildings";
    return mat(scene, name, appearanceTheme(themeState(scene).id).palette[role] ?? tint);
  }
  name = `${themeState(scene).id}_${name}_${path}`;
  const existing = scene.getMaterialByName(name);
  if (existing instanceof StandardMaterial) { existing.diffuseColor = Color3.FromArray(tint); return existing; }
  const m = new StandardMaterial(name, scene);
  let textures = SURFACE_TEXTURES.get(scene);
  if (!textures) { textures = new Map(); SURFACE_TEXTURES.set(scene, textures); }
  const textureKey = `${path}:${periodM}`;
  let t = textures.get(textureKey);
  if (!t) {
    t = themedTexture(scene, path, () => {
      for (const material of scene.materials) if (material instanceof StandardMaterial && material.diffuseTexture === t) {
        material.diffuseTexture = null; material.emissiveTexture = null;
      }
      fallback?.();
    });
    textures.set(textureKey, t);
  }
  t.wrapU = Texture.WRAP_ADDRESSMODE;
  t.wrapV = Texture.WRAP_ADDRESSMODE;
  t.uScale = 1 / periodM;
  t.vScale = 1 / periodM;
  m.diffuseTexture = themeState(scene).failed.has(path) ? null : t;
  m.diffuseColor = new Color3(tint[0], tint[1], tint[2]);
  m.specularColor = new Color3(0.03, 0.03, 0.03);
  // Night facades carry their own light: the texture doubles as emissive,
  // so lit windows glow while the dark walls stay dark, independent of how
  // little hemispheric light the night scene has (ADR-054).
  // Only the Realism night atlas contains authored lit windows. Adventure reuses
  // its daytime albedo and must never emit light from the entire wall.
  if (themeState(scene).id === "realism" && url.includes("_night") && !themeState(scene).failed.has(path)) m.emissiveTexture = t;
  return m;
}

/** Asset instances share source materials, so keep one Adventure conversion for
 * each source/finish across all actors and retries. These live with the scene. */
const MODEL_PBR_MATERIALS = new WeakMap<Scene, Map<string, PBRMaterial>>();
const NEIGHBORHOOD_MATERIALS = new WeakMap<Scene, Map<string, StandardMaterial>>();
function neighborhoodModelMaterial(scene: Scene, source: PBRMaterial, specular: number, actorId: string): StandardMaterial {
  let cache = NEIGHBORHOOD_MATERIALS.get(scene);
  if (!cache) { cache = new Map(); NEIGHBORHOOD_MATERIALS.set(scene, cache); }
  const role = /skin|face/i.test(source.name) ? 'skin' : /lamp|brake|indicator|cyan eyes|signal/i.test(source.name) ? 'functional' : '';
  const rgb = vary(scene, [source.albedoColor.r, source.albedoColor.g, source.albedoColor.b], `${actorId}:${source.name}`, role);
  const key = `${source.uniqueId}:${specular}:${SCENE_VARIATION.get(scene) ?? 1}:${rgb.join(',')}`;
  const existing = cache.get(key);
  if (existing) return existing;
  const finish = new StandardMaterial(`neighborhood_${source.name}_${source.uniqueId}`, scene);
  finish.diffuseColor = Color3.FromArray(rgb); finish.diffuseTexture = source.albedoTexture;
  finish.emissiveColor = source.emissiveColor.clone(); finish.emissiveTexture = source.emissiveTexture;
  finish.specularColor.set(specular, specular, specular); finish.specularPower = 48;
  finish.alpha = source.alpha; finish.transparencyMode = source.transparencyMode;
  finish.backFaceCulling = source.backFaceCulling;
  if (source.albedoTexture && source.useAlphaFromAlbedoTexture) {
    source.albedoTexture.hasAlpha = true; finish.useAlphaFromDiffuseTexture = true;
  }
  cache.set(key, finish); return finish;
}

function transientMaterial(scene: Scene, name: string): StandardMaterial {
  if (themeState(scene).id !== "neighborhood") return new StandardMaterial(name, scene);
  const ownedName = `neighborhood_${name}`;
  return scene.getMaterialByName(ownedName) as StandardMaterial | null ?? new StandardMaterial(ownedName, scene);
}

function mat(scene: Scene, name: string, rgb: Rgb, varyFinish = true): StandardMaterial {
  let cache = MAT_CACHE.get(scene);
  if (!cache) {
    cache = new Map();
    MAT_CACHE.set(scene, cache);
  }
  const id = themeState(scene).id;
  const role = /robotMat/.test(name) ? "robot" : /packageMat/.test(name) ? "items" : /curb|kerb|gutter|drain|lampPole/i.test(name) ? "infrastructure" : undefined;
  const functional = functionalColor(rgb) || /brake|indL|indR|lamp$|reverse|debug|guide|arrow|roadPaint|roadCentre|EyeGlow|CyanEye/i.test(name);
  const skin = rgb === SKIN || /skin/i.test(name);
  rgb = themeColor(id, rgb, role);
  if (varyFinish && !functional) rgb = vary(scene, rgb, name, skin ? 'skin' : role ?? (/road/i.test(name) ? 'road' : /ground/i.test(name) ? 'ground' : ''));
  const privateKey = /brake|indL|indR|lamp$|reverse/.test(name) ? name : '';
  const key = `${id}|${SCENE_VARIATION.get(scene) ?? 1}|${privateKey}|${rgb[0]},${rgb[1]},${rgb[2]}`;
  let m = cache.get(key);
  if (m) return m;
  m = new StandardMaterial(name, scene);
  m.diffuseColor = new Color3(rgb[0], rgb[1], rgb[2]);
  const specular = appearanceTheme(id).material.specular;
  m.specularColor = new Color3(specular, specular, specular);
  cache.set(key, m);
  return m;
}

/** Deterministic [0, 1) hash of two integers (city heights/tints). */
function hash01(a: number, b: number): number {
  let h = (Math.imul(a | 0, 374761393) + Math.imul(b | 0, 668265263)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** Deterministic [0, 1) hash of a string (per-agent tints). */
/** A pose the renderer interpolates from. */
interface Pose {
  x: number;
  z: number;
  heading: number;
}

/**
 * Further than this between two simulation steps and it was a teleport, not
 * movement — the safe-pose restore and the reverse recovery both move the
 * robot bodily. At the arcade cruise a single 1/60 s step covers ~0.15 m, so
 * 2 m is far outside anything driving can produce.
 */
const TELEPORT_M = 2;

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Shortest-arc angle interpolation; a naive lerp spins the long way round. */
function lerpAngle(a: number, b: number, t: number): number {
  return a + wrapAngle(b - a) * t;
}

function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  // Final avalanche: plain FNV barely moves for ids differing in one char.
  h = Math.imul(h ^ (h >>> 15), 2246822519);
  h = Math.imul(h ^ (h >>> 13), 3266489917);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

// ---------------------------------------------------------------------------
// Matrix helpers (build-time only; never called per frame).

const TMP_SCALE = new Vector3();
const TMP_POS = new Vector3();
const TMP_Q = new Quaternion();

/** scale -> roll (about z) -> yaw (about y) -> translate. */
function place(
  sx: number,
  sy: number,
  sz: number,
  yaw: number,
  x: number,
  y: number,
  z: number,
  roll = 0,
): Matrix {
  TMP_SCALE.set(sx, sy, sz);
  TMP_POS.set(x, y, z);
  Quaternion.RotationYawPitchRollToRef(yaw, 0, roll, TMP_Q);
  return Matrix.Compose(TMP_SCALE, TMP_Q, TMP_POS);
}

/** World frame of an object standing on the ground at (x, z) facing yaw. */
function frameAt(yaw: number, x: number, z: number): Matrix {
  return Matrix.RotationY(yaw).multiply(Matrix.Translation(x, 0, z));
}

// ---------------------------------------------------------------------------
// Static batching: one thin-instanced unit primitive per (shape, color).

type ProtoShape = "box" | "cyl" | "sphere" | "tree" | "shadow" | "glow";

interface BatchGroup {
  proto: Mesh;
  data: number[];
}

class StaticBatch {
  private readonly groups = new Map<string, BatchGroup>();

  constructor(private readonly scene: Scene) {}

  add(shape: ProtoShape, rgb: Rgb, m: Matrix, chunk = "", texUrl?: string): void {
    const theme = appearanceTheme(themeState(this.scene).id);
    if ([KERB_PAINT, KERB_CAP, GUTTER, CURB, LAMP_TINT, DRAIN].includes(rgb)) {
      const shade = (rgb[0] + rgb[1] + rgb[2]) / (CURB[0] + CURB[1] + CURB[2]);
      const base = theme.palette.infrastructure!;
      rgb = [base[0] * shade, base[1] * shade, base[2] * shade];
    }
    if (shape === "tree" && (!theme.policy.photographicVegetation || !texturePath(this.scene, texUrl ?? ""))) {
      // Keep each tree's authored location and envelope, with solid foliage and trunk.
      const crown = Matrix.Scaling(1, 0.7, 1).multiply(Matrix.Translation(0, 0.15, 0)).multiply(m);
      const scale = new Vector3();
      m.decompose(scale);
      const diameter = 2 * TREE_TRUNK_RADIUS_M;
      const trunk = Matrix.Scaling(diameter / scale.x, 0.6, diameter / scale.z)
        .multiply(Matrix.Translation(0, -0.2, 0)).multiply(m);
      if (theme.id === "neighborhood") {
        for (const [dx, dy, dz, size] of [[-.22,.06,0,.67],[.22,.13,.07,.70],[0,.30,-.1,.64],[0,-.02,.20,.60]]) {
          const lobe = Matrix.Scaling(size!, size! * .82, size!).multiply(Matrix.Translation(dx!, dy!, dz!)).multiply(crown);
          this.add("sphere", theme.palette.vegetation!, lobe, chunk);
        }
      } else this.add(theme.id === "cartoon" ? "sphere" : "box", theme.palette.vegetation!, crown, chunk);
      this.add("cyl", [0.37, 0.28, 0.2], trunk, chunk);
      return;
    }
    // The benchmark owns its PBR scans separately; generic Adventure scenery
    // retains its authored fallback when a legacy photo slot has no binding.
    if ((!theme.policy.photographicSurfaces || theme.id === "neighborhood") && texUrl && !texturePath(this.scene, texUrl)) {
      rgb = theme.palette.buildings!;
      texUrl = undefined;
    }
    if (shape !== "shadow" && shape !== "glow" && !functionalColor(rgb)) {
      const a = m.m;
      rgb = vary(this.scene, rgb, `static:${shape}:${a[12]!.toFixed(2)}:${a[14]!.toFixed(2)}`);
    }
    const key = `${shape}|${rgb[0]},${rgb[1]},${rgb[2]}|${chunk}|${texUrl ?? ""}`;
    let g = this.groups.get(key);
    if (!g) {
      const name = `batch_${shape}_${this.groups.size}`;
      let proto: Mesh;
      if (shape === "shadow") {
        // Contact shadows for batched things (trees): same blob as the
        // vehicles, thin-instanced with everything else.
        proto = CreateGround(name, { width: 1, height: 1 }, this.scene);
        proto.material = blobShadowMat(this.scene);
      } else if (shape === "tree") {
        // A photographic tree on two crossed quads (ADR-048). Unit-sized and
        // CENTRED like the box proto, so the same place() matrices work.
        // Normals point straight up: with a hemispheric light, side-facing
        // normals would render one quad bright and its twin near-black.
        proto = new Mesh(name, this.scene);
        const vd = new VertexData();
        const P = 0.5;
        vd.positions = [
          -P, -P, 0,  P, -P, 0,  P, P, 0,  -P, P, 0,
          0, -P, -P,  0, -P, P,  0, P, P,  0, P, -P,
        ];
        vd.normals = [0,1,0, 0,1,0, 0,1,0, 0,1,0, 0,1,0, 0,1,0, 0,1,0, 0,1,0];
        vd.uvs = [0,0, 1,0, 1,1, 0,1, 0,0, 1,0, 1,1, 0,1];
        vd.indices = [0,1,2, 0,2,3, 4,5,6, 4,6,7];
        vd.applyToMesh(proto);
        const m2 = texMat(this.scene, `${name}Mat`, texUrl ?? "", 1, rgb, () => {
          if (proto.isDisposed()) return;
          CreateSphereVertexData({ diameter: 1, segments: 6 }).applyToMesh(proto);
          proto.material = mat(this.scene, `${name}Fallback`, appearanceTheme(themeState(this.scene).id).palette.vegetation!);
        });
        const t = m2.diffuseTexture;
        if (t instanceof Texture) t.hasAlpha = true;
        else {
          CreateSphereVertexData({ diameter: 1, segments: 6 }).applyToMesh(proto);
          m2.diffuseColor = Color3.FromArray(themeColor(theme.id, theme.palette.vegetation!));
        }
        // The exact recipe the Bulldog's face decal uses, because that one
        // demonstrably renders its alpha in this scene. Plain ALPHATEST mode
        // shipped every billboard on a solid black card despite every flag
        // probing correct; rather than excavate the define plumbing, use the
        // path with a working example. Blend-order artefacts between trees in
        // one batch are possible in principle; the avenue spaces them 9 m
        // apart, where none are visible.
        m2.useAlphaFromDiffuseTexture = true;
        m2.transparencyMode = 2;
        m2.backFaceCulling = false;
        proto.material = m2;
      } else if (shape === "box" && texUrl) {
        // A textured box: the four walls read the window region of the
        // facade, the roof and floor read the plain strip at the top — a
        // building with windows on its roof is the kind of wrong nobody can
        // name but everybody sees. Thin instances stretch the texture with
        // the building, so window size varies building to building; on a
        // street of mixed blocks that variance reads as variety, not error.
        const wall = new Vector4(0, 0, 1, 0.875);
        const flat = new Vector4(0.02, 0.9, 0.2, 0.99);
        proto = CreateBox(name, { size: 1, faceUV: [wall, wall, wall, wall, flat, flat] }, this.scene);
      } else if (shape === "glow") {
        // A self-lit box (lamp heads after dark): plain emissive colour,
        // no lighting, so it reads as the light source it stands for.
        proto = CreateBox(name, { size: 1 }, this.scene);
        const gm = transientMaterial(this.scene, `${name}Mat`);
        gm.emissiveColor = Color3.FromArray(themeColor(theme.id, rgb));
        gm.diffuseColor = Color3.Black();
        gm.specularColor = Color3.Black();
        gm.disableLighting = true;
        proto.material = gm;
      } else if (shape === "box") proto = CreateBox(name, { size: 1 }, this.scene);
      else if (shape === "cyl") {
        proto = CreateCylinder(name, { diameter: 1, height: 1, tessellation: 12 }, this.scene);
      } else proto = CreateSphere(name, { diameter: 1, segments: theme.id === "neighborhood" ? (themeState(this.scene).lite ? 8 : 12) : 6 }, this.scene);
      // Textured protos mix the tint most of the way to white: the photo now
      // carries the colour, and multiplying it by a mid-grey CITY tint gave a
      // street of charcoal towers. The residue of the tint keeps neighbouring
      // blocks from being identical.
      // The shadow proto (and the tree proto) already assigned their own
      // materials above; the generic assignment must not overwrite them. It
      // DID: every batched shadow came out as a plain white quad, because a
      // fresh `${name}Mat` colour material silently replaced the blob. The
      // trees only survived by accident — texMat caches by name, so the
      // second call returned the same object the tree branch had configured.
      if (shape !== "shadow" && shape !== "tree" && shape !== "glow") {
        proto.material = texUrl
          ? texMat(this.scene, `${name}Mat`, texUrl, 1, [
              0.72 + 0.28 * rgb[0],
              0.72 + 0.28 * rgb[1],
              0.72 + 0.28 * rgb[2],
            ])
          : mat(this.scene, `${name}Mat`, rgb, false);
      }
      g = { proto, data: [] };
      this.groups.set(key, g);
    }
    const a = m.m;
    for (let i = 0; i < 16; i++) g.data.push(a[i]!);
  }

  /** Bake Adventure's static instances by spatial chunk and texture. Hundreds of
   * tiny colour batches otherwise dominate software-GPU submission time. Vertex
   * colours retain each primitive's tint, and inverse-transpose normals retain
   * its lighting under nonuniform scale; geometry and detail stay identical. */
  private finishNeighborhood(): Mesh[] {
    type Bucket = { positions: number[]; normals: number[]; uvs: number[]; colors: number[]; indices: number[]; material: StandardMaterial };
    const buckets = new Map<string, Bucket>();
    const out: Mesh[] = [];
    const vertex = new Vector3(); const transformed = new Vector3();
    for (const [key, group] of this.groups) {
      const [shape, , chunk = "", texture = ""] = key.split("|");
      if (shape === "shadow" || shape === "glow" || shape === "tree") {
        group.proto.thinInstanceSetBuffer("matrix", new Float32Array(group.data), 16, true);
        out.push(group.proto); continue;
      }
      const material = group.proto.material as StandardMaterial;
      const bucketKey = `${chunk}|${texture}`;
      let bucket = buckets.get(bucketKey);
      if (!bucket) {
        const name = `neighborhoodStatic_${texture || "solid"}`;
        let shared = this.scene.getMaterialByName(name) as StandardMaterial | null;
        if (!shared) {
          shared = new StandardMaterial(name, this.scene);
          shared.diffuseColor = Color3.White();
          shared.diffuseTexture = material.diffuseTexture;
          shared.specularColor = material.specularColor.clone();
          shared.specularPower = material.specularPower;
        }
        bucket = { positions: [], normals: [], uvs: [], colors: [], indices: [], material: shared };
        buckets.set(bucketKey, bucket);
      }
      const source = VertexData.ExtractFromMesh(group.proto);
      const positions = source.positions!; const normals = source.normals!;
      const tint = material.diffuseColor;
      for (let instance = 0; instance < group.data.length; instance += 16) {
        const matrix = Matrix.FromArray(group.data, instance);
        const normalMatrix = matrix.clone().invert().transpose();
        const offset = bucket.positions.length / 3;
        for (let i = 0; i < positions.length; i += 3) {
          vertex.set(positions[i]!, positions[i+1]!, positions[i+2]!);
          Vector3.TransformCoordinatesToRef(vertex, matrix, transformed);
          bucket.positions.push(transformed.x, transformed.y, transformed.z);
          vertex.set(normals[i]!, normals[i+1]!, normals[i+2]!);
          Vector3.TransformNormalToRef(vertex, normalMatrix, transformed); transformed.normalize();
          bucket.normals.push(transformed.x, transformed.y, transformed.z);
          bucket.colors.push(tint.r, tint.g, tint.b, 1);
          const uv = i / 3 * 2;
          bucket.uvs.push(source.uvs?.[uv] ?? 0, source.uvs?.[uv+1] ?? 0);
        }
        if (matrix.determinant() < 0) {
          for (let i = 0; i < source.indices!.length; i += 3) bucket.indices.push(source.indices![i]! + offset, source.indices![i+2]! + offset, source.indices![i+1]! + offset);
        } else for (const index of source.indices!) bucket.indices.push(index + offset);
      }
      group.proto.dispose();
      // Baked vertices contain the tint and the chunk material owns the shared
      // atlas reference. Prototype-only textured materials must not accumulate
      // a different grouping/name set for every appearance seed.
      if (material.name.startsWith("neighborhood_batch_")) material.dispose(false, false);
    }
    for (const [key, bucket] of buckets) {
      const mesh = new Mesh(`neighborhoodStatic_${key}`, this.scene);
      const data = new VertexData();
      data.positions = bucket.positions; data.normals = bucket.normals; data.uvs = bucket.uvs;
      data.colors = bucket.colors; data.indices = bucket.indices;
      data.applyToMesh(mesh); mesh.material = bucket.material; mesh.useVertexColors = true;
      out.push(mesh);
    }
    this.groups.clear(); return out;
  }

  /** Upload all instance buffers; returns the batch meshes (to track/freeze). */
  finish(): Mesh[] {
    if (themeState(this.scene).id === "neighborhood") return this.finishNeighborhood();
    const out: Mesh[] = [];
    for (const g of this.groups.values()) {
      g.proto.thinInstanceSetBuffer("matrix", new Float32Array(g.data), 16, true);
      out.push(g.proto);
    }
    this.groups.clear();
    return out;
  }
}

// ---------------------------------------------------------------------------
/** A flat rectangle from (ax,az) to (cx,cz), `half` wide, at height y. */
function addStripe(
  g: GeomBuilder,
  ax: number,
  az: number,
  cx: number,
  cz: number,
  half: number,
  y: number,
): void {
  const len = Math.hypot(cx - ax, cz - az);
  if (len < 1e-6) return;
  const ux = (cx - ax) / len;
  const uz = (cz - az) / len;
  const nx = uz * half;
  const nz = -ux * half;
  g.addFlat(
    [
      { x: ax + nx, z: az + nz },
      { x: cx + nx, z: cz + nz },
      { x: cx - nx, z: cz - nz },
      { x: ax - nx, z: az - nz },
    ],
    y,
  );
}

// Flat geometry accumulator (road ribbons, ring, chevrons, arrows). Top faces
// are CCW in (x, z) with z "up on the page" = front-facing with +y normal.

interface P2 {
  x: number;
  z: number;
}

class GeomBuilder {
  private readonly pos: number[] = [];
  private readonly nor: number[] = [];
  private readonly idx: number[] = [];
  /**
   * World-planar UVs: every vertex carries its own (x, z) as the texture
   * coordinate (ADR-047). For horizontal surfaces this is a planar map in
   * METRES, so a material tiles by setting uScale = 1 / period and the
   * pattern lines up seamlessly across every ribbon segment of every road —
   * per-face 0..1 UVs would restart the asphalt at each joint.
   */
  private readonly uv: number[] = [];

  get empty(): boolean {
    return this.idx.length === 0;
  }

  private v(x: number, y: number, z: number, nx: number, ny: number, nz: number): number {
    this.pos.push(x, y, z);
    this.nor.push(nx, ny, nz);
    this.uv.push(x, z);
    return this.pos.length / 3 - 1;
  }

  /** Horizontal convex polygon (CCW loop), normal up. */
  addFlat(loop: readonly P2[], y: number): void {
    const base = this.pos.length / 3;
    for (const p of loop) this.v(p.x, y, p.z, 0, 1, 0);
    for (let i = 1; i + 1 < loop.length; i++) this.idx.push(base, base + i, base + i + 1);
  }

  addDisc(cx: number, cz: number, r: number, y: number, n: number): void {
    const loop: P2[] = [];
    for (let k = 0; k < n; k++) {
      const a = (k / n) * Math.PI * 2;
      loop.push({ x: cx + Math.cos(a) * r, z: cz + Math.sin(a) * r });
    }
    this.addFlat(loop, y);
  }

  addAnnulus(cx: number, cz: number, rIn: number, rOut: number, y: number, n: number): void {
    for (let k = 0; k < n; k++) {
      const a0 = (k / n) * Math.PI * 2;
      const a1 = ((k + 1) / n) * Math.PI * 2;
      this.addFlat(
        [
          { x: cx + Math.cos(a0) * rIn, z: cz + Math.sin(a0) * rIn },
          { x: cx + Math.cos(a0) * rOut, z: cz + Math.sin(a0) * rOut },
          { x: cx + Math.cos(a1) * rOut, z: cz + Math.sin(a1) * rOut },
          { x: cx + Math.cos(a1) * rIn, z: cz + Math.sin(a1) * rIn },
        ],
        y,
      );
    }
  }

  /** Extruded polygon: `loop` is the CCW outline, `tris` its triangulation. */
  addPrism(loop: readonly P2[], tris: readonly number[], y0: number, y1: number): void {
    const top = this.pos.length / 3;
    for (const p of loop) this.v(p.x, y1, p.z, 0, 1, 0);
    for (const i of tris) this.idx.push(top + i);
    const bot = this.pos.length / 3;
    for (const p of loop) this.v(p.x, y0, p.z, 0, -1, 0);
    for (let i = 0; i + 2 < tris.length; i += 3) {
      this.idx.push(bot + tris[i]!, bot + tris[i + 2]!, bot + tris[i + 1]!);
    }
    for (let i = 0; i < loop.length; i++) {
      const a = loop[i]!;
      const b = loop[(i + 1) % loop.length]!;
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const len = Math.hypot(dx, dz) || 1;
      const nx = dz / len;
      const nz = -dx / len;
      const s = this.pos.length / 3;
      this.v(a.x, y1, a.z, nx, 0, nz);
      this.v(b.x, y1, b.z, nx, 0, nz);
      this.v(b.x, y0, b.z, nx, 0, nz);
      this.v(a.x, y0, a.z, nx, 0, nz);
      this.idx.push(s, s + 1, s + 2, s, s + 2, s + 3);
    }
  }

  build(name: string, scene: Scene): Mesh {
    const m = new Mesh(name, scene);
    const vd = new VertexData();
    vd.positions = this.pos;
    vd.normals = this.nor;
    vd.indices = this.idx;
    vd.uvs = this.uv;
    vd.applyToMesh(m);
    return m;
  }
}

/** Chevron pointing +z, 1.2 m wide (CCW outline + triangulation). */
const CHEVRON_LOOP: readonly P2[] = [
  { x: 0, z: 0.6 },
  { x: -0.6, z: -0.1 },
  { x: -0.6, z: -0.45 },
  { x: 0, z: 0.15 },
  { x: 0.6, z: -0.45 },
  { x: 0.6, z: -0.1 },
];
const CHEVRON_TRIS: readonly number[] = [0, 1, 2, 0, 2, 3, 0, 3, 4, 0, 4, 5];

/** Arrow pointing +z (shaft + head). */
const ARROW_LOOP: readonly P2[] = [
  { x: 0, z: 0.8 },
  { x: -0.5, z: 0.15 },
  { x: -0.16, z: 0.15 },
  { x: -0.16, z: -0.65 },
  { x: 0.16, z: -0.65 },
  { x: 0.16, z: 0.15 },
  { x: 0.5, z: 0.15 },
];
const ARROW_TRIS: readonly number[] = [0, 1, 6, 2, 3, 4, 2, 4, 5];

/**
 * A curved arrow sweeping counter-clockwise around the origin with a head at
 * the end — the roundabout marker. A straight arrow there read as "go
 * straight", which is exactly what a player told us it looked like.
 */
function circulationArrow(radius: number, fromDeg: number, toDeg: number, dir: 1 | -1): { loop: P2[]; tris: number[] } {
  const loop: P2[] = [];
  const tris: number[] = [];
  const steps = 14;
  const w = 0.13;
  const a0 = (fromDeg * Math.PI) / 180;
  const a1 = (toDeg * Math.PI) / 180;
  // Two rails of a ribbon along the arc.
  for (let i = 0; i <= steps; i++) {
    const a = a0 + ((a1 - a0) * i) / steps;
    const c = Math.cos(a);
    const sn = Math.sin(a);
    loop.push({ x: sn * (radius - w), z: c * (radius - w) });
    loop.push({ x: sn * (radius + w), z: c * (radius + w) });
  }
  for (let i = 0; i < steps; i++) {
    const b = i * 2;
    tris.push(b, b + 1, b + 3, b, b + 3, b + 2);
  }
  // Arrow head at the far end, pointing along the direction of travel.
  const ah = a1;
  const tip = ah + dir * 0.34;
  const hx = Math.sin(tip) * radius;
  const hz = Math.cos(tip) * radius;
  const bi = loop.length;
  loop.push({ x: hx, z: hz });
  loop.push({ x: Math.sin(ah) * (radius - 0.34), z: Math.cos(ah) * (radius - 0.34) });
  loop.push({ x: Math.sin(ah) * (radius + 0.34), z: Math.cos(ah) * (radius + 0.34) });
  tris.push(bi, bi + 1, bi + 2);
  return { loop, tris };
}

// ---------------------------------------------------------------------------
// Part specs: one description serves both static batching (parked props) and
// live agent meshes (animated parts are keyed).

interface PartSpec {
  shape: ProtoShape;
  rgb: Rgb;
  size: [number, number, number];
  pos: [number, number, number];
  /** Rotation about z (wheels lie on their side). */
  roll?: number;
  /** Animated part key (agents only). */
  key?: string;
  /** Shared-material key (agents only; e.g. both brake lights). */
  matKey?: string;
}

/** Car: 4.2 m long (z), 1.8 m wide, 1.4 m high; +z = front. */
function carSpec(tint: Rgb, driverX: number): PartSpec[] {
  const parts: PartSpec[] = [
    { shape: "box", rgb: tint, size: [1.8, 0.6, 4.2], pos: [0, 0.62, 0] },
    { shape: "box", rgb: CAR_CABIN, size: [1.6, 0.5, 2.0], pos: [0, 1.15, -0.2] },
  ];
  let w = 0;
  for (const sx of [-0.82, 0.82]) {
    for (const sz of [1.35, -1.35]) {
      parts.push({
        shape: "cyl",
        rgb: WHEEL,
        size: [0.64, 0.24, 0.64],
        pos: [sx, 0.32, sz],
        roll: Math.PI / 2,
        key: `wheel${w++}`,
      });
    }
  }
  for (const sx of [-0.6, 0.6]) {
    parts.push({ shape: "box", rgb: BRAKE, size: [0.32, 0.12, 0.06], pos: [sx, 0.72, -2.11], matKey: "brake" });
    parts.push({ shape: "box", rgb: HEADLIGHT, size: [0.32, 0.12, 0.06], pos: [sx, 0.72, 2.11], matKey: "lamp" });
  }
  for (const sz of [2.08, -2.08]) {
    parts.push({ shape: "box", rgb: INDICATOR, size: [0.08, 0.1, 0.16], pos: [-0.9, 0.74, sz], matKey: "indL" });
    parts.push({ shape: "box", rgb: INDICATOR, size: [0.08, 0.1, 0.16], pos: [0.9, 0.74, sz], matKey: "indR" });
  }
  parts.push({ shape: "sphere", rgb: SKIN, size: [0.28, 0.28, 0.28], pos: [driverX, 1.22, 0.25], key: "head" });
  return parts;
}

/**
 * City bus: 10.3 m long (z), 2.5 m wide, 3.0 m high; +z = front.
 *
 * Drawn at its real length rather than as a scaled car, because the length IS
 * the scenario in the occlusion maps — a bus that renders car-sized would let
 * the player see straight past the thing that is supposed to be hiding an
 * alley mouth, and the map would silently stop testing what it claims to.
 * The dimensions match the bus entry in VEHICLE_DEFAULTS (agents.ts).
 */
function busSpec(tint: Rgb): PartSpec[] {
  const parts: PartSpec[] = [
    { shape: "box", rgb: tint, size: [2.5, 1.5, 10.3], pos: [0, 1.05, 0] },
    // A lighter band of glazing, and a darker skirt, so it reads as a bus at a
    // glance and not as a very long van.
    { shape: "box", rgb: BUS_GLASS, size: [2.54, 0.75, 9.4], pos: [0, 1.75, 0.2] },
    { shape: "box", rgb: BUS_SKIRT, size: [2.52, 0.45, 10.0], pos: [0, 0.42, 0] },
    { shape: "box", rgb: BUS_GLASS, size: [2.1, 0.95, 0.1], pos: [0, 1.85, 5.13] },
  ];
  let w = 0;
  // Front axle, then a rear pair — a two-axle bus on four wheels reads wrong
  // at this length.
  for (const sx of [-1.15, 1.15]) {
    for (const sz of [3.7, -2.6, -3.8]) {
      parts.push({
        shape: "cyl",
        rgb: WHEEL,
        size: [0.9, 0.28, 0.9],
        pos: [sx, 0.45, sz],
        roll: Math.PI / 2,
        key: `wheel${w++}`,
      });
    }
  }
  for (const sx of [-0.85, 0.85]) {
    parts.push({ shape: "box", rgb: BRAKE, size: [0.34, 0.22, 0.06], pos: [sx, 0.95, -5.16], matKey: "brake" });
    parts.push({ shape: "box", rgb: HEADLIGHT, size: [0.34, 0.22, 0.06], pos: [sx, 0.95, 5.16], matKey: "lamp" });
  }
  for (const sz of [5.0, -5.0]) {
    parts.push({ shape: "box", rgb: INDICATOR, size: [0.08, 0.14, 0.2], pos: [-1.25, 0.98, sz], matKey: "indL" });
    parts.push({ shape: "box", rgb: INDICATOR, size: [0.08, 0.14, 0.2], pos: [1.25, 0.98, sz], matKey: "indR" });
  }
  return parts;
}

/**
 * Rigid truck: 6.6 m long, cab in front of a box body; +z = front.
 *
 * It used to be drawn with `carSpec` — a 4.2 m saloon standing in for a
 * vehicle whose collision body is 6.6 m. The player was judging gaps against a
 * shape two metres shorter than the one the simulation would hit them with,
 * which is the worst kind of wrong a mesh can be.
 */
function truckSpec(tint: Rgb): PartSpec[] {
  const parts: PartSpec[] = [
    { shape: "box", rgb: tint, size: [2.2, 2.1, 4.3], pos: [0, 1.65, -1.05] },
    { shape: "box", rgb: [0.42, 0.45, 0.5], size: [2.1, 1.5, 2.0], pos: [0, 1.05, 2.15] },
    { shape: "box", rgb: CAR_CABIN, size: [1.85, 0.72, 0.1], pos: [0, 1.5, 3.1] },
    { shape: "box", rgb: [0.28, 0.29, 0.32], size: [2.14, 0.5, 6.4], pos: [0, 0.45, 0] },
  ];
  // Separate cab glazing, mirrors, grille and rear shutters remain legible
  // in every pack, including the simple Graybox geometry.
  for (const side of [-1, 1]) {
    parts.push({ shape: "box", rgb: CAR_CABIN, size: [.05, .65, 1.15], pos: [side * 1.06, 1.5, 2.12] });
    parts.push({ shape: "box", rgb: [.18,.21,.23], size: [.11,.26,.17], pos: [side * 1.17, 1.57, 2.78] });
    parts.push({ shape: "box", rgb: [.70,.71,.69], size: [1.04,1.78,.06], pos: [side * .54,1.65,-3.22] });
    parts.push({ shape: "box", rgb: [.28,.30,.30], size: [.04,1.62,.08], pos: [side * .14,1.65,-3.26] });
    parts.push({ shape: "box", rgb: HEADLIGHT, size: [.16,.12,.06], pos: [side * .48,.87,-3.24], matKey: "reverse" });
  }
  for (const y of [.59,.70,.81]) parts.push({ shape: "box", rgb: [.14,.17,.18], size: [1.2,.06,.06], pos: [0,y,3.17] });
  let w = 0;
  for (const sx of [-0.95, 0.95]) {
    for (const sz of [2.3, -1.5, -2.6]) {
      parts.push({
        shape: "cyl",
        rgb: WHEEL,
        size: [0.86, 0.26, 0.86],
        pos: [sx, 0.43, sz],
        roll: Math.PI / 2,
        key: `wheel${w++}`,
      });
    }
  }
  for (const sx of [-0.75, 0.75]) {
    parts.push({ shape: "box", rgb: BRAKE, size: [0.3, 0.2, 0.06], pos: [sx, 0.9, -3.21], matKey: "brake" });
    parts.push({ shape: "box", rgb: HEADLIGHT, size: [0.3, 0.2, 0.06], pos: [sx, 0.8, 3.16], matKey: "lamp" });
  }
  for (const sz of [3.0, -3.0]) {
    parts.push({ shape: "box", rgb: INDICATOR, size: [0.08, 0.12, 0.18], pos: [-1.06, 0.85, sz], matKey: "indL" });
    parts.push({ shape: "box", rgb: INDICATOR, size: [0.08, 0.12, 0.18], pos: [1.06, 0.85, sz], matKey: "indR" });
  }
  return parts;
}

/** Motorbike (scale 1) / motorcycle (scale ~1.25); +z = front. */
function bikeSpec(scale: number, frame: Rgb, rider: Rgb | null): PartSpec[] {
  const k = scale;
  const parts: PartSpec[] = [
    { shape: "box", rgb: frame, size: [0.3 * k, 0.3 * k, 1.15 * k], pos: [0, 0.45 * k, 0] },
    { shape: "cyl", rgb: WHEEL, size: [0.44 * k, 0.08 * k, 0.44 * k], pos: [0, 0.22 * k, 0.48 * k], roll: Math.PI / 2, key: "wheelF" },
    { shape: "cyl", rgb: WHEEL, size: [0.44 * k, 0.08 * k, 0.44 * k], pos: [0, 0.22 * k, -0.48 * k], roll: Math.PI / 2, key: "wheelB" },
    { shape: "box", rgb: [0.3, 0.3, 0.33], size: [0.46 * k, 0.05, 0.06], pos: [0, 0.78 * k, 0.4 * k], key: "bars" },
    { shape: "box", rgb: HEADLIGHT, size: [0.12 * k, 0.1 * k, 0.05], pos: [0, 0.62 * k, 0.6 * k], matKey: "lamp" },
  ];
  if (rider) {
    // A seated version of the pedestrian build, not a cylinder with a ball on
    // it: riders sit a metre from the robot's camera and were the last people
    // in the scene still made of two primitives, which showed the moment the
    // pedestrians beside them got limbs.
    const sleeve: Rgb = [rider[0] * 0.82, rider[1] * 0.82, rider[2] * 0.82];
    parts.push(
      // Thighs forward to the pegs, shins down — the shape that says "seated".
      { shape: "box", rgb: [0.26, 0.28, 0.34], size: [0.12 * k, 0.13 * k, 0.46 * k], pos: [-0.13 * k, 0.6 * k, 0.05 * k] },
      { shape: "box", rgb: [0.26, 0.28, 0.34], size: [0.12 * k, 0.13 * k, 0.46 * k], pos: [0.13 * k, 0.6 * k, 0.05 * k] },
      { shape: "box", rgb: [0.26, 0.28, 0.34], size: [0.12 * k, 0.42 * k, 0.14 * k], pos: [-0.15 * k, 0.36 * k, 0.24 * k], key: "leg" },
      { shape: "box", rgb: [0.26, 0.28, 0.34], size: [0.12 * k, 0.42 * k, 0.14 * k], pos: [0.15 * k, 0.36 * k, 0.24 * k] },
      { shape: "box", rgb: rider, size: [0.34 * k, 0.5 * k, 0.24 * k], pos: [0, 0.95 * k, -0.08 * k] },
      // Arms reaching forward to the bars, which is what makes it read as
      // riding rather than as sitting on a wall.
      { shape: "box", rgb: sleeve, size: [0.09 * k, 0.11 * k, 0.46 * k], pos: [-0.2 * k, 1.0 * k, 0.2 * k] },
      { shape: "box", rgb: sleeve, size: [0.09 * k, 0.11 * k, 0.46 * k], pos: [0.2 * k, 1.0 * k, 0.2 * k] },
      { shape: "sphere", rgb: SKIN, size: [0.2 * k, 0.22 * k, 0.2 * k], pos: [0, 1.3 * k, -0.06 * k], key: "head" },
      // A helmet, because everyone on a motorbike here is wearing one.
      { shape: "sphere", rgb: HELMET, size: [0.24 * k, 0.24 * k, 0.25 * k], pos: [0, 1.33 * k, -0.06 * k] },
    );
  }
  return parts;
}

const HAIR: Rgb = [0.17, 0.14, 0.13];
const HELMET: Rgb = [0.86, 0.86, 0.88];
const TROUSER_TINTS: readonly Rgb[] = [
  [0.24, 0.27, 0.34],
  [0.32, 0.3, 0.28],
  [0.2, 0.22, 0.26],
  [0.36, 0.33, 0.3],
];
const SHOE: Rgb = [0.15, 0.14, 0.15];

/**
 * A person: head, torso, hips, two arms, two legs.
 *
 * It was one cylinder and one sphere, which from the robot's eye height reads
 * as a bollard with a ball on it — and pedestrians are the single most
 * important thing in this simulation to recognise as pedestrians. Limbs also
 * give the walk cycle something to move; a cylinder can only slide.
 *
 * The silhouette stays inside PED_RADIUS so what you see is what the
 * simulation collides with — a figure drawn wider than its collision circle
 * teaches the player the wrong margin.
 */
function personSpec(shirt: Rgb, scale = 1, seed = 0): PartSpec[] {
  const k = scale;
  const w = Math.min(0.34, PED_RADIUS * 2) * k; // shoulder width
  const trousers = TROUSER_TINTS[Math.abs(seed) % TROUSER_TINTS.length]!;
  // Arms a shade darker than the shirt: in the same colour they merge into the
  // torso and the figure reads as a block with a head, which is most of what
  // was wrong with the old one.
  const sleeve: Rgb = [shirt[0] * 0.82, shirt[1] * 0.82, shirt[2] * 0.82];
  return [
    // Legs first: they are what the walk cycle swings.
    { shape: "box", rgb: trousers, size: [0.13 * k, 0.82 * k, 0.17 * k], pos: [-0.085 * k, 0.44 * k, 0], key: "legL" },
    { shape: "box", rgb: trousers, size: [0.13 * k, 0.82 * k, 0.17 * k], pos: [0.085 * k, 0.44 * k, 0], key: "legR" },
    { shape: "box", rgb: SHOE, size: [0.14 * k, 0.09 * k, 0.26 * k], pos: [-0.085 * k, 0.05 * k, 0.03 * k], key: "footL" },
    { shape: "box", rgb: SHOE, size: [0.14 * k, 0.09 * k, 0.26 * k], pos: [0.085 * k, 0.05 * k, 0.03 * k], key: "footR" },
    { shape: "box", rgb: trousers, size: [w * 0.9, 0.2 * k, 0.2 * k], pos: [0, 0.92 * k, 0] },
    { shape: "box", rgb: shirt, size: [w, 0.5 * k, 0.22 * k], pos: [0, 1.26 * k, 0], key: "body" },
    { shape: "box", rgb: sleeve, size: [0.095 * k, 0.48 * k, 0.13 * k], pos: [-(w / 2 + 0.035 * k), 1.24 * k, 0], key: "armL" },
    { shape: "box", rgb: sleeve, size: [0.095 * k, 0.48 * k, 0.13 * k], pos: [w / 2 + 0.035 * k, 1.24 * k, 0], key: "armR" },
    { shape: "cyl", rgb: SKIN, size: [0.09 * k, 0.05 * k, 0.09 * k], pos: [0, 1.53 * k, 0] },
    { shape: "sphere", rgb: SKIN, size: [0.22 * k, 0.25 * k, 0.22 * k], pos: [0, 1.68 * k, 0], key: "head" },
    // Hair as a shallow cap ON TOP of the skull, not a second ball around it:
    // a sphere of nearly head size set slightly high reads as a wig sliding
    // off. Flattened and sunk in, it is a hairline.
    { shape: "sphere", rgb: HAIR, size: [0.225 * k, 0.13 * k, 0.225 * k], pos: [0, 1.74 * k, -0.012 * k] },
  ];
}

const DOG_BODY: Rgb = [0.8, 0.62, 0.38];
const DOG_HEAD: Rgb = [0.82, 0.66, 0.42];
const DOG_SPEC: PartSpec[] = [
  { shape: "box", rgb: DOG_BODY, size: [0.2, 0.18, 0.4], pos: [0, 0.19, 0] },
  { shape: "box", rgb: DOG_HEAD, size: [0.16, 0.16, 0.16], pos: [0, 0.32, 0.24], key: "head" },
  { shape: "box", rgb: DOG_HEAD, size: [0.04, 0.09, 0.03], pos: [-0.05, 0.44, 0.24], key: "earL" },
  { shape: "box", rgb: DOG_HEAD, size: [0.04, 0.09, 0.03], pos: [0.05, 0.44, 0.24], key: "earR" },
  { shape: "box", rgb: DOG_BODY, size: [0.05, 0.05, 0.16], pos: [0, 0.28, -0.24], key: "tail" },
];

const CAT_FUR: Rgb = [0.68, 0.55, 0.42];
const CAT_SPEC: PartSpec[] = [
  { shape: "box", rgb: CAT_FUR, size: [0.16, 0.14, 0.42], pos: [0, 0.16, 0] },
  { shape: "box", rgb: CAT_FUR, size: [0.14, 0.13, 0.14], pos: [0, 0.27, 0.24], key: "head" },
  { shape: "box", rgb: CAT_FUR, size: [0.04, 0.06, 0.03], pos: [-0.045, 0.36, 0.24] },
  { shape: "box", rgb: CAT_FUR, size: [0.04, 0.06, 0.03], pos: [0.045, 0.36, 0.24] },
  { shape: "box", rgb: CAT_FUR, size: [0.04, 0.04, 0.28], pos: [0, 0.2, -0.32], key: "tail" },
];

const CART_SPEC: PartSpec[] = [
  { shape: "box", rgb: [0.66, 0.55, 0.42], size: [0.8, 0.55, 0.72], pos: [0, 0.45, 0] },
  { shape: "box", rgb: [0.7, 0.45, 0.38], size: [0.9, 0.04, 0.8], pos: [0, 1.15, 0] },
  { shape: "box", rgb: [0.5, 0.48, 0.45], size: [0.05, 0.6, 0.05], pos: [-0.4, 0.85, -0.3] },
  { shape: "box", rgb: [0.5, 0.48, 0.45], size: [0.05, 0.6, 0.05], pos: [0.4, 0.85, -0.3] },
  { shape: "cyl", rgb: WHEEL, size: [0.3, 0.06, 0.3], pos: [-0.42, 0.15, 0], roll: Math.PI / 2, key: "wheelL" },
  { shape: "cyl", rgb: WHEEL, size: [0.3, 0.06, 0.3], pos: [0.42, 0.15, 0], roll: Math.PI / 2, key: "wheelR" },
];

// ---------------------------------------------------------------------------

interface AgentVisual {
  adventureRig?: AdventureActor;
  agent: AgentModel;
  type: AgentType;
  root: TransformNode;
  parts: Map<string, TransformNode>;
  mats: Map<string, StandardMaterial>;
  /** Accumulated wheel spin (rad). */
  spin: number;
  /** Smoothed gesture pose (standing people). */
  gesture: number;
  /** Walk clip of the rigged person GLB, once it has streamed in (ADR-056). */
  walk: AnimationGroup | null;
  /** The rig's skeletons, to return a STOPPED person to the rest pose —
   * pausing the clip alone freezes them mid-stride, one foot in the air. */
  skeletons: Skeleton[];
  shown: boolean;
  motion: ActorMotionVariation;
}

/**
 * How the onboard sensor rig is configured (ADR-064).
 *
 * Defaults are the SOURCED stock lens at the interactive resolution tier. A
 * capture run overrides them; nothing here is ever guessed silently — the
 * lens's own `status` travels with every frame written to disk.
 */
export interface OnboardOptics {
  /** Lens profile name from config/robot_cameras.json. */
  lens: string;
  /** Horizontal FOV override in degrees, for matching reference footage. */
  fovDeg: number | null;
  /** Cube face resolution tier: play | hq | max. */
  quality: OpticsQuality;
}

export const DEFAULT_ONBOARD_OPTICS: OnboardOptics = {
  lens: DEFAULT_LENS,
  fovDeg: null,
  quality: "play",
};

export class Renderer {
  private isAdventureBenchmark = false;
  private adventureWorld: ReturnType<typeof buildAdventureBenchmarkWorld> | null = null;
  private adventureMaterials: AdventureMaterials | null = null;
  private adventureActors: AdventureActor[] = [];
  private adventureRobot: AdventureActor | null = null;
  private variationSeed = 1;
  setVariationSeed(seed: number): void { this.variationSeed = seed >>> 0; SCENE_VARIATION.set(this.scene, this.variationSeed); }
  private appearanceId: AppearanceThemeId = DEFAULT_APPEARANCE_THEME;
  private buildGeneration = 0;
  private appearanceGeneration = 0;
  private readonly appearanceStates = new Map<AppearanceThemeId, ThemeState>();
  get appearance(): AppearanceThemeId { return this.appearanceId; }
  get appearanceManifest(): AppearanceManifest & { variation_seed: number; time_of_day: "day" | "dusk" | "night"; weather: "clear" | "wet"; backdrop: "procedural" | "splat"; splat_source: string | null } {
    const theme = appearanceTheme(this.appearance);
    const state = themeState(this.scene);
    return { variation_seed: this.variationSeed, theme_id: theme.id, theme_version: theme.version, config_hash: appearanceConfigHash(theme),
      assets: state.pending.size ? "loading" : state.failed.size ? "degraded" : "ready",
      failed_assets: [...state.failed].sort(), fallback_policy: "same-theme-procedural",
      time_of_day: this.tod, weather: this.wet ? "wet" : "clear",
      backdrop: this.splatMesh ? "splat" : "procedural", splat_source: this.splatSource };
  }
  /** Applied atomically before rebuilding a level; stale async mounts cannot cross packs. */
  setAppearance(id: AppearanceThemeId): void {
    id = normalizeAppearanceTheme(id);
    if (id === this.appearanceId) return;
    this.appearanceId = id;
    this.buildGeneration++;
    this.appearanceGeneration++;
    let state = this.appearanceStates.get(id);
    if (!state) { state = { id, lite: this.qualityTier === "lite", pending: new Set(), failed: new Set() }; this.appearanceStates.set(id, state); }
    SCENE_THEME.set(this.scene, state);
    if (!appearanceTheme(id).policy.splatWorld) {
      this.splatMesh?.dispose(); this.splatMesh = null; this.splatSource = null;
    }
    this.scene.environmentTexture = null;
    // Keep loaded assets cached, but detach every photograph from the active pack.
    this.domeMat.reflectionTexture = null; this.domeMat.emissiveTexture = null;
    this.configureEnvironment();
  }
  private configureEnvironment(): void {
    const theme = appearanceTheme(this.appearance);
    const path = themeAssetPath(theme.id, "textures", "env/sky.hdr");
    if (!theme.policy.photographicSky || !path) return;
    if (this.envTex) { this.scene.environmentTexture = this.envTex; return; }
    const state = themeState(this.scene);
    const url = `${import.meta.env.BASE_URL}${path}`;
    state.pending.add(url);
    const env = new HDRCubeTexture(url, this.scene, 128, false, true, false, false,
      () => state.pending.delete(url), () => { state.pending.delete(url); state.failed.add(url); if (themeState(this.scene) === state) { this.scene.environmentTexture = null; this.envTex = null; this.skyRef = null; if (this.domeMat) { this.domeMat.reflectionTexture = null; this.domeMat.emissiveColor = Color3.FromArray(theme.lighting[this.tod].sky); } } });
    this.scene.environmentTexture = env;
    this.envTex = env;
  }
  private async themeModel(url: string): Promise<AssetContainer | null> {
    const state = themeState(this.scene);
    state.pending.add(url);
    if (state.failed.has(url)) { this.modelCache.delete(url); state.failed.delete(url); }
    let pending = this.modelCache.get(url);
    if (!pending) {
      pending = LoadAssetContainerAsync(url, this.scene).catch(() => null);
      this.modelCache.set(url, pending);
    }
    const result = await pending;
    state.pending.delete(url);
    if (!result) state.failed.add(url);
    return result;
  }

  readonly engine: Engine;
  readonly scene: Scene;
  /** Which budget this renderer runs on (see the constructor). Surfaced in
   * the perf overlay so a device screenshot says which tier it measured. */
  readonly qualityTier: "full" | "lite";
  private readonly camera: FreeCamera;
  private readonly fpvCam: FreeCamera;
  /** Which camera the player is looking through. */
  private playView: PlayView = "third";
  private readonly cameraView: CameraView;
  private onboardCam: FreeCamera | null = null;
  private fisheye: FisheyeRig | null = null;
  private readonly debug: boolean;
  private readonly camTarget = new Vector3();

  // Robot rig.
  private robotNode!: TransformNode;
  private wobbleNode!: TransformNode;
  private packageBox!: Mesh;
  /**
   * The real robot (ADR-046): the Bulldog GLB exported from the team's SVL
   * simulator — the same second-generation delivery robot the bags were
   * recorded on. Loaded once and cached as an asset container, because
   * buildLevel() rebuilds the robot on every map change and re-downloading
   * 1.3 MB per retry would be absurd.
   */
  private readonly modelCache = new Map<string, Promise<AssetContainer | null>>();
  /** World-space contact shadows, one per moving or mounted thing (ADR-052). */
  private shadowFollowers: Array<{ host: TransformNode; w: number; d: number; mesh: Mesh | null }> = [];
  /** Which machine the player drives (ADR-048): the real Bulldog by default,
   * the first-generation Alaska, or the original blocky placeholder. */
  robotModel: "bulldog" | "alaska" | "box" = "bulldog";
  /**
   * The world's setting (ADR-048). "suburban" — the default — is the open
   * master-planned district the robot actually works in: detached villas,
   * grass verges, street trees, towers only in the haze. "downtown" keeps the
   * dense continuous frontage, wrong for the reference but a world worth
   * driving, so it stays as a choice rather than being deleted.
   */
  envTheme: "suburban" | "downtown" = "suburban";
  /** Everything of the placeholder robot, hidden once the Bulldog is in. */
  private placeholderParts: Mesh[] = [];
  /** Wheel nodes of the loaded model, spun by road speed in animateRobot. */
  private bulldogWheels: TransformNode[] = [];
  private tailNode!: TransformNode;
  private armL!: Mesh;
  private armR!: Mesh;

  private pedMesh: Mesh | null = null;
  private destMarker!: Mesh;
  private levelMeshes: Mesh[] = [];
  private agentVisuals: AgentVisual[] = [];
  private dogAgent: AgentModel | null = null;
  private turnArrows: TransformNode[] = [];
  /** Camera yaw: chases the ROBOT's heading, not the road frame. Snapping to
   * the road made junctions feel like the world jerked round the player. */
  private camYaw = 0;
  private camYawInit = false;

  // Event-driven animation state.
  private seenEvents = 0;
  private wobbleStartT: number | null = null;
  private blinkStartT: number | null = null;
  private lastSmoothT = -Infinity;
  private lastAwkwardT = -Infinity;
  /** A clean give-way just ended: the tail says well done (ADR-065). */
  private lastResumeT = -Infinity;
  private corridorLayer: CorridorLayer | null = null;
  /** Calm blue ring under the robot while the SYSTEM is limiting speed. */
  private slowGlow: Mesh | null = null;
  private slowGlowA = 0;
  private slowGlowUntil = -Infinity;
  private prevSlowed = false;
  private prevCap = Infinity;
  private prevT = 0;
  /** Ambience declared by the map (ADR-054): sky, light, lamps, wet sheen. */
  private tod: "day" | "dusk" | "night" = "day";
  private wet = false;
  /**
   * Player overrides (ADR-058). `auto` defers to the map, which is a clear
   * day everywhere except the two scenarios written to be dark and wet.
   */
  skyChoice: "auto" | "day" | "dusk" | "night" = "auto";
  weatherChoice: "auto" | "clear" | "rain" = "auto";
  private domeMat!: StandardMaterial;
  private hemi!: HemisphericLight;
  /** The sun, and the shadow map it casts (ADR-060). */
  private sun!: DirectionalLight;
  private shadows: ShadowGenerator | null = null;
  private pipeline: DefaultRenderingPipeline | null = null;
  private adventureContactAO: SSAO2RenderingPipeline | null = null;
  /** The sky HDR, doing two jobs: the backdrop, and the light that fills
   * shadow (ADR-061). One texture for both is what keeps them agreeing. */
  private envTex: HDRCubeTexture | null = null;
  private skyRef: HDRCubeTexture | null = null;
  private skyTexCache = new Map<string, Texture>();
  /** Warm light-pool matrices under the lamps, flushed per level build. */
  private lampPools: number[] = [];

  constructor(
    canvas: HTMLCanvasElement,
    debug = false,
    cameraView: CameraView = "follow",
    optics: OnboardOptics = DEFAULT_ONBOARD_OPTICS,
    capture = false,
    gfx: GfxTier = "auto",
    appearance: AppearanceThemeId = DEFAULT_APPEARANCE_THEME,
  ) {
    this.appearanceId = normalizeAppearanceTheme(appearance);
    this.debug = debug;
    this.cameraView = cameraView;
    /**
     * The software-adapter budget (the "lite" tier).
     *
     * On SwiftShader — headless Chromium in the e2e suite, GPU-blocklisted
     * machines, VMs — this scene is fill-bound: an ablation profile put the
     * frame in MSAA, anisotropic + trilinear filtering, sheer pixel count at
     * the 2x DPR cap, and (since ADR-060) the shadow map's second scene render
     * and the filmic pipeline's full-screen passes — while everything anyone
     * would suspect first (skeletal animation 0.6 ms, all ten rigged
     * pedestrians ~5 ms, the corridor and decals ~5 ms) was noise. So on a
     * software rasterizer the budget changes, not the scene: no MSAA,
     * CSS-resolution rendering, bilinear sampling, no anisotropy, no shadow
     * map (blob shadows return), no post pipeline — measured together on the
     * v0.9.0 scene, 7.7 -> 39.6 fps with every mesh and texture still in the
     * frame. A real GPU takes none of this: the picture there is unchanged.
     */
    // Offline dataset capture (?capture=1) never auto-drops: it runs headless
    // on the same SwiftShader that trips the detection, and its whole purpose
    // is faithful frames — shadows, tone mapping and full resolution are the
    // product, not overhead to shed. An explicit ?gfx=lite is still honoured
    // (someone who asks for it by name means it); only `auto` is overridden.
    const lite =
      gfx === "lite" || (gfx === "auto" && !capture && isSoftwareRenderer());
    this.qualityTier = lite ? "lite" : "full";
    // Two decisions are fixed at context creation and cannot wait for the
    // engine to exist: MSAA (the lite tier drops it), and
    // `preserveDrawingBuffer`, kept only for offline capture (?capture=1). A
    // WebGL canvas normally discards its buffer the moment the frame is
    // composited, so a screenshot taken while the render loop is STOPPED —
    // which is how a deterministic clip is captured — comes back as the clear
    // colour. Keeping the buffer costs a copy per frame, which is why
    // interactive play does not pay it.
    this.engine = capture
      ? new Engine(canvas, !lite, { preserveDrawingBuffer: true })
      : new Engine(canvas, !lite);
    // Render at device resolution but cap at 2x: >2 DPR buys nothing visible
    // in a gray-box scene and costs real fill-rate on mobile GPUs. The lite
    // tier renders at CSS resolution instead — Babylon's own default — since
    // pixel count is the single biggest term in the software frame.
    this.engine.setHardwareScalingLevel(lite ? 1 : 1 / Math.min(window.devicePixelRatio || 1, 2));
    this.scene = new Scene(this.engine);
    const state: ThemeState = { id: this.appearanceId, lite, pending: new Set(), failed: new Set() };
    SCENE_THEME.set(this.scene, state);
    this.appearanceStates.set(this.appearanceId, state);
    if (lite) {
      // Hooked before any texture exists (the sky dome's is created a few
      // lines down), so every texture the scene will ever hold gets the
      // cheap sampler: SwiftShader honours — and pays full price for — the
      // default anisotropy of 4 and trilinear mips.
      this.scene.onNewTextureAddedObservable.add((t) => {
        t.anisotropicFilteringLevel = 1;
        if (t instanceof Texture) t.updateSamplingMode(Texture.BILINEAR_SAMPLINGMODE);
      });
    }
    this.scene.clearColor = new Color4(0.85, 0.86, 0.88, 1);
    this.scene.skipPointerMovePicking = true;
    // Distant city fades into the sky color (hides the far clip on the mission).
    this.scene.fogMode = Scene.FOGMODE_LINEAR;
    this.scene.fogColor = new Color3(0.85, 0.86, 0.88);
    this.scene.fogStart = 120;
    this.scene.fogEnd = FAR_CLIP - 10;

    // Daylight comes off a photographed sky (ADR-061): as a backdrop it puts
    // real cloud where a two-stop vertical gradient used to be, and as
    // `environmentTexture` it is what fills the shadows, so every reflective
    // surface is lit by the sky the player can actually see. Dusk and night
    // keep their authored gradients — they were tuned to the reference and a
    // daytime HDR cannot be tinted into either.
    this.configureEnvironment();

    const dome = CreateSphere("skyDome", { diameter: FAR_CLIP * 1.7, segments: 24 }, this.scene);
    const domeMat = new StandardMaterial("skyDomeMat", this.scene);
    domeMat.emissiveColor = Color3.FromArray(appearanceTheme(this.appearance).lighting.day.sky);
    domeMat.diffuseColor = Color3.Black();
    domeMat.specularColor = Color3.Black();
    domeMat.disableLighting = true;
    domeMat.backFaceCulling = false;
    dome.material = domeMat;
    dome.infiniteDistance = true;
    dome.applyFog = false;
    dome.isPickable = false;
    this.domeMat = domeMat;

    const light = new HemisphericLight("light", new Vector3(0.3, 1, -0.4), this.scene);
    light.intensity = 1.0;
    light.groundColor = new Color3(0.4, 0.4, 0.45);
    this.hemi = light;

    // The sun (ADR-060). Until now the whole world was lit by one ambient
    // hemisphere: nothing cast a shadow, so nothing sat ON the ground —
    // which is most of why a scene with real photographic textures still
    // read as a diagram. The reference footage is overcast, so this is a
    // soft high sun rather than a hard one.
    const sun = new DirectionalLight("sun", new Vector3(-0.62, -0.78, 0.34), this.scene);
    sun.intensity = 0.85;
    sun.specular = new Color3(0.12, 0.12, 0.12);
    this.sun = sun;
    // An orthographic box that travels with the robot. A directional light
    // covering a 400 m map at usable resolution needs a 16k map; a 60 m box
    // that follows gives crisp contact shadows out to the horizon of what
    // the camera can actually resolve.
    sun.autoUpdateExtends = false;
    sun.orthoLeft = -SHADOW_SPAN;
    sun.orthoRight = SHADOW_SPAN;
    sun.orthoTop = SHADOW_SPAN;
    sun.orthoBottom = -SHADOW_SPAN;
    sun.shadowMinZ = 1;
    sun.shadowMaxZ = 180;
    // The lite tier keeps the sun but not the shadow map: the map is a second
    // render of the scene every frame (draw calls went 145 -> 394 when it
    // arrived) plus PCF sampling per lit pixel, and a software rasterizer can
    // afford neither. `sunCasts` sees the missing generator and brings the
    // pre-ADR-060 blob shadows back, so things still sit on the ground.
    if (!lite) {
      const shadows = new ShadowGenerator(SHADOW_MAP_PX, sun);
      shadows.usePercentageCloserFiltering = true;
      shadows.filteringQuality = ShadowGenerator.QUALITY_MEDIUM;
      shadows.bias = 0.008;
      shadows.normalBias = 0.02;
      shadows.darkness = 0.28;
      // Trees are crossed alpha quads: without this each casts a solid
      // rectangle the size of its billboard, which is worse than no shadow.
      shadows.transparencyShadow = true;
      shadows.enableSoftTransparentShadow = true;
      this.shadows = shadows;
    }

    this.camera = new FreeCamera("cam", new Vector3(0, CAM_UP, -CAM_BACK), this.scene);
    this.camera.setTarget(new Vector3(0, LOOK_UP, LOOK_AHEAD));
    if (isCameraId(cameraView)) {
      // The onboard camera keeps the real extrinsics; only the projection is
      // replaced, because the lens is not a pinhole (ADR-023).
      const cam = new FreeCamera(`cam_${cameraView}`, Vector3.Zero(), this.scene);
      cam.minZ = ONBOARD_MIN_Z;
      cam.maxZ = ONBOARD_MAX_Z;
      this.onboardCam = cam;
      // The lens is the geometry of every pixel this view produces, so it is
      // resolved once, here, and reported verbatim in the clip metadata. An
      // explicit ?fov overrides only the angle, never the projection model.
      const base = lensProfile(optics.lens);
      const lens =
        optics.fovDeg !== null && Number.isFinite(optics.fovDeg) && optics.fovDeg > 1
          ? { ...base, fov_h_deg: optics.fovDeg, status: `${base.status}+FOV_OVERRIDE` }
          : base;
      const facePx = CUBE_FACE_TIERS[optics.quality] ?? CUBE_FACE_TIERS.play;
      this.fisheye = attachFisheye(this.scene, cam, lens, SENSOR.aspect, {
        cubeFacePx: facePx,
      });
      this.scene.activeCameras = [cam];
    }
    this.camera.fov = 0.8; // vertical FOV (portrait)
    this.camera.minZ = NEAR_CLIP;
    this.camera.maxZ = FAR_CLIP;

    // Film response (ADR-060). Raw linear output is what made the picture
    // read as a render: no highlight rolloff, no antialiasing, primary-bright
    // colour. ACES plus a gentle contrast curve is what a camera does to
    // light, and the reference clips are all soft, low-contrast and a little
    // washed — so the exposure sits slightly hot on purpose.
    //
    // Attached to the PLAY cameras only. The onboard sensor camera already
    // carries its own fisheye post-process, and stacking a second pipeline
    // on it reorders that chain.
    // (Built after both play cameras exist — see below.)

    // The first-person play camera. Parented to the robot in buildRobot(), so
    // it inherits the interpolated pose the body is drawn at and cannot judder
    // against it.
    this.fpvCam = new FreeCamera("cam_fpv", new Vector3(0, FPV_HEIGHT, FPV_FORWARD), this.scene);
    this.fpvCam.fov = FPV_FOV;
    this.fpvCam.minZ = 0.08;
    this.fpvCam.maxZ = FAR_CLIP;

    // The lite tier also skips the filmic pipeline: FXAA and tone mapping are
    // full-screen passes through an intermediate texture, which on a software
    // rasterizer costs more than the antialiasing and film response are worth.
    // The picture there is raw linear — the pre-ADR-060 look — not a missing
    // picture.
    if (!lite) {
      const pipeline = new DefaultRenderingPipeline("filmic", false, this.scene, [this.camera, this.fpvCam]);
      pipeline.fxaaEnabled = true;
      pipeline.imageProcessingEnabled = true;
      const ip = pipeline.imageProcessing;
      ip.toneMappingEnabled = true;
      ip.toneMappingType = ImageProcessingConfiguration.TONEMAPPING_ACES;
      ip.exposure = 1.15;
      ip.contrast = 1.12;
      ip.vignetteEnabled = false;
      this.pipeline = pipeline;
    }
    this.aimFpv();
    window.addEventListener("resize", () => this.aimFpv());
    window.visualViewport?.addEventListener("resize", () => this.aimFpv());

    window.addEventListener("resize", () => this.engine.resize());
    // Mobile browser chrome collapse fires visualViewport resizes that may
    // not fire a classic resize event.
    window.visualViewport?.addEventListener("resize", () => this.engine.resize());
  }

  // ------------------------------------------------------------------

  /** (Re)build all level geometry + actors from a fresh Simulation. */
  /**
   * Honour the map's declared time of day and weather (ADR-054). m05 says
   * night and m06 says wet dusk, and both rendered as a dry noon for a year
   * of versions — the absurdity was in the renderer, not the data. Sky dome,
   * hemispheric light, fog and clear colour all move together; the lamps,
   * facades and light pools are handled where they are built, keyed off the
   * same `tod` this sets.
   */
  /**
   * The sky this map photographed, if it brought one (ADR-133). A
   * reconstruction ships with its sky stripped out — gaussians placed at a
   * finite distance parallax wrongly the moment the robot moves — so the
   * street arrived under the simulator's own gradient, which is a different
   * sky over a real place. `sky_dome.py` turns the sky pixels of every
   * photograph into one equirectangular panorama, and this flies it.
   */
  mapSkyUrl: string | null = null;

  private applyAmbience(): void {
    const sky =
      this.tod === "night" ? "skygrad_night.png" : this.tod === "dusk" ? "skygrad_dusk.png" : "skygrad.png";
    const theme = appearanceTheme(this.appearance);
    const path = themeAssetPath(theme.id, "textures", sky);
    const key = this.mapSkyUrl ?? sky;
    let t = theme.policy.photographicSky ? this.skyTexCache.get(key) ?? null : null;
    if (!t && theme.policy.photographicSky && this.mapSkyUrl) {
      // The map's own sky wins over the theme's gradient, and falls back to it
      // if the file is missing rather than leaving a black dome.
      t = themedTexture(this.scene, this.mapSkyUrl, () => { this.mapSkyUrl = null; this.applyAmbience(); });
      t.coordinatesMode = Texture.FIXED_EQUIRECTANGULAR_MODE;
      this.skyTexCache.set(key, t);
    }
    if (!t && path && theme.policy.photographicSky) {
      t = themedTexture(this.scene, `${import.meta.env.BASE_URL}${path}`, () => { this.domeMat.emissiveTexture = null; });
      this.skyTexCache.set(key, t);
    }
    this.domeMat.emissiveColor = t ? Color3.White() : theme.policy.photographicSky && this.envTex ? Color3.Black() : Color3.FromArray(theme.lighting[this.tod].sky);
    // Day flies the photographed sky; dusk and night keep their gradients.
    // One dome, two modes: a skybox reflection for the HDR, an emissive map
    // for the gradients, and never both at once or they add together.
    if (theme.policy.photographicSky && this.tod === "day" && this.envTex && !this.mapSkyUrl) {
      if (!this.skyRef) {
        const ref = this.envTex.clone();
        ref.coordinatesMode = Texture.SKYBOX_MODE;
        this.skyRef = ref;
      }
      this.domeMat.reflectionTexture = this.skyRef;
      this.domeMat.emissiveTexture = null;
    } else {
      this.domeMat.reflectionTexture = null;
      this.domeMat.emissiveTexture = t;
    }
    // The sky also lights the scene, so its strength follows the hour.
    this.scene.environmentIntensity =
      this.tod === "night" ? 0.08 : this.tod === "dusk" ? 0.3 : 0.9;
    // Ambient is the SKY's share only (ADR-060). It used to be the whole
    // lighting model at 1.0, and once a sun was added on top of that the
    // scene was flooded: the shadow map worked perfectly and removed a
    // contribution nothing could see missing. Day splits roughly 1:3 sky to
    // sun; night keeps more ambient because there is no sun at all.
    const [fog, intensity, diffuse, ground]: [Rgb, number, Rgb, Rgb] =
      this.tod === "night"
        ? [[0.05, 0.06, 0.1], 0.3, [0.66, 0.72, 0.95], [0.14, 0.15, 0.22]]
        : this.tod === "dusk"
          ? [[0.62, 0.5, 0.47], 0.42, [1, 0.85, 0.7], [0.42, 0.36, 0.36]]
          // `groundColor` is the bounce off the ground, and it was set for a
          // world with no sun in it. A kerb's road-facing face gets no direct
          // light at all — the sun is behind it — so under a dark ground term
          // it went black, and ACES crushed what was left. Real asphalt under
          // an overcast sky throws a lot of light back up; without any GI this
          // constant is the only thing standing in for it.
          : [[0.85, 0.86, 0.88], 0.6, [1, 1, 1], [0.62, 0.62, 0.6]];
    const lighting = theme.lighting[this.tod];
    this.scene.clearColor = new Color4(...lighting.sky, 1);
    this.scene.fogColor = Color3.FromArray(lighting.sky);
    this.hemi.intensity = lighting.ambient;
    void fog; void intensity;
    this.hemi.diffuse = new Color3(diffuse[0], diffuse[1], diffuse[2]);
    this.hemi.groundColor = new Color3(ground[0], ground[1], ground[2]);
    if (this.appearance === "neighborhood" && this.tod === "day") {
      this.hemi.diffuse = new Color3(.72, .85, 1);
      this.hemi.groundColor = new Color3(.76, .66, .52);
    }
    // The sun follows the hour (ADR-060). At night there is none: the light
    // in the street comes from the lamps, and a moon casting building-sharp
    // shadows at midnight is a stranger sight than no shadows at all.
    if (this.sun) {
      // The reference footage is OVERCAST: light arrives from the whole sky,
      // not from one hard source. Too much sun and every face turned away
      // from it goes to shade the reference simply does not have. Enough sun
      // to cast a shadow and model form, no more.
      const sunLit = lighting.sun;
      this.sun.intensity = sunLit;
      this.sun.setEnabled(sunLit > 0);
      this.sun.diffuse =
        this.tod === "dusk" ? new Color3(1, 0.78, 0.6) : this.appearance === "neighborhood" ? new Color3(1, .87, .68) : new Color3(1, 0.98, 0.93);
      // Low and raking at dusk, high and soft at midday.
      // Mid-afternoon rather than noon: a sun straight overhead casts
      // shadows that never leave the object's own footprint, so the map was
      // working and nothing could be seen of it.
      this.sun.direction =
        this.tod === "dusk" ? new Vector3(-0.86, -0.34, 0.28) : new Vector3(-0.62, -0.78, 0.34);
    }
    if (this.pipeline) {
      // Dusk and night are darker scenes, not darker photographs: a real
      // camera opens up. Without this the night map merely lost its picture.
      this.pipeline.imageProcessing.exposure = lighting.exposure;
      this.pipeline.imageProcessing.contrast = lighting.contrast;
    }
  }

  /** Keep the small hero sharp without truncating shadows down the street.
   * The benchmark owns this override; leaving it rebuilds the original generator. */
  private configureBenchmarkShadows(): void {
    if (!this.shadows) return; // The lite tier intentionally has no shadow map.
    this.shadows.dispose();
    const benchmark = this.isAdventureBenchmark;
    const cascaded = benchmark && CascadedShadowGenerator.IsSupported;
    const shadows = cascaded
      ? new CascadedShadowGenerator(SHADOW_MAP_PX, this.sun)
      : new ShadowGenerator(SHADOW_MAP_PX, this.sun);
    if (shadows instanceof CascadedShadowGenerator) {
      shadows.numCascades = 2;
      shadows.lambda = .8;
      shadows.shadowMaxZ = 60;
      shadows.stabilizeCascades = true;
      shadows.depthClamp = true;
      shadows.autoCalcDepthBounds = false;
      shadows.cascadeBlendPercentage = .12;
    }
    const span = benchmark && !cascaded ? 18 : SHADOW_SPAN;
    this.sun.orthoLeft = -span; this.sun.orthoRight = span;
    this.sun.orthoTop = span; this.sun.orthoBottom = -span;
    this.sun.shadowMinZ = 1; this.sun.shadowMaxZ = 180;
    this.sun.forceProjectionMatrixCompute();
    shadows.usePercentageCloserFiltering = true;
    shadows.filteringQuality = ShadowGenerator.QUALITY_MEDIUM;
    shadows.bias = benchmark ? .0001 : .008;
    shadows.normalBias = benchmark ? .003 : .02;
    shadows.darkness = benchmark ? .10 : .28;
    shadows.transparencyShadow = true;
    shadows.enableSoftTransparentShadow = true;
    this.shadows = shadows;
  }

  buildLevel(sim: Simulation): void {
    this.buildGeneration++;
    this.adventureContactAO?.dispose(true); this.adventureContactAO = null;
    const previousBenchmark = this.isAdventureBenchmark;
    this.isAdventureBenchmark = this.appearance === 'neighborhood' && sim.level.mission?.mapId === 'adventure_benchmark';
    document.documentElement.toggleAttribute('data-adventure-benchmark', this.isAdventureBenchmark);
    this.adventureWorld?.dispose(); this.adventureWorld = null;
    for (const actor of this.adventureActors) actor.dispose();
    this.adventureActors = []; this.adventureRobot = null;
    this.adventureMaterials?.dispose(); this.adventureMaterials = null;
    if (previousBenchmark || this.isAdventureBenchmark) {
      this.scene.environmentTexture = null;
      this.configureEnvironment();
      this.scene.fogStart = this.isAdventureBenchmark ? 75 : 120;
      this.scene.fogEnd = this.isAdventureBenchmark ? 190 : FAR_CLIP - 10;
      this.camera.fov = this.isAdventureBenchmark ? .87 : .8;
      this.camera.minZ = this.isAdventureBenchmark ? .1 : NEAR_CLIP;
      this.configureBenchmarkShadows();
      this.sun.specular.setAll(.12);
    }
    const cond = sim.level.mission?.conditions;
    const mapTod = cond?.timeOfDay === "night" ? "night" : cond?.timeOfDay === "dusk" ? "dusk" : "day";
    const mapWet = cond?.weather === "rain" || cond?.weather === "wet";
    this.tod = this.isAdventureBenchmark ? "day" : this.skyChoice === "auto" ? mapTod : this.skyChoice;
    this.wet = this.isAdventureBenchmark ? false : this.weatherChoice === "auto" ? mapWet : this.weatherChoice === "rain";
    this.applyAmbience();
    if (this.isAdventureBenchmark) {
      this.adventureMaterials = new AdventureMaterials(this.scene, themeState(this.scene));
      if (this.qualityTier === "full" && SSAO2RenderingPipeline.IsSupported) {
        const ao = new SSAO2RenderingPipeline("adventureContactAO", this.scene, { ssaoRatio: .5, blurRatio: .5 }, [this.camera, this.fpvCam], true);
        ao.radius = .45; ao.totalStrength = .95; ao.base = .16;
        ao.samples = 8; ao.textureSamples = 1; ao.maxZ = 65;
        ao.bilateralSamples = 8; ao.bilateralSoften = .7; ao.bilateralTolerance = .2;
        this.adventureContactAO = ao;
      }
      this.scene.environmentTexture = this.adventureMaterials.environment;
      this.scene.environmentIntensity = 1.1;
      this.scene.fogColor.set(.58, .65, .67);
      this.scene.clearColor.set(.48, .65, .78, 1);
      this.domeMat.emissiveColor.set(.48, .65, .78);
      this.hemi.intensity = .5;
      this.hemi.diffuse.set(.88, .90, 1);
      this.hemi.groundColor.set(.48, .39, .27);
      this.sun.direction.set(-.30, -.90, -.65);
      this.sun.diffuse.set(1, .86, .66);
      this.sun.specular.set(1, .92, .8);
      this.sun.intensity = 4.6;
      if (this.pipeline) {
        this.pipeline.imageProcessing.exposure = 1.15;
        this.pipeline.imageProcessing.contrast = 1.17;
      }
    }
    this.lampPools = [];
    for (const m of this.levelMeshes) m.dispose();
    this.levelMeshes = [];
    for (const v of this.agentVisuals) v.root.dispose();
    this.agentVisuals = [];
    for (const n of this.turnArrows) n.dispose();
    this.turnArrows = [];
    this.camYawInit = false;
    for (const f of this.shadowFollowers) f.mesh?.dispose();
    this.shadowFollowers = [];
    // Detach the cameras BEFORE the robot goes.
    //
    // Both play cameras and the onboard sensor camera are parented to
    // `robotNode` so they inherit its interpolated pose. `dispose()` recurses
    // into descendants by default, so rebuilding the level destroyed them and
    // Babylon quietly fell back to the chase camera: pressing Retry in the
    // driver's view dropped the player into the chase view while the HUD went
    // on saying "first", and a `?view=front` capture session started writing
    // chase frames into a dataset that says they came from the robot's own
    // lens. Nothing errors; the picture just changes.
    this.fpvCam.parent = null;
    if (this.onboardCam) this.onboardCam.parent = null;
    this.robotNode?.dispose();
    this.pedMesh?.dispose();
    this.pedMesh = null;
    for (const material of MAT_CACHE.get(this.scene)?.values() ?? []) material.dispose(false, false);
    MAT_CACHE.get(this.scene)?.clear();
    for (const material of NEIGHBORHOOD_MATERIALS.get(this.scene)?.values() ?? []) material.dispose(false, false);
    NEIGHBORHOOD_MATERIALS.get(this.scene)?.clear();
    for (const material of MODEL_PBR_MATERIALS.get(this.scene)?.values() ?? []) material.dispose(false, false);
    MODEL_PBR_MATERIALS.get(this.scene)?.clear();

    const batch = new StaticBatch(this.scene);
    if (sim.level.mission) this.buildMissionWorld(sim, batch);
    else this.buildLabWorld(sim, batch);
    for (const m of batch.finish()) this.track(m);
    this.wireShadows();
    if (this.lampPools.length > 0) {
      // One plane, thin-instanced under every lit lamp: the blob texture's
      // alpha shapes the pool, a flat emissive colour warms it.
      const pool = CreateGround("lampPools", { width: 1, height: 1 }, this.scene);
      const pm = transientMaterial(this.scene, "lampPoolMat");
      const pt = this.appearance === "neighborhood" && pm.diffuseTexture instanceof Texture ? pm.diffuseTexture : themedTexture(this.scene, texturePath(this.scene, "textures/blobshadow.png")!);
      pt.hasAlpha = true;
      pm.diffuseTexture = pt;
      pm.diffuseColor = Color3.Black();
      pm.emissiveColor = new Color3(0.62, 0.5, 0.26);
      pm.specularColor = Color3.Black();
      pm.disableLighting = true;
      pm.useAlphaFromDiffuseTexture = true;
      pm.transparencyMode = 2;
      pm.alpha = 0.85;
      pool.material = pm;
      pool.isPickable = false;
      pool.thinInstanceSetBuffer("matrix", new Float32Array(this.lampPools), 16);
      this.track(pool);
    }
    this.buildDestination(sim);
    this.buildRobot(sim);

    // Lab pedestrian (mission agents get their own visuals).
    if (sim.pedModel) {
      this.pedMesh = this.buildLabPersonMesh("ped", [0.55, 0.35, 0.3]);
    }
    for (const a of sim.agents) this.agentVisuals.push(this.buildAgentVisual(a, sim));
    this.dogAgent = sim.agents.find((a) => a.state.type === "dog") ?? null;

    // The onboard camera renders through a cube map with an EXPLICIT mesh
    // list, and the agents were only just created — a list captured earlier
    // (when the robot rig was built) leaves every person, animal and vehicle
    // out of the very camera that exists to see them, with no error to show
    // for it. Refresh it here, after everything is in the scene.
    this.fisheye?.setRenderList(this.scene.meshes);

    // ...and put the right one back in charge, since disposing the old robot
    // may have taken the active camera with it.
    if (this.onboardCam && isCameraId(this.cameraView)) {
      this.scene.activeCameras = [this.onboardCam];
      this.scene.activeCamera = this.onboardCam;
    } else {
      this.scene.activeCamera = this.playView === "first" ? this.fpvCam : this.camera;
    }

    if (this.debug) this.buildDebugHelpers(sim);
    if (this.appearance === "neighborhood") this.wireShadows();

    // Static world: freeze matrices + materials (big CPU win on mobile; the
    // destination marker keeps its pulse, so it stays unfrozen).
    for (const m of this.levelMeshes) {
      if (m !== this.destMarker) {
        m.freezeWorldMatrix();
        m.doNotSyncBoundingInfo = true;
      }
      // Never freeze a material whose texture may not have arrived yet AND
      // needs alpha testing: freezing locks the shader defines, and a tree
      // frozen before its PNG loads keeps the no-alpha shader forever — every
      // billboard renders on a solid black card. Textured materials are few
      // (asphalt, pavers, facades, trees); leaving them unfrozen costs a
      // handful of isReady checks, not draw calls.
      const mt = m.material as StandardMaterial | null;
      // ANY texture defers freezing, not just diffuse: the blob-shadow
      // material carries only an opacityTexture, and freezing it before that
      // PNG arrived locked the no-opacity shader — every soft shadow rendered
      // as a solid black slab.
      if (mt && mt.getActiveTextures().length === 0) mt.freeze();
    }

    this.seenEvents = sim.events.length;
    this.wobbleStartT = null;
    this.blinkStartT = null;
    this.lastSmoothT = -Infinity;
    this.lastAwkwardT = -Infinity;
    this.lastResumeT = -Infinity;
    this.prevT = sim.t;
    this.prevRobot = null;
    this.prevAgents.clear();
    if (this.freeRoam) this.hideErrandMarkers();
    this.update(sim);
    this.precompile();
  }

  /**
   * Start compiling every material's shader, and report when they are done.
   *
   * Babylon compiles a material's shader the first time it is DRAWN, and this
   * scene carries ~58 of them. Left alone that lands as one ~500 ms task
   * blocking the main thread about half a second into the run — exactly when
   * the player has started driving, and felt as the picture locking up. A CPU
   * profile puts 94% of it in `(program)` with `getProgramParameter` alongside,
   * which is the GL call that blocks waiting for the driver to finish linking.
   *
   * `forceCompilation` is ASYNCHRONOUS — it polls until ready and returns
   * immediately — so kicking it off is not enough on its own; without
   * something waiting on it the link still lands mid-drive. `ready` is what
   * the game's warm-up holds on, so the wait happens with the scene on screen
   * and the clock stopped instead.
   *
   * Best effort on purpose: a material may have no mesh to derive attributes
   * from, and a driver may refuse. Neither is worth failing a level build
   * over, because the only cost of giving up is the stall we had before —
   * hence every path decrements the counter.
   */
  private precompile(): void {
    const meshFor = new Map<StandardMaterial, AbstractMesh>();
    for (const m of this.scene.meshes) {
      const mat = m.material as StandardMaterial | null;
      if (mat && !meshFor.has(mat)) meshFor.set(mat, m);
    }
    this.compiling = meshFor.size;
    for (const [mat, mesh] of meshFor) {
      const done = (): void => {
        this.compiling--;
      };
      try {
        mat.forceCompilation(mesh, done, undefined, done);
      } catch {
        done();
      }
    }
  }

  /** Where the robot was actually DRAWN this frame (perf overlay). */
  drawnRobotPosition(): { x: number; z: number } {
    return { x: this.drawn.x, z: this.drawn.z };
  }

  /**
   * Width guides on the road ahead, shown only in the driver's view.
   *
   * From inside the machine you cannot see its own edges, and every gap in
   * these maps is judged in centimetres — the barrier in m10 leaves 1.4 m for
   * a 0.6 m robot. Two lines laid at the robot's ACTUAL half-width, running
   * forward, turn "will I fit?" into something you can read off the road, the
   * way reversing guidelines do.
   *
   * Drawn at exactly `width_m / 2`, never wider: a guide that flattered the
   * robot would teach a margin the simulation does not honour, which is worse
   * than no guide at all. The cross ticks are at 1 m and 2 m so distance is
   * readable too.
   */
  private buildFpvGuide(widthM: number): void {
    const scene = this.scene;
    // Every attempt rebuilds the level, so the previous one has to go or the
    // guides stack up invisibly, one set per retry.
    this.fpvGuide?.dispose(false, true);
    const guide = new TransformNode("fpvGuide", scene);
    guide.parent = this.robotNode;
    const half = widthM / 2;
    // 5 cm, not the 1 cm that "just above the road" suggests. At 1-6 m ahead
    // the depth buffer cannot separate a 12 mm offset from the road itself:
    // the guides were in the frustum, enabled and visible by every check the
    // engine offers, and drew EIGHT pixels. At 5 cm they draw properly and at
    // this scale still read as paint on the surface.
    const y = 0.05;
    const add = (sx: number, sz: number, x: number, z: number, rgb: Rgb): void => {
      const m = CreateBox(`fpvGuide_${x}_${z}`, { size: 1 }, scene);
      m.scaling.set(sx, 0.004, sz);
      m.position.set(x, y, z);
      m.parent = guide;
      const mm = mat(scene, `fpvGuideMat_${rgb.join(",")}`, rgb);
      mm.emissiveColor = new Color3(rgb[0] * 0.35, rgb[1] * 0.35, rgb[2] * 0.35);
      // Faint on purpose. The first pass was thick and fully lit and took over
      // the whole lower half of the screen; a reference you cannot see past is
      // not a reference, it is a windscreen sticker.
      mm.alpha = 0.42;
      m.material = mm;
      m.isPickable = false;
    };
    // From 1.2 m to 6 m ahead. The first attempt started at 0.5 m and was
    // invisible on a laptop: the driver's camera sits nearly level in
    // landscape, so ground that close is below the bottom edge. Further out
    // shows on both shapes of screen, and is the more useful distance anyway —
    // a gap is judged while approaching it, not once you are in it.
    for (const sign of [-1, 1]) {
      add(0.035, 4.8, sign * half, 3.6, GUIDE_NEAR);
    }
    // Distance ticks measured from the robot's nose.
    for (const [z, rgb] of [[2, GUIDE_NEAR], [4, GUIDE_FAR]] as const) {
      add(half * 2 + 0.035, 0.05, 0, z + 0.45, rgb);
    }
    this.fpvGuide = guide;
    // The view may already have been chosen — the saved preference is applied
    // before the first level is built — so take the current state rather than
    // assuming the chase camera.
    guide.setEnabled(this.playView === "first");
  }

  /** Pitch the driver's-eye camera for the current screen shape. */
  private aimFpv(): void {
    const w = this.engine.getRenderWidth();
    const h = Math.max(1, this.engine.getRenderHeight());
    // 0 at a square frame or wider, 1 at a tall phone (aspect ~0.46).
    const tall = Math.min(1, Math.max(0, (1 - w / h) / 0.5));
    this.fpvCam.rotation.set(
      FPV_PITCH_LANDSCAPE + (FPV_PITCH_PORTRAIT - FPV_PITCH_LANDSCAPE) * tall,
      0,
      0,
    );
  }

  /**
   * Switch between the chase view and the driver's-eye view.
   *
   * Never while the research sensor rig is active (`?view=front`): that render
   * path exists to produce training frames from a known lens, and quietly
   * swapping the camera under it would put frames in the dataset that are not
   * what they claim to be.
   */
  setPlayView(view: PlayView): void {
    if (isCameraId(this.cameraView)) return;
    this.playView = view;
    this.scene.activeCamera = view === "first" ? this.fpvCam : this.camera;
    // The guides answer a question only the driver's view raises; from the
    // chase camera the robot's own edges are plainly visible and lines on the
    // road would just be clutter.
    // Not over a photographic world: the guide is painted lane, and painted
    // lane on a street that already has its own markings reads as the sim
    // inventing road furniture on top of the evidence.
    this.fpvGuide?.setEnabled(view === "first" && !this.builtWorldHidden);
  }

  get view(): PlayView {
    return this.playView;
  }

  /** True once every material has finished linking (see precompile). */
  /** The Gaussian-splat world backdrop, when one is loaded (ADR-069). */
  private splatMesh: import("@babylonjs/core/Meshes/abstractMesh").AbstractMesh | null = null;
  private splatSource: string | null = null;

  /**
   * Load a Gaussian-splat scene (.splat / .ply / .spz) as a world backdrop.
   *
   * This is the "real pixels" path from RESEARCH_VIDEO_GEN_VI.md section 12.3:
   * a neural reconstruction of the actual street stands in for (or alongside)
   * the generated gray-box world, while the robot, the agents and every label
   * keep coming from the simulation. The mesh is deliberately NOT tracked as
   * a level mesh — it survives level rebuilds — and is excluded from the
   * shadow pass (a splat bakes its own light; engine shadows double-darken).
   *
   * Returns true when the splat is in the scene. Never throws: a missing or
   * malformed file logs and leaves the ordinary world in charge.
   */
  async loadSplatWorld(
    url: string,
    opts: {
      position?: { x: number; y: number; z: number };
      rotationDeg?: { x: number; y: number; z: number };
      scale?: number;
      /** Hide the gray-box scenery so the reconstruction IS the world (ADR-086). */
      hideBuiltWorld?: boolean;
      /** Called with bytes received and total while the reconstruction downloads. */
      onProgress?: (loaded: number, total: number) => void;
    } = {},
  ): Promise<boolean> {
    if (!appearanceTheme(this.appearance).policy.splatWorld) return false;
    const generation = this.appearanceGeneration;
    const state = themeState(this.scene);
    state.pending.add(url);
    // Hide the built scenery from the first frame, not when the file arrives
    // (ADR-126). A reconstruction is tens of megabytes and takes the better
    // part of a minute on an ordinary connection; hiding afterwards means the
    // visitor spends that minute driving through the gray-box world they came
    // here not to see, with nothing saying anything else is on its way —
    // reported twice from the deployed site as "it is just the simulation".
    //
    // Repeatedly, because one call at the start hides exactly ONE mesh: the
    // level's scenery is built after this runs. Measured on the deployed site,
    // the call made here hid 1 and the one made after the file landed hid 363.
    // So keep hiding whatever appears until the street itself arrives.
    let keepHiding: ReturnType<typeof setInterval> | null = null;
    if (opts.hideBuiltWorld) {
      this.hideBuiltWorld();
      keepHiding = setInterval(() => this.hideBuiltWorld(), 250);
    }
    try {
      const container = await LoadAssetContainerAsync(url, this.scene, {
        onProgress: (e) => opts.onProgress?.(e.loaded, e.lengthComputable ? e.total : 0),
      });
      state.pending.delete(url);
      if (generation !== this.appearanceGeneration || !appearanceTheme(this.appearance).policy.splatWorld) { container.dispose(); return false; }
      container.addAllToScene();
      const mesh = container.meshes[0] ?? null;
      if (!mesh) {
        console.error(`splat: ${url} loaded but contained no mesh`);
        return false;
      }
      mesh.name = "splatWorld";
      const p = opts.position ?? { x: 0, y: 0, z: 0 };
      const r = opts.rotationDeg ?? { x: 0, y: 0, z: 0 };
      const sc = opts.scale ?? 1;
      mesh.position.set(p.x, p.y, p.z);
      mesh.rotation.set((r.x * Math.PI) / 180, (r.y * Math.PI) / 180, (r.z * Math.PI) / 180);
      mesh.scaling.setAll(sc);
      mesh.isPickable = false;
      // Splat bounds are loose and the sorter assumes visibility; never let
      // frustum culling blink the world in and out of the cube faces.
      mesh.alwaysSelectAsActiveMesh = true;
      this.splatMesh?.dispose();
      this.splatMesh = mesh;
      this.splatSource = url;
      // Again after the mesh exists: anything the scene built while the file
      // was in flight must go too.
      if (opts.hideBuiltWorld) this.hideBuiltWorld();
      // The onboard cube map renders an EXPLICIT mesh list (ADR-055); a world
      // that arrived after the list was captured would be invisible to the
      // one camera that exists to photograph it.
      this.fisheye?.setRenderList(this.scene.meshes);
      return true;
    } catch (err) {
      state.pending.delete(url); state.failed.add(url);
      console.error(`splat: failed to load ${url}:`, err);
      return false;
    } finally {
      if (keepHiding !== null) clearInterval(keepHiding);
    }
  }

  /**
   * A photographic world with nothing reconstructed in it (ADR-088).
   *
   * A Gaussian scene is sharp where a camera stood and soft everywhere else,
   * and open 360 imagery leaves 8 m between camera positions — far more
   * "everywhere else" than it can cover. The panoramas have no such problem:
   * they are the photographs. So stand inside them. Each one is a station with
   * a metric position; the nearest is drawn on a sphere around the robot and
   * swapped as it drives. The trade is honest and it is Street View's: no
   * parallax when you step off a station, but every pixel is real.
   */
  async loadPanoWorld(url: string, opts: { hideBuiltWorld?: boolean } = {}): Promise<boolean> {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const doc = (await res.json()) as PanoWorld;
      if (!doc?.stations?.length) throw new Error("no stations");
      this.disposePanoWorld();
      this.panoWorld = doc;
      this.panoStation = -1;
      this.setPanoStation(0);
      if (opts.hideBuiltWorld) this.hideBuiltWorld();
      this.fisheye?.setRenderList(this.scene.meshes);
      console.info(`pano: ${doc.stations.length} stations over ${doc.length_m ?? "?"} m — ${doc.source ?? url}`);
      return true;
    } catch (err) {
      console.error(`pano: failed to load ${url}:`, err);
      return false;
    }
  }

  /** True while a panorama world is up. */
  get panoWorldActive(): boolean {
    return this.panoWorld !== null;
  }

  private setPanoStation(i: number): void {
    const w = this.panoWorld;
    if (!w || i === this.panoStation) return;
    const st = w.stations[i];
    if (!st) return;
    const src = w.base + st.img;
    if (!this.panoDome) {
      this.panoDome = new PhotoDome("panoWorld", src, { resolution: 48, size: (w.radius ?? 60) * 2, useDirectMapping: false }, this.scene);
      this.panoDome.mesh.isPickable = false;
      // Loose bounds plus a camera sitting inside the sphere: culling it would
      // blink the whole world out.
      this.panoDome.mesh.alwaysSelectAsActiveMesh = true;
      this.panoDome.mesh.infiniteDistance = false;
    } else {
      const old = this.panoDome.photoTexture;
      this.panoDome.photoTexture = new Texture(src, this.scene);
      old?.dispose();
    }
    // The dome sits at the station, not on the robot: that is what makes the
    // world hold still as the robot moves through it.
    this.panoDome.position.set(st.x, w.camHeight ?? 1.2, st.z);
    this.panoDome.rotation.y = ((st.yaw ?? 0) * Math.PI) / 180;
    this.panoStation = i;
  }

  /** Swap to the panorama nearest the robot, the way Street View steps along. */
  private updatePanoStation(x: number, z: number): void {
    const w = this.panoWorld;
    if (!w) return;
    let best = this.panoStation;
    let bestD = Infinity;
    for (let i = 0; i < w.stations.length; i++) {
      const st = w.stations[i];
      if (!st) continue;
      const d = (st.x - x) * (st.x - x) + (st.z - z) * (st.z - z);
      if (d < bestD) { bestD = d; best = i; }
    }
    if (best !== this.panoStation) this.setPanoStation(best);
  }

  private disposePanoWorld(): void {
    this.panoDome?.dispose();
    this.panoDome = null;
    this.panoWorld = null;
    this.panoStation = -1;
  }

  private panoDome: PhotoDome | null = null;
  private panoWorld: PanoWorld | null = null;
  private panoStation = -1;

  /** The onboard sensor camera, or null when the play camera is active. */
  get onboardCamera(): FreeCamera | null {
    return this.onboardCam;
  }

  /** Optics actually in force on the onboard camera (null when not onboard). */
  get onboardOptics(): OpticsDescription | null {
    return this.fisheye?.describe() ?? null;
  }

  get shadersReady(): boolean {
    return this.compiling <= 0;
  }

  /** Materials still linking; see precompile(). */
  private compiling = 0;
  /** Poses at the previous simulation step, for render interpolation. */
  private prevRobot: Pose | null = null;
  /** The interpolated pose the robot is drawn at THIS frame; the camera and
   * anything else that follows the robot must use it, not the sim's. */
  private readonly drawn: Pose = { x: 0, z: 0, heading: 0 };
  /** Width guides on the road ahead (driver's view only). */
  private fpvGuide: TransformNode | null = null;
  private readonly prevAgents = new Map<string, Pose>();

  private track(m: Mesh): Mesh {
    this.levelMeshes.push(m);
    return m;
  }

  // ------------------------------------------------------------------
  // Lab levels (straight alley between two walls).

  private buildLabWorld(sim: Simulation, batch: StaticBatch): void {
    const world = sim.level.world;
    const scene = this.scene;
    const floor = this.track(
      CreateGround("floor", { width: world.alleyHalfWidth * 2 + 2, height: world.alleyLength + 10 }, scene),
    );
    floor.position.z = world.alleyLength / 2 - 2;
    floor.material = this.appearance === "neighborhood"
      ? texMat(scene, "adventureLabPavers", "textures/pavers.jpg", 1, [.85, .82, .72])
      : mat(scene, "floorMat", [0.62, 0.63, 0.65]);
    if (this.appearance === "neighborhood" && floor.material instanceof StandardMaterial && floor.material.diffuseTexture instanceof Texture) {
      floor.material.diffuseTexture.uScale = (world.alleyHalfWidth * 2 + 2) / 2;
      floor.material.diffuseTexture.vScale = (world.alleyLength + 10) / 2;
    }

    for (const side of [-1, 1]) {
      const wall = this.track(
        CreateBox(`wall${side}`, { width: 0.3, height: 2.2, depth: world.alleyLength + 10 }, scene),
      );
      wall.position.set(side * (world.alleyHalfWidth + 0.15), 1.1, world.alleyLength / 2 - 2);
      wall.material = mat(scene, `wallMat${side}`, [0.5, 0.5, 0.54]);
      if (this.appearance === "neighborhood") {
        wall.scaling.y = .10; wall.position.y = .11;
        wall.material = mat(scene, "adventureLabCurb", appearanceTheme(this.appearance).palette.infrastructure!);
        for (let z = -5; z < world.alleyLength + 5; z += 3) {
          neighborhoodHouse((shape, color, transform, texture) => batch.add(shape, color, transform, "lab", texture),
            side * (world.alleyHalfWidth + 1.85), z, -side * Math.PI / 2, 6.8 + hash01(side, Math.floor(z))*2,
            Math.floor(hash01(side, Math.floor(z))*100), this.tod !== "day");
        }
      }
    }
    for (const [i, ob] of world.obstacles.entries()) {
      batch.add(
        "box",
        i % 2 === 0 ? [0.78, 0.5, 0.3] : [0.42, 0.42, 0.46],
        place(ob.halfW * 2, ob.height, ob.halfD * 2, 0, ob.x, ob.height / 2, ob.z),
      );
    }
  }

  // ------------------------------------------------------------------
  // District mission world (ADR-022).

  private buildMissionWorld(sim: Simulation, batch: StaticBatch): void {
    const mission = sim.level.mission!;
    const net = mission.network;
    // A map that declares `imagery.only` IS the photograph (ADR-098). Every
    // other surface this method would draw — the road ribbon, the kerbs, the
    // pavement, the city, the trees — is invented, and laying invented
    // geometry over measured pixels does two kinds of harm at once: the road
    // ribbon sits at y=0.02 and covers the very data the map exists to show,
    // and what stays visible around it reads as evidence when it is only
    // decoration. So draw the data and stop. The robot still drives the
    // compiled network, arrives at the compiled destination and clamps to the
    // same kerb: only the looks are gone.
    if (mission.imagery?.only) {
      this.buildImagery(mission.imagery, mission.mirror);
      this.photographicWorld();
      return;
    }
    const b = net.bounds;
    const pad = CITY_PAD + 40;
    const ground = this.track(
      CreateGround("ground", { width: b.maxX - b.minX + 2 * pad, height: b.maxZ - b.minZ + 2 * pad }, this.scene),
    );
    ground.position.set((b.minX + b.maxX) / 2, -0.02, (b.minZ + b.maxZ) / 2);
    // The ground IS the pavement here — the strip the player sees runs from
    // kerb to building line. Grey pavers with terracotta courses, drawn from
    // the palette of the robot's own footage (reference_real). CreateGround
    // carries 0..1 UVs over the whole plane, so tiling is size / period.
    const gw = b.maxX - b.minX + 2 * pad;
    const gh = b.maxZ - b.minZ + 2 * pad;
    const pave = texMat(this.scene, "groundMat", `${import.meta.env.BASE_URL}textures/pavers.jpg`, 1, [0.94, 0.93, 0.92]);
    if (pave.diffuseTexture instanceof Texture) {
      pave.diffuseTexture.uScale = gw / 2.0;
      pave.diffuseTexture.vScale = gh / 2.0;
    }
    ground.material = pave;
    this.buildTrees(mission, batch);

    if (this.isAdventureBenchmark && this.adventureMaterials) {
      // Render-only kit: this exploration map owns the authoritative road and
      // prop footprints; architecture stays beyond the carriageway.
      ground.material = this.adventureMaterials.material('pavementBase', [.46, .43, .36], 'paving');
      const sourceRoad = net.pieces.find((piece): piece is BandPiece => piece.kind === 'band')!;
      const first = sourceRoad.pts[0]!, last = sourceRoad.pts[sourceRoad.pts.length - 1]!;
      const roadWidth = sourceRoad.halfWidth * 2, roadLength = Math.hypot(last.x - first.x, last.z - first.z);
      const road = this.track(CreateGround('adventureBenchmarkRoad', { width: roadWidth, height: roadLength }, this.scene));
      road.position.set((first.x + last.x) / 2, .012, (first.z + last.z) / 2);
      road.rotation.y = Math.atan2(last.x - first.x, last.z - first.z);
      const uv = road.getVerticesData('uv')!;
      for (let i = 0; i < uv.length; i += 2) { uv[i] = uv[i]! * roadWidth; uv[i + 1] = uv[i + 1]! * roadLength; }
      road.setVerticesData('uv', uv);
      road.material = this.adventureMaterials.material('asphalt', [.82, .77, .69], 'asphalt');
      this.adventureWorld = buildAdventureBenchmarkWorld(this.scene, {
        startZ: -12, length: 95, roadHalfWidth: sourceRoad.halfWidth, material: this.adventureMaterials.material,
      });
      const parkedCar = buildAdventureParkedCar(this.scene, {
        roadHalfWidth: sourceRoad.halfWidth, loadState: themeState(this.scene),
        onReady: meshes => {
          for (const mesh of meshes) this.shadows?.addShadowCaster(mesh, false);
          this.fisheye?.setRenderList(this.scene.meshes);
        },
      });
      this.adventureActors.push(parkedCar);
      this.buildProps(mission.obstacles, mission.mirror, batch);
      return;
    }
    if (mission.imagery) this.buildImagery(mission.imagery, mission.mirror);
    this.buildRoads(net, batch);
    if (this.envTheme === "suburban") this.buildVerges(net, batch);
    this.buildCity(net, mission.decor, batch);
    for (const d of mission.decor) {
      if (d.kind !== "tree") this.buildDecor(d, net, batch);
    }
    this.buildProps(mission.obstacles, mission.mirror, batch);
    this.buildGuidance(mission.path, mission.network);
  }

  /**
   * Asphalt with the weather applied (ADR-054): a wet map darkens the sheet
   * and gives it a specular sheen. texMat caches by name across level
   * rebuilds, so both states are set explicitly every build — a dry map
   * after a wet one must dry the road back out.
   */
  private roadSurface(name: string, periodM: number, tint: Rgb): PBRMaterial | StandardMaterial {
    const theme = appearanceTheme(this.appearance);
    tint = vary(this.scene, tint, name, "road");
    if (!theme.policy.photographicSurfaces && !texturePath(this.scene, "textures/asphalt.jpg")) {
      const road = theme.palette.road!;
      const wet = this.wet ? 0.7 : 1;
      return mat(this.scene, name, [road[0] * wet, road[1] * wet, road[2] * wet]);
    }
    name = `${this.appearance}_${name}`;
    // Asphalt as a metallic-roughness surface (ADR-061). Dry it is nearly
    // matte and behaves as the old Standard material did; wet, roughness
    // drops and the sky HDR appears in the road, which is what a wet street
    // actually looks like and what a specular highlight could only imitate.
    const existing = this.scene.getMaterialByName(name);
    let m: PBRMaterial;
    if (existing instanceof PBRMaterial) {
      m = existing;
    } else {
      m = new PBRMaterial(name, this.scene);
      const path = texturePath(this.scene, "textures/asphalt.jpg");
      const t = path ? themedTexture(this.scene, path, () => { m.albedoTexture = null; m.albedoColor = Color3.FromArray(theme.palette.road!); }) : null;
      if (t) {
        t.wrapU = Texture.WRAP_ADDRESSMODE;
        t.wrapV = Texture.WRAP_ADDRESSMODE;
        t.uScale = 1 / periodM;
        t.vScale = 1 / periodM;
      }
      m.albedoTexture = t;
      m.metallic = 0;
    }
    // Asphalt is dark; a PBR surface reads brighter than the Standard one it
    // replaced, and at night the road came out a pale grey sheet. Scale the
    // albedo with the hour as well as the weather.
    const dim = this.tod === "night" ? 0.5 : this.tod === "dusk" ? 0.72 : 1;
    const wetK = this.wet ? 0.55 : 1;
    m.albedoColor = new Color3(tint[0] * dim * wetK, tint[1] * dim * wetK, tint[2] * dim * wetK * (this.wet ? 1.08 : 1));
    if (!m.albedoTexture) m.albedoColor = Color3.FromArray(vary(this.scene, themeColor(this.appearance, theme.palette.road!), name, "road"));
    m.roughness = this.wet ? 0.32 : theme.material.roughness;
    m.environmentIntensity = this.wet ? 0.75 : 0.35;
    return m;
  }

  /**
   * Tar patches and gutter litter (ADR-059).
   *
   * The reference clips are unanimous and it is the easiest thing to miss:
   * real asphalt is mottled. Dark repair patches spread across the lane,
   * paler scuffed ground between them, and a drift of fallen leaves along
   * the kerb where the sweeper does not reach. Our sheet was one photograph
   * tiled evenly, which is why it read as a material rather than a road.
   *
   * Soft-edged quads on the blob texture, thin-instanced: no new art, and a
   * few hundred of them cost one draw call.
   */
  private buildRoadGrime(net: RoadNetwork): void {
    const patches: number[] = [];
    const leaves: number[] = [];
    for (const piece of net.pieces) {
      if (piece.kind !== "band" || piece.id.startsWith("mouth_")) continue;
      const pts = piece.pts;
      for (let i = 0; i + 1 < pts.length; i++) {
        const a = pts[i]!;
        const b = pts[i + 1]!;
        const len = Math.hypot(b.x - a.x, b.z - a.z);
        if (len < 1e-6) continue;
        const ux = (b.x - a.x) / len;
        const uz = (b.z - a.z) / len;
        const nx = uz;
        const nz = -ux;
        const hw = piece.halfWidth;
        for (let d = 0; d < len; d += 3.5) {
          const h1 = hash01(Math.round((a.x + ux * d) * 7), Math.round((a.z + uz * d) * 11));
          if (h1 > 0.55) continue;
          const off = (hash01(Math.round(d * 13), Math.round(len * 3)) - 0.5) * 2 * (hw - 0.8);
          const px = a.x + ux * d + nx * off;
          const pz = a.z + uz * d + nz * off;
          if (net.sdf(px, pz) > -0.5) continue;
          const w = 0.9 + 2.6 * h1;
          const m = place(w, 1, w * (0.5 + h1), hash01(Math.round(px), Math.round(pz)) * Math.PI, px, 0.021, pz);
          for (let q = 0; q < 16; q++) patches.push(m.m[q]!);
        }
        // Leaf drift, hard against both kerbs.
        for (let d = 1; d < len; d += 1.7) {
          for (const side of [-1, 1]) {
            const h2 = hash01(Math.round(d * 5 + side * 3), Math.round((a.x + a.z) * 7));
            if (h2 > 0.4) continue;
            const off = (hw - 0.35 - h2 * 0.9) * side;
            const px = a.x + ux * d + nx * off;
            const pz = a.z + uz * d + nz * off;
            if (net.sdf(px, pz) > -0.2) continue;
            const m = place(0.5 + h2, 1, 0.9 + h2 * 2, hash01(Math.round(px * 3), Math.round(pz * 3)) * Math.PI, px, 0.024, pz);
            for (let q = 0; q < 16; q++) leaves.push(m.m[q]!);
          }
        }
      }
    }
    const soft = (name: string, rgb: Rgb, alpha: number, data: number[]): void => {
      if (data.length === 0) return;
      const g = CreateGround(name, { width: 1, height: 1 }, this.scene);
      const pm = transientMaterial(this.scene, `${name}Mat`);
      const t = this.appearance === "neighborhood" && pm.diffuseTexture instanceof Texture ? pm.diffuseTexture : themedTexture(this.scene, texturePath(this.scene, "textures/blobshadow.png")!);
      t.hasAlpha = true;
      pm.diffuseTexture = t;
      pm.diffuseColor = Color3.Black();
      pm.emissiveColor = new Color3(rgb[0], rgb[1], rgb[2]);
      pm.specularColor = Color3.Black();
      pm.disableLighting = true;
      pm.useAlphaFromDiffuseTexture = true;
      pm.transparencyMode = 2;
      pm.alpha = alpha;
      g.material = pm;
      g.isPickable = false;
      g.thinInstanceSetBuffer("matrix", new Float32Array(data), 16);
      this.track(g);
    };
    soft("tarPatches", [0.1, 0.1, 0.11], 0.5, patches);
    soft("leafDrift", [0.55, 0.47, 0.24], 0.42, leaves);
  }

  /** Road ribbons (one merged mesh per road material), curbs, ring island. */
  /**
   * Lay the real road photograph where it was taken (ADR-098).
   *
   * Lit FLAT, on purpose. The image came out of a camera that had already
   * exposed and tone-mapped the scene; running the scene's own sun and ambient
   * over it a second time is the mistake that washed the Gaussian worlds out to
   * near-white (ADR-086), and it is the same mistake here.
   */
  private buildImagery(img: { url: string; originXZ: [number, number]; mpp: number; lift?: number },
                       mirror: 1 | -1): void {
    const tex = new Texture(img.url, this.scene, true, false, Texture.TRILINEAR_SAMPLINGMODE, () => {
      const w = tex.getSize().width * img.mpp;
      const h = tex.getSize().height * img.mpp;
      const g = this.track(CreateGround("mapImagery", { width: w, height: h }, this.scene));
      // originXZ is the top-left of the image; a ground plane is centred.
      g.position.set((img.originXZ[0] + w / 2) * mirror, img.lift ?? 0.015, img.originXZ[1] - h / 2);
      if (mirror < 0) g.scaling.x = -1;
      const mat = new StandardMaterial("mapImageryMat", this.scene);
      mat.emissiveTexture = tex;
      mat.opacityTexture = tex;      // only where a camera actually saw the ground
      tex.hasAlpha = true;
      mat.diffuseColor = Color3.Black();
      mat.specularColor = Color3.Black();
      mat.disableLighting = true;
      mat.backFaceCulling = false;
      g.material = mat;
      g.isPickable = false;
      g.receiveShadows = false;
      this.fisheye?.setRenderList(this.scene.meshes);
      console.info(`imagery: ${img.url} laid at ${img.originXZ} covering ${w.toFixed(1)} x ${h.toFixed(1)} m`);
    }, () => console.error(`imagery: failed to load ${img.url}`));
  }

  private buildRoads(net: RoadNetwork, batch: StaticBatch): void {
    const street = new GeomBuilder();
    const alley = new GeomBuilder();
    for (const piece of net.pieces) {
      if (piece.kind === "band") {
        const isAlley = piece.roadType === "alley";
        const g = isAlley ? alley : street;
        // Stacked by kind so overlapping coplanar pieces never z-fight: streets
        // sit above alleys (and the ring) so an alley's cap/tongue vanishes
        // under the street it opens onto; mouths sit above everything.
        const y = isAlley ? 0 : piece.id.startsWith("mouth_") ? 0.03 : 0.02;
        const hw = piece.halfWidth + 0.1;
        for (let i = 0; i + 1 < piece.pts.length; i++) {
          const a = piece.pts[i]!;
          const c = piece.pts[i + 1]!;
          const len = Math.hypot(c.x - a.x, c.z - a.z) || 1;
          const ux = (c.x - a.x) / len;
          const uz = (c.z - a.z) / len;
          const nx = uz * hw;
          const nz = -ux * hw;
          g.addFlat(
            [
              { x: a.x + nx, z: a.z + nz },
              { x: c.x + nx, z: c.z + nz },
              { x: c.x - nx, z: c.z - nz },
              { x: a.x - nx, z: a.z - nz },
            ],
            y,
          );
        }
        for (const p of piece.pts) g.addDisc(p.x, p.z, hw, y, 20);
        this.addBandCurbs(piece, net, batch);
      } else {
        street.addAnnulus(piece.cx, piece.cz, piece.rIn - 0.1, piece.rOut + 0.1, 0.01, 48);
        const island = this.track(
          CreateCylinder("island", { diameter: (piece.rIn - 0.12) * 2, height: 0.4, tessellation: 32 }, this.scene),
        );
        island.position.set(piece.cx, 0.2, piece.cz);
        island.material = mat(this.scene, "islandMat", ISLAND);
        // A roundabout island is a planted thing, not a concrete puck: grass
        // across the top and one substantial tree in the middle, which also
        // gives the junction a landmark you can see over the traffic.
        const lawn = new GeomBuilder();
        lawn.addDisc(piece.cx, piece.cz, Math.max(0.5, piece.rIn - 0.55), 0.41, 28);
        const lawnMesh = this.track(lawn.build("islandLawn", this.scene));
        lawnMesh.material = texMat(this.scene, "vergeMat", `${import.meta.env.BASE_URL}textures/grass.jpg`, 2.6, [0.95, 0.97, 0.92]);
        this.addRingCurbs(piece, net, batch);
      }
    }
    if (!street.empty) {
      const m = this.track(street.build("roadStreet", this.scene));
      // Real asphalt (PolyHaven, mined from the team's own SVL simulator
      // bundles): tyre scuffs and cracks that a flat grey never had. 7 m per
      // repeat, so the scuff pattern does not visibly loop at driving speed.
      m.material = this.roadSurface("roadStreetMat", 7, [0.98, 0.98, 0.99]);
    }
    this.buildRoadMarkings(net);
    this.buildRoadGrime(net);
    if (!alley.empty) {
      const m = this.track(alley.build("roadAlley", this.scene));
      // Standard asphalt here too, a shade darker and tighter than the
      // street. A cracked sheet was tried and rejected: it read as a road
      // nobody maintains, not as an alley, and the reference streets are
      // simply ordinary tarmac everywhere.
            m.material = this.roadSurface("roadAlleyMat", 5, [0.86, 0.86, 0.88]);
    }
  }

  /**
   * Ground markings. Two-way roads get a dashed centre line, one-way roads
   * get arrows down the middle, and every street gets an edge line inside
   * each kerb. Alleys are painted only when one-way: an unmarked narrow
   * lane is what an alley looks like.
   */
  private buildRoadMarkings(net: RoadNetwork): void {
    const paint = new GeomBuilder();
    // Two-way centre dashes are YELLOW, separately built: Vietnamese roads
    // (and every frame of the OCP footage) mark opposing flows in yellow and
    // same-direction lanes in white. Painting the centre line white was one
    // of those quiet wrongnesses nobody could name.
    const centre = new GeomBuilder();
    const arrows: number[] = [];
    for (const piece of net.pieces) {
      if (piece.kind !== "band") continue;
      const isAlley = piece.roadType === "alley";
      // Every two-way carriageway gets its centre line, alleys included —
      // reviewed against the OCP estate, where even the narrow internal
      // lanes carry the yellow dashes, start stretch and all.
      const y = (isAlley ? 0 : piece.id.startsWith("mouth_") ? 0.03 : 0.02) + PAINT_LIFT;
      let travelled = 0;
      for (let i = 0; i + 1 < piece.pts.length; i++) {
        const a = piece.pts[i]!;
        const c = piece.pts[i + 1]!;
        const len = Math.hypot(c.x - a.x, c.z - a.z);
        if (len < 1e-6) continue;
        const ux = (c.x - a.x) / len;
        const uz = (c.z - a.z) / len;
        if (piece.oneWay) {
          const yaw = Math.atan2(ux, uz);
          const first = ARROW_SPACING / 2 - (travelled % ARROW_SPACING);
          for (let d = first; d < len; d += ARROW_SPACING) {
            const px = a.x + ux * d;
            const pz = a.z + uz * d;
            if (this.paintBlocked(net, piece, px, pz)) continue;
            const mtx = Matrix.RotationY(yaw).multiply(Matrix.Translation(px, y, pz));
            for (let k = 0; k < 16; k++) arrows.push(mtx.m[k]!);
          }
        } else {
          for (let d = 0; d < len; d += DASH_LEN + DASH_GAP) {
            const end = Math.min(len, d + DASH_LEN);
            const mx = a.x + ux * ((d + end) / 2);
            const mz = a.z + uz * ((d + end) / 2);
            if (this.paintBlocked(net, piece, mx, mz)) continue;
            addStripe(centre, a.x + ux * d, a.z + uz * d, a.x + ux * end, a.z + uz * end, DASH_HALF_W, y);
          }
        }
        if (!isAlley) {
          const inset = piece.halfWidth - EDGE_INSET;
          for (const side of [1, -1]) {
            const nx = uz * inset * side;
            const nz = -ux * inset * side;
            const steps = Math.max(1, Math.ceil(len / 4));
            for (let k = 0; k < steps; k++) {
              const d0 = (len * k) / steps;
              const d1 = (len * (k + 1)) / steps;
              const mx = a.x + ux * ((d0 + d1) / 2) + nx;
              const mz = a.z + uz * ((d0 + d1) / 2) + nz;
              if (this.paintBlocked(net, piece, mx, mz)) continue;
              addStripe(
                paint,
                a.x + ux * d0 + nx,
                a.z + uz * d0 + nz,
                a.x + ux * d1 + nx,
                a.z + uz * d1 + nz,
                EDGE_HALF_W,
                y,
              );
            }
          }
        }
        travelled += len;
      }
    }
    // Zebra crossings on the approach to a junction. A real crossing joins
    // one kerb to the other across a single carriageway — never floats in
    // the shared junction asphalt — so the band is marched inward from the
    // piece end until its whole footprint (both kerb edges, front and back)
    // sits on ground this piece alone owns. Streets only: an alley mouth in
    // OCP is a continuous footway, not a marked crossing, and the first
    // attempt at painting one there landed mid-junction and read as absurd.
    // Paints one crossing if the whole band — both kerb edges, front and
    // back — sits on ground this piece alone owns; refuses otherwise.
    const tryZebra = (piece: BandPiece, ax: number, az: number, ux: number, uz: number): boolean => {
      const nx = uz;
      const nz = -ux;
      const half = piece.halfWidth - 0.3; // kerb to kerb
      for (const [ds, t] of [
        [-ZEBRA_DEPTH / 2, -half],
        [-ZEBRA_DEPTH / 2, half],
        [ZEBRA_DEPTH / 2, -half],
        [ZEBRA_DEPTH / 2, half],
        [-ZEBRA_DEPTH / 2, 0],
        [ZEBRA_DEPTH / 2, 0],
      ] as const) {
        if (this.paintBlocked(net, piece, ax + ux * ds + nx * t, az + uz * ds + nz * t)) return false;
      }
      const nStripes = Math.max(4, Math.round((half * 2) / (ZEBRA_STRIPE * 2)));
      for (let k = 0; k < nStripes; k++) {
        const t = -half + ZEBRA_STRIPE / 2 + k * ZEBRA_STRIPE * 2;
        if (t + ZEBRA_STRIPE / 2 > half) break;
        addStripe(
          paint,
          ax + nx * t - ux * (ZEBRA_DEPTH / 2),
          az + nz * t - uz * (ZEBRA_DEPTH / 2),
          ax + nx * t + ux * (ZEBRA_DEPTH / 2),
          az + nz * t + uz * (ZEBRA_DEPTH / 2),
          ZEBRA_STRIPE / 2,
          // Top of the road stack so a mouth cap overlapping the approach
          // can never bury the paint.
          0.03 + PAINT_LIFT + 0.012,
        );
      }
      return true;
    };
    // A junction is worth a crossing only if what joins is itself a street
    // or a roundabout — an alley mouth in OCP is a continuous footway, not
    // a marked crossing, and the first attempt at painting one there landed
    // mid-junction and read as absurd.
    const zebraWorthy = (piece: RoadPiece, px: number, pz: number): boolean => {
      for (const q of net.pieces) {
        if (q === piece || pieceSdf(q, px, pz) >= -0.3) continue;
        if (q.kind !== "band" || (q.roadType !== "alley" && !q.id.startsWith("mouth_"))) return true;
      }
      return false;
    };
    for (const piece of net.pieces) {
      if (piece.kind !== "band" || piece.roadType === "alley" || piece.id.startsWith("mouth_")) continue;
      const pts = piece.pts;
      const total = pts.reduce((acc, q, i) => (i ? acc + Math.hypot(q.x - pts[i - 1]!.x, q.z - pts[i - 1]!.z) : 0), 0);
      if (total < ZEBRA_MIN_ROAD) continue;
      // Walk the centreline; every stretch owned by a junction gets one
      // crossing marched clear of each side of it (which also covers the
      // piece's own ends: the junction stretch is simply half-open there).
      const placed: number[] = [];
      let sAll = 0;
      const marks: { s: number; x: number; z: number; ux: number; uz: number; blocked: boolean }[] = [];
      for (let i = 1; i < pts.length; i++) {
        const p0 = pts[i - 1]!;
        const p1 = pts[i]!;
        const len = Math.hypot(p1.x - p0.x, p1.z - p0.z);
        if (len < 1e-6) continue;
        const ux = (p1.x - p0.x) / len;
        const uz = (p1.z - p0.z) / len;
        for (let d = 0; d < len; d += 1) {
          const x = p0.x + ux * d;
          const z = p0.z + uz * d;
          marks.push({ s: sAll + d, x, z, ux, uz, blocked: this.paintBlocked(net, piece, x, z) });
        }
        sAll += len;
      }
      for (let i = 0; i < marks.length; i++) {
        const m = marks[i]!;
        const prev = i > 0 ? marks[i - 1]! : null;
        const entering = m.blocked && (!prev || !prev.blocked);
        const leaving = !m.blocked && prev?.blocked;
        if (!entering && !leaving) continue;
        if (!zebraWorthy(piece, m.x, m.z)) continue;
        // March away from the junction stretch to find clean ground.
        const dir = entering ? -1 : 1;
        for (let s = ZEBRA_SETBACK; s <= ZEBRA_SETBACK + 9; s += 0.5) {
          const at = m.s + dir * s;
          if (at < 1 || at > sAll - 1) break;
          if (placed.some((p) => Math.abs(p - at) < 8)) break;
          const ax = m.x + m.ux * dir * s;
          const az = m.z + m.uz * dir * s;
          if (tryZebra(piece, ax, az, m.ux, m.uz)) {
            placed.push(at);
            break;
          }
        }
      }
      // Piece ends that touch a junction beyond them (the junction asphalt
      // may not reach back into this piece, so the walk above can miss it).
      for (const end of [0, 1]) {
        const a = end === 0 ? pts[0]! : pts[pts.length - 1]!;
        const b = end === 0 ? pts[1]! : pts[pts.length - 2]!;
        const len = Math.hypot(b.x - a.x, b.z - a.z) || 1;
        const ux = (b.x - a.x) / len;
        const uz = (b.z - a.z) / len;
        if (!zebraWorthy(piece, a.x - ux * 1.5, a.z - uz * 1.5)) continue;
        for (let s = ZEBRA_SETBACK; s <= ZEBRA_SETBACK + 9; s += 0.5) {
          const at = end === 0 ? s : sAll - s;
          if (placed.some((p) => Math.abs(p - at) < 8)) break;
          if (tryZebra(piece, a.x + ux * s, a.z + uz * s, ux, uz)) {
            placed.push(at);
            break;
          }
        }
      }
    }

    const paintMat = mat(this.scene, "roadPaintMat", ROAD_PAINT);
    if (!paint.empty) {
      const m = this.track(paint.build("roadPaint", this.scene));
      m.material = paintMat;
    }
    if (!centre.empty) {
      const m = this.track(centre.build("roadCentre", this.scene));
      m.material = mat(this.scene, "roadCentreMat", CENTRE_PAINT);
    }
    if (arrows.length > 0) {
      const g = new GeomBuilder();
      g.addPrism(LANE_ARROW, LANE_ARROW_TRIS, 0.001, 0.004);
      const mesh = this.track(g.build("laneArrows", this.scene));
      mesh.material = paintMat;
      mesh.thinInstanceSetBuffer("matrix", new Float32Array(arrows), 16);
    }
  }

  /**
   * True where another road piece overlaps, so paint stops at junctions
   * and roundabout mouths instead of running through them.
   */
  private paintBlocked(net: RoadNetwork, self: RoadPiece, x: number, z: number): boolean {
    for (const other of net.pieces) {
      if (other === self) continue;
      if (pieceSdf(other, x, z) < -0.2) return true;
    }
    return false;
  }

  /** One curb box unless its outward probe line (midpoint or either end)
   * lies inside another road piece — junctions and ring mouths stay open. */
  private addCurb(net: RoadNetwork, batch: StaticBatch, cx: number, cz: number, px: number, pz: number, yaw: number, len: number, gutterSide = 1): void {
    const tx = Math.sin(yaw) * (len / 2);
    const tz = Math.cos(yaw) * (len / 2);
    if (net.sdf(px, pz) < 0 || net.sdf(px + tx, pz + tz) < 0 || net.sdf(px - tx, pz - tz) < 0) return;
    const chunkSize = this.appearance === "neighborhood" ? 12 : CITY_CHUNK;
        const chunk = `${Math.floor(cx / chunkSize)},${Math.floor(cz / chunkSize)}`;
    // Painted kerb (ADR-059). Every reference clip from OCP shows the same
    // thing: a white-painted kerb face with grime streaks, capped by pale
    // concrete. A plain grey box was reading as a step in a car park.
    batch.add("box", KERB_PAINT, place(CURB_SIZE, CURB_SIZE, len, yaw, cx, CURB_SIZE / 2, cz), chunk);
    batch.add("box", KERB_CAP, place(CURB_SIZE * 1.08, 0.035, len, yaw, cx, CURB_SIZE, cz), chunk);
    // The gutter pan: a pale concrete strip inside the kerb, bounded by the
    // edge line. In the footage it is as wide as the kerb is tall and is one
    // of the strongest cues that the road has an edge at all.
    const gx = cx - Math.cos(yaw) * (CURB_SIZE / 2 + GUTTER_W / 2) * gutterSide;
    const gz = cz + Math.sin(yaw) * (CURB_SIZE / 2 + GUTTER_W / 2) * gutterSide;
    batch.add("box", GUTTER, place(GUTTER_W, 0.02, len, yaw, gx, 0.012, gz), chunk);
  }

  private addBandCurbs(piece: BandPiece, net: RoadNetwork, batch: StaticBatch): void {
    const hw = piece.halfWidth;
    const pts = piece.pts;
    for (let i = 0; i + 1 < pts.length; i++) {
      const a = pts[i]!;
      const c = pts[i + 1]!;
      const len = Math.hypot(c.x - a.x, c.z - a.z);
      if (len < 1e-6) continue;
      const ux = (c.x - a.x) / len;
      const uz = (c.z - a.z) / len;
      const nx = uz;
      const nz = -ux;
      const yaw = Math.atan2(ux, uz);
      const nSeg = Math.max(1, Math.ceil(len / CURB_LEN));
      const step = len / nSeg;
      for (const side of [-1, 1]) {
        for (let k = 0; k < nSeg; k++) {
          const t = (k + 0.5) * step;
          const mx = a.x + ux * t;
          const mz = a.z + uz * t;
          this.addCurb(
            net,
            batch,
            mx + nx * side * (hw + CURB_SIZE / 2),
            mz + nz * side * (hw + CURB_SIZE / 2),
            mx + nx * side * (hw + CURB_PROBE),
            mz + nz * side * (hw + CURB_PROBE),
            yaw,
            step,
            side,
          );
        }
      }
    }
    // Round caps at both ends (the half circle facing away from the band).
    for (const end of [0, pts.length - 1]) {
      const p = pts[end]!;
      const q = end === 0 ? pts[1]! : pts[pts.length - 2]!;
      const dx = p.x - q.x;
      const dz = p.z - q.z;
      if (Math.hypot(dx, dz) < 1e-6) continue;
      const phi0 = Math.atan2(dx, dz); // outward yaw
      const r = hw + CURB_SIZE / 2;
      const nArc = Math.max(3, Math.ceil((Math.PI * r) / CURB_LEN));
      const dphi = Math.PI / nArc;
      const chord = 2 * r * Math.sin(dphi / 2);
      for (let k = 0; k < nArc; k++) {
        const phi = phi0 - Math.PI / 2 + (k + 0.5) * dphi;
        const sx = Math.sin(phi);
        const cz = Math.cos(phi);
        this.addCurb(
          net,
          batch,
          p.x + sx * r,
          p.z + cz * r,
          p.x + sx * (hw + CURB_PROBE),
          p.z + cz * (hw + CURB_PROBE),
          phi + Math.PI / 2,
          chord,
        );
      }
    }
  }

  private addRingCurbs(piece: RingPiece, net: RoadNetwork, batch: StaticBatch): void {
    for (const [r, probeR] of [
      [piece.rIn - CURB_SIZE / 2, piece.rIn - CURB_PROBE],
      [piece.rOut + CURB_SIZE / 2, piece.rOut + CURB_PROBE],
    ] as const) {
      const n = Math.ceil((2 * Math.PI * r) / CURB_LEN);
      const dphi = (2 * Math.PI) / n;
      const chord = 2 * r * Math.sin(dphi / 2);
      for (let k = 0; k < n; k++) {
        const phi = (k + 0.5) * dphi;
        const sx = Math.sin(phi);
        const cz = Math.cos(phi);
        this.addCurb(
          net,
          batch,
          piece.cx + sx * r,
          piece.cz + cz * r,
          piece.cx + sx * probeR,
          piece.cz + cz * probeR,
          phi + Math.PI / 2,
          chord,
        );
      }
    }
  }

  /** Voxel city: a box on every off-road grid cell (deterministic heights/tints). */
  /**
   * Put the scene into the shadow map (ADR-060).
   *
   * Casters are everything solid; receivers are everything, because a wall
   * takes the shadow of the tree in front of it as readily as the road does.
   * Excluded by name: the sky dome (it encloses the camera, so it would
   * shadow the entire world), and the soft decals — blob shadows, lamp
   * pools, tar patches — which are painted darkness already and would cast
   * a second, squarer darkness of their own.
   */
  private wireShadows(): void {
    const gen = this.shadows;
    if (!gen) return;
    const map = gen.getShadowMap();
    if (!map) return;
    map.renderList = [];
    const skip = /^(skyDome|splatWorld|blobShadow|lampPools|tarPatches|leafDrift|headlightPool|roadPaint|roadCentre|laneArrows|paveTrim)/;
    for (const m of this.scene.meshes) {
      if (!(m instanceof Mesh)) continue;
      m.receiveShadows = !skip.test(m.name) || m.name.startsWith("road");
      if (skip.test(m.name) || m.material?.name.startsWith("adventure-wear-")) continue;
      // Flat road/base planes only receive shadows. Casting them introduces
      // self-shadow rings on the normal-mapped asphalt at the small hero bias.
      if (this.isAdventureBenchmark && (m.name === "ground" || m.material?.name === "adventureBenchmark_asphalt")) continue;
      map.renderList.push(m);
    }
  }

  /**
   * Hide the built scenery so a photographic world (a Gaussian-splat
   * reconstruction of a real street) is what the cameras see (ADR-086).
   *
   * Only the LOOKS go: the road network, the kerb clamp and every collision
   * test read the level's geometry, not these meshes, so the robot still
   * drives the same road it always did — it simply drives it inside the real
   * photographs. The robot itself, the other road users and any marker stay
   * visible: they are what the sim adds to the real world, and hiding them
   * would defeat the purpose. The sky dome is not one of them — it is built
   * scenery like the rest, and a photographic world brings its own sky.
   */
  hideBuiltWorld(): void {
    this.photographicWorld();
    // No `guide`: the corridor is painted lane, and painted lane on a street
    // that has its own markings reads as the sim inventing road furniture. The
    // destination beacon carries the route instead.
    // `skyDome` is kept ONLY when the map brought its own sky (ADR-133). The
    // comment that used to sit here said a photographic world brings its own
    // sky, and until now none did — so the dome went out with the rest of the
    // built scenery and the street arrived under nothing at all. Setting a
    // texture on it, as the first attempt did, changes nothing while the mesh
    // is switched off: reported from the deployed site as "no sky at all".
    const sky = this.mapSkyUrl ? "|skyDome" : "";
    const keep = this.freeRoam
      ? new RegExp(`^(robot|robotWobble|tailPivot|splatWorld|panoWorld|mapImagery|ped_|ag_|blobShadow|package${sky})`)
      : new RegExp(`^(robot|robotWobble|tailPivot|splatWorld|panoWorld|mapImagery|ped_|ag_|blobShadow|dest|marker|arrow|package${sky})`);
    // Walk the whole ancestor chain, not just the immediate parent. The robot
    // is a loaded GLB, so its meshes sit six levels down —
    // `WhitePlastic <- Body <- Bulldog_Clone_ <- RootNode <- __root__ <-
    // robotWobble <- robot` — and a `parent?.name === "robot"` test misses
    // every one of them. Measured: in a splat world the robot's body, wheels,
    // visor, arms and tail were all switched off, leaving only the procedural
    // fallback shell and the package floating down the street.
    const isKept = (m: { name: string; parent: { name: string; parent: unknown } | null }): boolean => {
      for (let n: { name: string; parent: unknown } | null = m; n; n = n.parent as typeof n) {
        if (keep.test(n.name)) return true;
      }
      return false;
    };
    let hidden = 0;
    for (const m of this.scene.meshes) {
      if (isKept(m)) continue;
      if (m === this.splatMesh) continue;
      m.setEnabled(false);
      hidden++;
    }
    this.builtWorldHidden = true;
    console.info(`splat: hid ${hidden} built meshes — the reconstruction is the world now`);
  }

  /**
   * Put the renderer into photographic mode: the world on screen came out of a
   * camera, not out of this engine.
   *
   * A photograph has the real street's light baked into it — it was already
   * exposed and tone-mapped when it was taken. Running the filmic pipeline
   * over it a second time (ACES + exposure 1.15 + contrast 1.12, tuned for the
   * gray-box world's flat lighting) washes the street out to near-white, which
   * is what happened to the Gaussian worlds (ADR-086) before this. The sky
   * dome goes for the same reason: real imagery brings its own sky, and the
   * built dome's bright white shows through every gap and lifts the whole
   * frame. Whatever the data does not cover should read as distance, not as a
   * light source behind it. The built world's own settings come back with the
   * next scene build.
   */
  private photographicWorld(): void {
    if (this.pipeline) {
      const ip = this.pipeline.imageProcessing;
      ip.toneMappingEnabled = false;
      ip.exposure = 1;
      ip.contrast = 1;
    }
    this.scene.clearColor = new Color4(0.55, 0.6, 0.66, 1);
    // A map that brings its own sky (sky_dome.py, ADR-133) keeps the dome ON.
    // `hideBuiltWorld` lists `skyDome` as kept, but keeping only means "not
    // switched off again": this line had already switched it off, so the map's
    // sky texture sat on a disabled mesh and the street showed the clear colour
    // instead (measured on ocp_sanho_depth: dome disabled, texture set).
    this.scene.getMeshByName("skyDome")?.setEnabled(!!this.mapSkyUrl);
    // The first-person guide is built when the view switches, which happens
    // before an async splat finishes loading — so setting the flag is not
    // enough, it has to be switched off here as well.
    this.fpvGuide?.setEnabled(false);
    // Where to look from. See PHOTOGRAPHIC_EYE for why this is a question at
    // all; `?eye=` and `?vfov=` are here so it can be answered by looking
    // rather than by rebuilding the site once per guess.
    const q = new URLSearchParams(location.search);
    const eye = Number(q.get("eye"));
    const vfov = Number(q.get("vfov"));
    if (Number.isFinite(eye) && eye > 0.1 && eye < 3) this.fpvCam.position.y = eye;
    if (Number.isFinite(vfov) && vfov > 10 && vfov < 140) this.fpvCam.fov = (vfov * Math.PI) / 180;
    this.builtWorldHidden = true;
  }

  /** True once the world became photographic (splat, pano or real imagery). */
  builtWorldHidden = false;

  /**
   * Roam a reconstruction instead of running a delivery through it (ADR-101).
   *
   * The robot stays, and stays drivable: it is what makes the scale and the eye
   * height read correctly, and driving it is how you reach a viewpoint. What
   * goes is the errand — the destination beacon standing in the middle of the
   * picture, and the episode ending at it, which put a delivery report over the
   * street and stopped the loop.
   *
   * An earlier attempt detached the camera from the robot entirely. That was
   * the wrong shape twice over: it hid the robot, and a photographic map opens
   * in first person, so the ACTIVE camera was `cam_fpv` bolted to the robot
   * while the controls went to a camera nobody was looking through.
   */
  freeRoam = false;

  enableFreeRoam(): void {
    this.freeRoam = true;
    this.hideErrandMarkers();
  }

  /**
   * The errand's furniture: the destination disc and beacon, route markers and
   * turn arrows.
   *
   * This has to run after every buildLevel, not once at setup. `enableFreeRoam`
   * is called while the app is still being assembled, and `buildLevel` comes
   * later and builds the destination again — so a free-roam session was getting
   * its beacon back, standing in the middle of the street it exists to show,
   * and a map was being judged with a finish line planted in it.
   *
   * The meshes are disabled, not skipped: the per-frame pulse and the
   * freeze-matrices pass both reach for `destMarker` by name.
   */
  private hideErrandMarkers(): void {
    for (const m of this.scene.meshes) {
      if (/^(dest|marker|arrow)/.test(m.name)) m.setEnabled(false);
    }
  }

  /**
   * Which way the street is, from a plot (ADR-057).
   *
   * `pieceForward` gives the road's own direction, which is what a building
   * aligns to — but anything that hangs off the FRONT of a building (a
   * balcony, an air-conditioner, an awning) needs the perpendicular, and
   * needs to know which of the two perpendiculars faces the carriageway.
   * The signed-distance field answers both at once: its gradient points
   * straight away from the road, so the negated gradient points at it.
   * Balconies keyed off the road direction instead were built into the
   * neighbour's party wall, where nothing could see them.
   */
  private towardRoad(net: RoadNetwork, x: number, z: number): { x: number; z: number } {
    const e = 0.35;
    const gx = net.sdf(x + e, z) - net.sdf(x - e, z);
    const gz = net.sdf(x, z + e) - net.sdf(x, z - e);
    const n = Math.hypot(gx, gz);
    if (n < 1e-6) return { x: 0, z: 1 };
    return { x: -gx / n, z: -gz / n };
  }

  /**
   * Roof and facade clutter (ADR-057).
   *
   * A block ends at a clean horizontal edge in exactly one place: a render.
   * Real ones carry a parapet, and in Vietnam a roof carries stainless water
   * tanks — the inox drums are the single most recognisable thing about a
   * Vietnamese skyline, and their absence is most of why the boxes read as
   * placeholder geometry. Air-conditioner units break the flat facade the
   * same way. Only near-road blocks get the treatment: past the first row
   * nothing of it is resolvable, and it is thin instances all the way down.
   */
  private addBlockDetails(
    batch: StaticBatch,
    chunk: string,
    yaw: number,
    cx: number,
    cz: number,
    fp: number,
    h: number,
    tint: Rgb,
    seed: number,
    street: { x: number; z: number },
  ): void {
    const R = (k: number) => hash01(Math.round(cx * 13 + k), Math.round(cz * 17 + seed));
    // Parapet: a low wall around the roof edge, slightly proud of the walls.
    batch.add("box", [tint[0] * 1.05, tint[1] * 1.05, tint[2] * 1.05], place(fp * 1.04, 0.34, fp * 1.04, yaw, cx, h + 0.17, cz), chunk);
    // Stainless water tanks, on short stands, set back from the parapet.
    const tanks = R(1) < 0.35 ? 2 : 1;
    for (let t = 0; t < tanks; t++) {
      const ox = (R(2 + t) - 0.5) * fp * 0.5;
      const oz = (R(5 + t) - 0.5) * fp * 0.5;
      const tx = cx + Math.cos(yaw) * ox - Math.sin(yaw) * oz;
      const tz = cz + Math.sin(yaw) * ox + Math.cos(yaw) * oz;
      batch.add("box", [0.42, 0.42, 0.44], place(0.7, 0.35, 0.7, yaw, tx, h + 0.5, tz), chunk);
      batch.add("cyl", [0.78, 0.79, 0.8], place(0.82, 1.05, 0.82, yaw, tx, h + 1.2, tz), chunk);
    }
    // A stair head-house on the bigger roofs.
    if (h > 6 && R(9) < 0.55) {
      const sx = cx + Math.cos(yaw) * fp * 0.22;
      const sz = cz + Math.sin(yaw) * fp * 0.22;
      batch.add("box", tint, place(fp * 0.3, 1.9, fp * 0.34, yaw, sx, h + 0.95, sz), chunk);
    }
    // Split units clinging to the street facade, a couple of floors apart.
    const front = fp / 2 + 0.16;
    const sideX = street.z;
    const sideZ = -street.x;
    const faceYawStreet = Math.atan2(street.x, street.z);
    for (let k = 0; k < 3; k++) {
      if (R(20 + k) > 0.55) continue;
      const fy = 2.6 + k * 3.1;
      if (fy > h - 0.8) break;
      const off = (R(30 + k) - 0.5) * fp * 0.6;
      const ax = cx + street.x * front + sideX * off;
      const az = cz + street.z * front + sideZ * off;
      batch.add("box", [0.84, 0.85, 0.83], place(0.78, 0.5, 0.34, faceYawStreet, ax, fy, az), chunk);
    }
  }

  private buildCity(net: RoadNetwork, decor: readonly DecorDef[], batch: StaticBatch): void {
    const b = net.bounds;
    const x0 = b.minX - CITY_PAD;
    const z0 = b.minZ - CITY_PAD;
    const nx = Math.ceil((b.maxX + CITY_PAD - x0) / CITY_CELL);
    const nz = Math.ceil((b.maxZ + CITY_PAD - z0) / CITY_CELL);
    for (let i = 0; i < nx; i++) {
      for (let j = 0; j < nz; j++) {
        const cx = x0 + (i + 0.5) * CITY_CELL;
        const cz = z0 + (j + 0.5) * CITY_CELL;
        const sd = net.sdf(cx, cz);
        if (sd <= CITY_MIN_SDF || sd > CITY_MAX_SDF) continue;
        // A few empty cells read as yards/gaps and cut the fill further.
        if (hash01(i * 11 + 3, j * 5 + 9) < 0.12) continue;
        let blocked = false;
        for (const d of decor) {
          if (Math.abs(d.x - cx) < DECOR_KEEPOUT && Math.abs(d.z - cz) < DECOR_KEEPOUT) {
            blocked = true;
            break;
          }
        }
        if (blocked) continue;
        const tint = CITY_TINTS[Math.floor(hash01(j * 7 + 1, i * 3 + 5) * CITY_TINTS.length)]!;
        const chunkSize = this.appearance === "neighborhood" ? 12 : CITY_CHUNK;
        const chunk = `${Math.floor(cx / chunkSize)},${Math.floor(cz / chunkSize)}`;
        // Face the street. Grid-aligned blocks along the HD maps' diagonal
        // roads read as a city built with no idea where its own street was;
        // buildings front onto the road they stand beside, everywhere.
        const npiece = net.pieces[net.deepestPiece(cx, cz)];
        const faceYaw = npiece ? pieceForward(npiece, cx, cz) : 0;
        // After dark every facade swaps for its lit-window twin (ADR-054).
        const nsuf = this.tod === "day" ? "" : "_night";
        if (this.appearance === "neighborhood") {
          const st = this.towardRoad(net, cx, cz);
          const frontYaw = Math.atan2(st.x, st.z);
          neighborhoodHouse((shape, color, transform, texture) => batch.add(shape, color, transform, chunk, texture),
            cx, cz, frontYaw, 6.2 + 3 * hash01(i, j), Math.floor(hash01(i * 13, j * 7) * 100), this.tod !== "day");
          continue;
        }
        if (this.envTheme === "suburban") {
          // The open master-planned district of the reference footage: most
          // plots empty, low villas with terracotta roofs set well back, and
          // towers only far from the road — the hazy skyline of ref_0, not a
          // wall of frontage. A continuous street wall read as inner-city,
          // which is a different place from the one the robot works.
          if (sd < CITY_MIN_SDF + 2.5) continue; // deeper setback
          if (sd > 30) {
            if (hash01(i * 13 + 1, j * 17 + 4) < 0.55) continue;
            const h = 9 + 9 * hash01(i, j);
            const fp = CITY_FOOTPRINT * 1.3;
            const facadeUrl = `${import.meta.env.BASE_URL}textures/facade${nsuf}.jpg`;
            // Taller blocks step back above a podium instead of rising as
            // one extruded rectangle — the profile of the real towers, and
            // the cheapest way to stop a skyline of identical slabs.
            const stepped = h > 13 && hash01(i * 5 + 2, j * 11 + 6) < 0.5;
            if (stepped) {
              const hp = h * 0.42;
              batch.add("box", tint, place(fp * 1.22, hp, fp * 1.22, faceYaw, cx, hp / 2, cz), chunk, facadeUrl);
              batch.add("box", tint, place(fp, h - hp, fp, faceYaw, cx, hp + (h - hp) / 2, cz), chunk, facadeUrl);
              batch.add("box", [tint[0] * 1.05, tint[1] * 1.05, tint[2] * 1.05], place(fp * 1.26, 0.3, fp * 1.26, faceYaw, cx, hp + 0.15, cz), chunk);
            } else {
              batch.add("box", tint, place(fp, h, fp, faceYaw, cx, h / 2, cz), chunk, facadeUrl);
            }
            this.addBlockDetails(batch, chunk, faceYaw, cx, cz, fp, h, tint, 3, this.towardRoad(net, cx, cz));
          } else {
            if (hash01(i * 11 + 3, j * 5 + 9) < 0.45) continue; // open plots
            const h = 2.2 + 1.3 * hash01(i, j);
            const fp = CITY_FOOTPRINT * 0.72;
            batch.add(
              "box",
              [0.93, 0.9, 0.84],
              place(fp, h, fp, faceYaw, cx, h / 2, cz),
              chunk,
              `${import.meta.env.BASE_URL}textures/facade${nsuf}.jpg`,
            );
            // Terracotta roof cap, slightly proud of the walls.
            batch.add("box", [0.62, 0.33, 0.25], place(fp * 1.12, 0.3, fp * 1.12, faceYaw, cx, h + 0.15, cz), chunk);
            // One water tank even on a villa: every flat roof in the
            // district has one, and the silhouette is the giveaway.
            if (hash01(i * 3 + 7, j * 13 + 2) < 0.7) {
              batch.add("cyl", [0.78, 0.79, 0.8], place(0.7, 0.9, 0.7, faceYaw, cx + fp * 0.2, h + 0.75, cz - fp * 0.2), chunk);
            }
          }
        } else if (sd <= CITY_MIN_SDF + 5) {
          // Terraced shophouse row (ADR-054): the OCP frontage is party-wall
          // shophouses — ground-floor shops under uniform upper storeys —
          // not detached blocks. Cells are keyed into rows by which road
          // they face and how far back they sit; every cell in a row shares
          // one height, and each two-cell "house" picks its own facade, so
          // the row reads terraced rather than photocopied. The footprint
          // spans the full cell (plus a lick of overlap) so neighbours touch.
          const dirX = Math.sin(faceYaw);
          const dirZ = Math.cos(faceYaw);
          const along = cx * dirX + cz * dirZ;
          const perp = cx * dirZ - cz * dirX;
          const rowBin = Math.floor(perp / CITY_CELL);
          const rowSeed = (npiece ? net.pieces.indexOf(npiece) : 0) * 31 + rowBin;
          const unit = Math.floor(along / (CITY_CELL * 2));
          // Four to five storeys, not the single-storey sheds the first pass
          // produced: an OCP shophouse is a narrow 4-5 floor block over a
          // double-height shopfront, and its height is most of why the row
          // reads as a street rather than a fence.
          const h = 11.5 + 4.5 * hash01(rowSeed, rowSeed * 7 + 3);
          const ground = 3.6;
          const facade = hash01(unit * 5 + 1, rowSeed * 3 + 2) < 0.4 ? "facade_brick.jpg" : "facade.jpg";
          const houseTint = CITY_TINTS[Math.floor(hash01(unit * 9 + 4, rowSeed) * CITY_TINTS.length)]!;
          const fpAlong = CITY_CELL + 0.06;
          batch.add(
            "box",
            [0.9, 0.88, 0.84],
            place(fpAlong, ground, CITY_FOOTPRINT, faceYaw, cx, ground / 2, cz),
            chunk,
            `${import.meta.env.BASE_URL}textures/${SHOPFRONTS[Math.floor(hash01(unit * 7 + 5, rowSeed * 5 + 1) * SHOPFRONTS.length)]!.replace(".jpg", `${nsuf}.jpg`)}`,
          );
          batch.add(
            "box",
            houseTint,
            place(fpAlong, h - ground, CITY_FOOTPRINT, faceYaw, cx, ground + (h - ground) / 2, cz),
            chunk,
            `${import.meta.env.BASE_URL}textures/${facade.replace(".jpg", `${nsuf}.jpg`)}`,
          );
          // A shophouse upper floor has a balcony, and the balcony is what
          // makes the row read as somewhere people live rather than as an
          // extruded strip: a thin slab with a dark railing on it, one per
          // floor above the shop.
          const st = this.towardRoad(net, cx, cz);
          const stYaw = Math.atan2(st.x, st.z);
          const frontZ = CITY_FOOTPRINT / 2;
          for (let fl = 1; ground + fl * 3.0 < h - 0.5; fl++) {
            const fy = ground + fl * 3.0;
            const bd = frontZ + 0.34;
            batch.add("box", [0.86, 0.85, 0.82], place(fpAlong * 0.86, 0.12, 0.72, stYaw, cx + st.x * bd, fy, cz + st.z * bd), chunk);
            const rd = frontZ + 0.68;
            batch.add("box", [0.32, 0.34, 0.36], place(fpAlong * 0.86, 0.62, 0.06, stYaw, cx + st.x * rd, fy + 0.37, cz + st.z * rd), chunk);
          }
          this.addBlockDetails(batch, chunk, faceYaw, cx, cz, CITY_FOOTPRINT, h, houseTint, 11, st);
          // A pot or two on the shopfront step: every Vietnamese shophouse
          // has them, and they are the cheapest thing that says a person
          // works behind that glass (ADR-063).
          if (hash01(unit * 13 + 2, rowSeed * 7 + 5) < 0.55) {
            const pd = frontZ + 0.9;
            const side2 = hash01(unit * 3, rowSeed) < 0.5 ? 1 : -1;
            const px2 = cx + st.x * pd + st.z * side2 * 0.9;
            const pz2 = cz + st.z * pd - st.x * side2 * 0.9;
            const node = new TransformNode(`pot_${Math.round(px2)}_${Math.round(pz2)}`, this.scene);
            node.position.set(px2, 0, pz2);
            this.track(node as unknown as Mesh);
            void this.mountDecorGlb(node, "potplant", 1.1 + 0.5 * hash01(unit, rowSeed), stYaw);
          }
        } else {
          const h = 2.5 + 3.5 * hash01(i, j);
          // Behind the shophouse frontage the fill stays mixed blocks, brick
          // against concrete, so the skyline is not one photocopied building.
          const facade =
            hash01(i * 3 + 2, j * 9 + 7) < 0.4 ? "facade_brick.jpg" : "facade.jpg";
          batch.add(
            "box",
            tint,
            place(CITY_FOOTPRINT, h, CITY_FOOTPRINT, faceYaw, cx, h / 2, cz),
            chunk,
            `${import.meta.env.BASE_URL}textures/${facade.replace(".jpg", `${nsuf}.jpg`)}`,
          );
        }
      }
    }
  }

  /** Every visible trunk has the same world footprint, regardless of appearance. */
  private buildTrees(mission: MissionBuild, batch: StaticBatch): void {
    const net = mission.network;
    // Three photographic street trees (CC0, cgbookcase) instead of one
    // repeated cut-out. Each carries its own width:height so the crossed
    // quads match the photo rather than stretching it into a different tree.
    const TREES: readonly (readonly [string, number])[] = [
      ["tree_a.png", 412 / 700],
      ["tree_b.png", 516 / 700],
      ["tree_c.png", 512 / 700],
    ];
    for (const { x: tx, z: tz } of streetTreePlacements(net, mission.mirror)) {
      const hgt = 4.2 + 2.8 * hash01(Math.round(tx * 7), Math.round(tz * 7));
      const pick = TREES[Math.floor(hash01(Math.round(tx * 17), Math.round(tz * 19)) * TREES.length)]!;
      const w = hgt * pick[1];
      const yaw = hash01(Math.round(tz * 3), Math.round(tx * 5)) * Math.PI;
      const chunk = `${Math.floor(tx / CITY_CHUNK)},${Math.floor(tz / CITY_CHUNK)}`;
      const tint = TREE_TINTS[Math.floor(hash01(Math.round(tx * 11), Math.round(tz * 13)) * TREE_TINTS.length)]!;
      // Every fourth planting is a real palm (ADR-063). Vinhomes Ocean
      // Park is planted with them and we had none — a billboard broadleaf
      // avenue is a different city. A mesh, not a billboard, so it holds
      // up when the camera passes a metre from the trunk.
      const isPalm = hash01(Math.round(tx * 23 + 5), Math.round(tz * 29 + 3)) < 0.26;
      if (isPalm) {
        const node = new TransformNode(`palm_${Math.round(tx)}_${Math.round(tz)}`, this.scene);
        node.position.set(tx, 0, tz);
        this.track(node as unknown as Mesh);
        void this.mountDecorGlb(node, "palm", 9 + 3.5 * hash01(Math.round(tz), Math.round(tx)), yaw);
      } else {
        batch.add("tree", tint, place(w, hgt, w, yaw, tx, hgt / 2, tz), chunk, `${import.meta.env.BASE_URL}textures/${pick[0]}`);
      }
      // Newly planted street trees in OCP stand in a square soil pit cut
      // into the paving, held up by a wooden tripod (ADR-061). Both are
      // in every reference clip and neither was here: our trees grew
      // straight out of the grass like a park.
      batch.add("box", TREE_PIT, place(1.15, 0.03, 1.15, yaw, tx, 0.02, tz), chunk);
      const trunkH = Math.min(2.1, hgt * 0.42);
      for (let k = 0; k < 3; k++) {
        const a2 = yaw + (k * 2 * Math.PI) / 3;
        const lean = 0.52;
        batch.add(
          "cyl",
          STAKE,
          place(0.07, trunkH, 0.07, 0, tx + Math.sin(a2) * lean * 0.5, trunkH / 2, tz + Math.cos(a2) * lean * 0.5),
          chunk,
        );
      }
      // The tie ring that holds the trunk to the three stakes.
      batch.add("box", STAKE, place(1.02, 0.06, 0.06, yaw, tx, trunkH * 0.82, tz), chunk);
      batch.add("box", STAKE, place(0.06, 0.06, 1.02, yaw, tx, trunkH * 0.82, tz), chunk);
      if (!this.sunCasts) batch.add("shadow", [1, 1, 1], place(w * 0.8, 1, w * 0.7, 0, tx, 0.055, tz), chunk);
    }
    for (const d of mission.decor) {
      if (d.kind === "tree") this.buildDecor(d, net, batch);
    }
    for (const piece of net.pieces) {
      if (piece.kind !== "ring") continue;
      if (mission.decor.some((d) => d.kind === "tree" && d.x === piece.cx && d.z === piece.cz)) continue;
      const treeH = 5.6;
      batch.add(
        "tree",
        [1, 1, 1],
        place(treeH * 0.72, treeH, treeH * 0.72, 0.6, piece.cx, 0.4 + treeH / 2, piece.cz),
        "",
        `${import.meta.env.BASE_URL}textures/tree.png`,
      );
    }
  }

  /** Suburban ground cover and furniture; solid trees are shared by all environments. */
  private buildVerges(net: RoadNetwork, batch: StaticBatch): void {
    const verge = new GeomBuilder();
    const trim = new GeomBuilder();
    for (const piece of net.pieces) {
      if (piece.kind !== "band" || piece.roadType !== "street") continue;
      const hw = piece.halfWidth;
      for (let i = 0; i + 1 < piece.pts.length; i++) {
        const a = piece.pts[i]!;
        const c = piece.pts[i + 1]!;
        const len = Math.hypot(c.x - a.x, c.z - a.z);
        if (len < 1e-6) continue;
        const ux = (c.x - a.x) / len;
        const uz = (c.z - a.z) / len;
        const nx = uz;
        const nz = -ux;
        const step = 3;
        const nSeg = Math.max(1, Math.ceil(len / step));
        for (const side of [-1, 1]) {
          for (let k = 0; k < nSeg; k++) {
            const d0 = (len * k) / nSeg;
            const d1 = (len * (k + 1)) / nSeg;
            const mid = (d0 + d1) / 2;
            const px = a.x + ux * mid + nx * side * (hw + CURB_PROBE);
            const pz = a.z + uz * mid + nz * side * (hw + CURB_PROBE);
            if (net.sdf(px, pz) < 0) continue; // junction mouth: keep it open
            const o0 = hw + CURB_SIZE;
            const o1 = o0 + VERGE_W;
            const quad = [
              { x: a.x + ux * d0 + nx * side * o0, z: a.z + uz * d0 + nz * side * o0 },
              { x: a.x + ux * d1 + nx * side * o0, z: a.z + uz * d1 + nz * side * o0 },
              { x: a.x + ux * d1 + nx * side * o1, z: a.z + uz * d1 + nz * side * o1 },
              { x: a.x + ux * d0 + nx * side * o1, z: a.z + uz * d0 + nz * side * o1 },
            ];
            // Wound to match the road ribbons' own order (far edge first);
            // the first attempt guessed the other way and both strips faced
            // straight down — grass that existed and could never be seen.
            verge.addFlat(side === 1 ? quad.reverse() : quad, 0.008);
            // Terracotta border course along the kerb edge of the footway —
            // in every reference clip the paving is bounded by a coloured
            // band, and its absence was most of why our footway read as a
            // flat grey apron (ADR-059).
            const t0 = o1;
            const t1 = o1 + PAVE_TRIM_W;
            const trimQuad = [
              { x: a.x + ux * d0 + nx * side * t0, z: a.z + uz * d0 + nz * side * t0 },
              { x: a.x + ux * d1 + nx * side * t0, z: a.z + uz * d1 + nz * side * t0 },
              { x: a.x + ux * d1 + nx * side * t1, z: a.z + uz * d1 + nz * side * t1 },
              { x: a.x + ux * d0 + nx * side * t1, z: a.z + uz * d0 + nz * side * t1 },
            ];
            trim.addFlat(side === 1 ? trimQuad.reverse() : trimQuad, 0.011);
          }
        }
        // Street lamps, both sides, phased between the trees — the boulevard
        // furniture of the OCP reference. A street with trees and no lamps is
        // a park path pretending to be a road.
        for (let d = LAMP_SPACING * 0.35; d < len; d += LAMP_SPACING) {
          for (const side of [-1, 1]) {
            const o = hw + CURB_SIZE + 0.35;
            const lx = a.x + ux * d + nx * side * o;
            const lz = a.z + uz * d + nz * side * o;
            if (net.sdf(lx, lz) < 0.3) continue;
            const chunk = `${Math.floor(lx / CITY_CHUNK)},${Math.floor(lz / CITY_CHUNK)}`;
            const yaw = Math.atan2(ux, uz) + (side > 0 ? -Math.PI / 2 : Math.PI / 2);
            batch.add("cyl", LAMP_TINT, place(0.11, 5.4, 0.11, 0, lx, 2.7, lz), chunk);
            // A gully cover in the gutter beside every other lamp: the
            // reference kerbs are punctuated by them, and a kerb line with
            // nothing in it reads as extruded rather than built.
            if (hash01(Math.round(lx * 3), Math.round(lz * 5)) < 0.5) {
              const dx2 = lx - nx * side * (0.35 + GUTTER_W / 2);
              const dz2 = lz - nz * side * (0.35 + GUTTER_W / 2);
              if (net.sdf(dx2, dz2) < -0.1) {
                batch.add("box", DRAIN, place(0.42, 0.02, 0.62, yaw, dx2, 0.03, dz2), chunk);
              }
            }
            // Arm reaching over the carriageway, head at its tip. After dark
            // the head is a glow proto and throws a warm pool on the road
            // under it — the sodium-lit look of the OCP avenues at night.
            const ax2 = lx - nx * side * 0.55;
            const az2 = lz - nz * side * 0.55;
            batch.add("box", LAMP_TINT, place(0.07, 0.07, 1.15, yaw, ax2, 5.32, az2), chunk);
            const hx = lx - nx * side * 1.0;
            const hz = lz - nz * side * 1.0;
            if (this.tod === "day") {
              batch.add("box", [0.92, 0.9, 0.78], place(0.16, 0.1, 0.5, yaw, hx, 5.24, hz), chunk);
            } else {
              batch.add("glow", [1, 0.93, 0.62], place(0.18, 0.12, 0.54, yaw, hx, 5.24, hz), chunk);
              const pm = place(7.5, 1, 7.5, 0, hx, 0.065, hz);
              for (let q = 0; q < 16; q++) this.lampPools.push(pm.m[q]!);
            }
          }
        }
        // A clipped hedge along the property line beyond the footway: the
        // boundary that stops buildings floating on an endless pavement.
        for (let d = 1.2; d < len - 1.2; d += HEDGE_SEG) {
          for (const side of [-1, 1]) {
            const o = hw + CURB_SIZE + VERGE_W + PAVE_W;
            const hx = a.x + ux * d + nx * side * o;
            const hz = a.z + uz * d + nz * side * o;
            if (net.sdf(hx, hz) < 1.2) continue;
            // Gates: every few segments the hedge breaks for a plot entrance.
            // An unbroken hedge is not landscaping, it is a green wall.
            if (hash01(Math.round(hx * 3 + 7), Math.round(hz * 5 + 1)) < 0.24) continue;
            const chunk = `${Math.floor(hx / CITY_CHUNK)},${Math.floor(hz / CITY_CHUNK)}`;
            const yaw = Math.atan2(ux, uz);
            const jitterH = 0.42 + 0.1 * hash01(Math.round(hx * 5), Math.round(hz * 3));
            batch.add("box", HEDGE_TINT, place(0.5, jitterH, HEDGE_SEG * 0.92, yaw, hx, jitterH / 2, hz), chunk);
          }
        }
      }
    }
    if (!trim.empty) {
      const m = this.track(trim.build("paveTrim", this.scene));
      m.material = mat(this.scene, "paveTrimMat", PAVE_TRIM);
    }
    if (!verge.empty) {
      const m = this.track(verge.build("verge", this.scene));
      m.material = texMat(this.scene, "vergeMat", `${import.meta.env.BASE_URL}textures/grass.jpg`, 2.6, [0.95, 0.97, 0.92]);
    }
  }

  /** Static props: parked cars/motorbikes, construction barriers. */
  private buildProps(props: readonly PropDef[], mirror: 1 | -1, batch: StaticBatch): void {
    for (const [i, ob] of props.entries()) {
      const alongX = ob.halfW >= ob.halfD;
      const yaw = alongX ? Math.PI / 2 : 0;
      switch (ob.kind) {
        case "parked_car": {
          // The real car (VF8 — the only complete car export). No placeholder: a
          // parked row pops in ~half a second after the level (the container
          // is cached after the first), which beats batching box-cars that
          // could never be hidden once thin-instanced.
          const node = new TransformNode(`parked_${i}`, this.scene);
          node.position.set(ob.x, 0, ob.z);
          node.rotation.y = yaw;
          this.track(node as unknown as Mesh);
          const len = 2 * Math.max(ob.halfW, ob.halfD);
          // Two cars, not one: a kerb-side row of identical VF8s was the
          // giveaway that the street was generated. The SUV is CC-BY (see
          // docs/CREDITS.md).
          const car = hash01(i * 7 + 3, Math.round(ob.z)) < 0.62 ? "car_vf8" : "truck";
          void this.mountVehicleGlb(node, car, len);
          break;
        }
        case "parked_motorbike": {
          if (this.isAdventureBenchmark) {
            const rig = buildAdventureParkedScooter(this.scene, {
              width: 2 * (alongX ? ob.halfD : ob.halfW), length: 2 * (alongX ? ob.halfW : ob.halfD),
              loadState: themeState(this.scene),
              onReady: meshes => {
                for (const mesh of meshes) this.shadows?.addShadowCaster(mesh, false);
                this.fisheye?.setRenderList(this.scene.meshes);
              },
            });
            const host = new TransformNode(`parkedbike_${i}`, this.scene);
            host.position.set(ob.x, 0, ob.z); host.rotation.y = yaw;
            rig.root.parent = host;
            this.track(host as unknown as Mesh);
            this.adventureActors.push(rig);
            break;
          }
          // The four bikes Vietnamese streets actually park: Wave, AB,
          // Activa, Exciter — converted straight out of the SVL NPC bundles
          // (ADR-050). Chosen by index so a row is a mixed row.
          const node = new TransformNode(`parkedbike_${i}`, this.scene);
          node.position.set(ob.x, 0, ob.z);
          node.rotation.y = yaw + 0.25;
          this.track(node as unknown as Mesh);
          const bikes = ["bike_hondawave", "bike_hondaab", "bike_hondaactiva", "bike_exciter150"];
          void this.mountVehicleGlb(node, this.appearance === "neighborhood" ? "scooter" : bikes[i % bikes.length]!, 2 * Math.max(ob.halfW, ob.halfD));
          break;
        }
        case "barrier": {
          // A cheerful "road closed": a low planter wall with bushes, a couple
          // of cones and a small sign — not two red stripes.
          const alongX = ob.halfW >= ob.halfD;
          const half = Math.max(ob.halfW, ob.halfD);
          const ux = alongX ? 1 : 0;
          const uz = alongX ? 0 : 1;
          const w = alongX ? half * 2 : 0.5;
          const d = alongX ? 0.5 : half * 2;
          batch.add("box", [0.72, 0.62, 0.5], place(w, 0.42, d, 0, ob.x, 0.21, ob.z));
          const n = Math.max(2, Math.round(half));
          for (let k = 0; k < n; k++) {
            const u = n === 1 ? 0 : -half + 0.5 + ((2 * half - 1) * k) / (n - 1);
            const px = ob.x + ux * u;
            const pz = ob.z + uz * u;
            const leaf: Rgb = k % 2 === 0 ? [0.35, 0.6, 0.32] : [0.45, 0.66, 0.3];
            batch.add("sphere", leaf, place(0.62, 0.5, 0.62, 0, px, 0.62, pz));
            batch.add("box", [0.78, 0.42, 0.3], place(0.42, 0.26, 0.42, 0, px, 0.5, pz));
          }
          // Cones at the ends.
          for (const sgn of [-1, 1]) {
            const px = ob.x + ux * sgn * (half - 0.2);
            const pz = ob.z + uz * sgn * (half - 0.2);
            batch.add("cyl", [0.92, 0.45, 0.15], place(0.3, 0.5, 0.3, 0, px, 0.25, pz));
            batch.add("cyl", [0.96, 0.96, 0.96], place(0.33, 0.1, 0.33, 0, px, 0.3, pz));
          }
          // Little sign board on a post, facing the road.
          batch.add("cyl", [0.55, 0.55, 0.58], place(0.07, 1.1, 0.07, 0, ob.x, 0.55, ob.z));
          batch.add(
            "box",
            [0.95, 0.78, 0.2],
            place(alongX ? 0.9 : 0.08, 0.42, alongX ? 0.08 : 0.9, 0, ob.x, 1.15, ob.z),
          );
          break;
        }
        case "open_car_door": {
          // A front door caught mid-swing, drawn as the DIAGONAL of its own
          // collision box: hinge at the +x/+z corner (touching the parked
          // car's flank, forward of its centre — the A-pillar), free edge at
          // the -x/-z corner (out in the lane, swung back toward the tail).
          // The first version stood a red slab NEXT TO the car at its rear,
          // which no door has ever done, and the generated footage
          // faithfully reproduced the nonsense. Convention: the car is
          // parked on the +x side; place the prop box accordingly.
          const hingeX = ob.x + ob.halfW;
          const hingeZ = ob.z + ob.halfD;
          const tipX = ob.x - ob.halfW;
          const tipZ = ob.z - ob.halfD;
          const dLen = Math.hypot(hingeX - tipX, hingeZ - tipZ);
          const dYaw = Math.atan2(hingeX - tipX, hingeZ - tipZ);
          this.addSpecStatic(
            batch,
            [
              // painted panel (sill to waistline), VF8 red like its car
              { shape: "box", rgb: [0.68, 0.1, 0.12], size: [0.05, 0.62, dLen], pos: [0, 0.66, 0] },
              // window glass above the waistline
              { shape: "box", rgb: [0.16, 0.2, 0.26], size: [0.04, 0.4, dLen * 0.86], pos: [0, 1.17, 0] },
              // window frame rim
              { shape: "box", rgb: [0.68, 0.1, 0.12], size: [0.05, 0.06, dLen * 0.9], pos: [0, 1.4, 0] },
            ],
            dYaw,
            (hingeX + tipX) / 2,
            (hingeZ + tipZ) / 2,
          );
          break;
        }
        case "broken_down_truck": {
          const alongX = ob.halfW >= ob.halfD;
          const yaw = alongX ? Math.PI / 2 : 0;
          const len = Math.max(ob.halfW, ob.halfD) * 2;
          const wide = Math.min(ob.halfW, ob.halfD) * 2;
          const specs = truckSpec([.72,.44,.24]).map(part => ({ ...part,
            size: [part.size[0] * wide / 2.45, part.size[1], part.size[2] * len / 6.6] as [number, number, number],
            pos: [part.pos[0] * wide / 2.45, part.pos[1], part.pos[2] * len / 6.6] as [number, number, number],
          }));
          this.addSpecStatic(batch, specs, yaw, ob.x, ob.z);
          break;
        }
        case "speed_bump": {
          // The raised Vietnamese kind (g\u1eddi gi\u1ea3m t\u1ed1c): alternating yellow and
          // dark segments, drawn as separate full-height blocks rather than
          // stripes painted on a base — same height means z-fighting, and a
          // shimmering bump is exactly the artefact the guides fought.
          const alongX = ob.halfW >= ob.halfD;
          const length = 2 * Math.max(ob.halfW, ob.halfD);
          const depth = 2 * Math.min(ob.halfW, ob.halfD);
          const h = Math.max(0.06, ob.height);
          const segs = Math.max(4, 2 * Math.round(length / 0.6));
          const segLen = length / segs;
          for (let k = 0; k < segs; k++) {
            const off = -length / 2 + segLen * (k + 0.5);
            const tint: [number, number, number] =
              k % 2 === 0 ? [0.83, 0.68, 0.12] : [0.16, 0.16, 0.18];
            batch.add(
              "box",
              tint,
              place(
                alongX ? segLen : depth,
                h,
                alongX ? depth : segLen,
                0,
                ob.x + (alongX ? off : 0),
                h / 2,
                ob.z + (alongX ? 0 : off),
              ),
            );
          }
          break;
        }
        case "pothole":
        case "hole": {
          // Sunken, not stacked: a hole drawn as a box on the road reads as a
          // kerb to drive around for the wrong reason. A dark dish just below
          // the surface with a wet rim is what a driver actually sees.
          const w = ob.halfW * 2;
          const d = ob.halfD * 2;
          batch.add("box", [0.1, 0.11, 0.13], place(w, 0.06, d, 0, ob.x, 0.02, ob.z));
          batch.add("box", [0.24, 0.26, 0.3], place(w + 0.22, 0.03, d + 0.22, 0, ob.x, 0.012, ob.z));
          break;
        }
        default:
          batch.add(
            "box",
            i % 2 === 0 ? [0.78, 0.5, 0.3] : [0.42, 0.42, 0.46],
            place(ob.halfW * 2, ob.height, ob.halfD * 2, 0, ob.x, ob.height / 2, ob.z),
          );
          break;
      }
    }
  }

  private addSpecStatic(batch: StaticBatch, specs: readonly PartSpec[], yaw: number, x: number, z: number): void {
    const frame = frameAt(yaw, x, z);
    for (const p of specs) {
      batch.add(
        p.shape,
        p.rgb,
        place(p.size[0], p.size[1], p.size[2], 0, p.pos[0], p.pos[1], p.pos[2], p.roll ?? 0).multiply(frame),
      );
    }
  }

  /** Neighborhood modules; trees have matching sim trunks. Local +z faces the road. */
  private buildDecor(d: DecorDef, net: RoadNetwork, batch: StaticBatch): void {
    // A tree on the roundabout island stands on top of it.
    let y0 = 0;
    for (const p of net.pieces) {
      if (p.kind === "ring" && Math.hypot(d.x - p.cx, d.z - p.cz) < p.rIn) y0 = 0.4;
    }
    const frame = frameAt(d.yaw, d.x, d.z);
    const add = (shape: ProtoShape, rgb: Rgb, sx: number, sy: number, sz: number, x: number, y: number, z: number): void => {
      batch.add(shape, rgb, place(sx, sy, sz, 0, x, y + y0, z).multiply(frame));
    };
    switch (d.kind) {
      case "food_shop":
        add("box", [0.62, 0.58, 0.52], 2.6, 1.7, 2.2, 0, 0.85, 0);
        add("box", [0.7, 0.42, 0.36], 2.8, 0.06, 1.2, 0, 1.45, 1.4);
        add("box", [0.5, 0.45, 0.4], 1.2, 0.5, 0.4, 0.4, 0.25, 1.3);
        break;
      case "apartment":
        add("box", [0.55, 0.57, 0.62], 3.2, 3.4, 2.4, 0, 1.7, 0);
        add("box", [0.35, 0.38, 0.45], 2.6, 0.5, 0.06, 0, 2.4, 1.21);
        add("box", [0.35, 0.38, 0.45], 2.6, 0.5, 0.06, 0, 1.3, 1.21);
        break;
      case "stall":
        add("box", [0.66, 0.55, 0.42], 1.2, 0.9, 1.1, 0, 0.45, 0);
        add("box", [0.5, 0.55, 0.5], 1.4, 0.05, 1.3, 0, 1.35, 0);
        for (const sx of [-0.6, 0.6]) add("box", [0.45, 0.42, 0.4], 0.05, 1.35, 0.05, sx, 0.68, 0.6);
        break;
      case "gate":
        for (const gx of [-0.7, 0.7]) add("box", [0.5, 0.48, 0.5], 0.18, 1.5, 0.18, gx, 0.75, 0);
        add("box", [0.5, 0.48, 0.5], 1.6, 0.16, 0.22, 0, 1.55, 0);
        break;
      case "tree": {
        // Same photographic billboard as the street trees: a map-placed tree
        // next to an avenue of real ones must not be a green lollipop.
        const hgt = 3.4 + 2 * hash01(Math.round(d.x * 7), Math.round(d.z * 7));
        const w = hgt * 0.72;
        const chunk = `${Math.floor(d.x / CITY_CHUNK)},${Math.floor(d.z / CITY_CHUNK)}`;
        batch.add(
          "tree",
          [1, 1, 1],
          place(w, hgt, w, d.yaw, d.x, y0 + hgt / 2, d.z),
          chunk,
          `${import.meta.env.BASE_URL}textures/tree.png`,
        );
        if (!this.sunCasts) batch.add("shadow", [1, 1, 1], place(w * 0.95, 1, w * 0.85, 0, d.x, 0.055, d.z), chunk);
        break;
      }
      case "speed_bump":
        // Legacy visual-only hump (pre-ADR-045 maps). New maps use the PROP
        // speed_bump, which has the crossing jolt.
        add("box", [0.35, 0.35, 0.38], 4.0, 0.09, 0.55, 0, 0.045, 0);
        for (const sx of [-1.5, -0.5, 0.5, 1.5]) {
          add("box", [0.92, 0.88, 0.5], 0.55, 0.1, 0.58, sx, 0.05, 0);
        }
        break;
      case "speed_bump_painted":
        // V\u1ea1ch s\u01a1n gi\u1ea3m t\u1ed1c: the painted kind — a cluster of flat white
        // transverse strips. y = 0.05 for the same reason as the FPV guides:
        // anything closer to the road plane z-fights with it and vanishes.
        for (const sz of [-0.9, -0.45, 0, 0.45, 0.9]) {
          add("box", [0.92, 0.92, 0.9], 3.6, 0.012, 0.3, 0, 0.05, sz);
        }
        break;
      case "planter":
        add("box", [0.62, 0.45, 0.38], 0.8, 0.4, 0.5, 0, 0.2, 0);
        add("box", [0.42, 0.55, 0.4], 0.7, 0.3, 0.4, 0, 0.52, 0);
        break;
      case "sign":
        add("cyl", [0.5, 0.5, 0.52], 0.07, 1.6, 0.07, 0, 0.8, 0);
        add("box", [0.72, 0.68, 0.55], 0.55, 0.4, 0.05, 0, 1.35, 0);
        break;
      case "customer":
        add("box", [0.6, 0.55, 0.48], 1.4, 1.8, 0.3, 0, 0.9, 0);
        add("box", [0.42, 0.55, 0.45], 1.8, 0.15, 0.5, 0, 1.9, 0.1);
        break;
      default:
        break;
    }
  }

  /** Turn guidance: painted chevrons before each turn + a floating arrow at it. */
  private buildGuidance(path: DeliveryPath, net?: RoadNetwork): void {
    const scene = this.scene;
    const accent = mat(scene, "guideMat", ACCENT);
    accent.emissiveColor = new Color3(0.42, 0.27, 0.05);
    accent.backFaceCulling = false;

    const chev = new GeomBuilder();
    chev.addPrism(CHEVRON_LOOP, CHEVRON_TRIS, 0.04, 0.06);
    const chevMesh = chev.build("chevrons", scene);
    chevMesh.material = accent;
    const data: number[] = [];
    for (const turn of path.turns) {
      if (turn.kind === "arrive") continue;
      for (const off of CHEVRON_OFFSETS) {
        const s = turn.s + off;
        if (s < CHEVRON_MIN_S) continue;
        const p = path.point(s);
        const m = Matrix.RotationY(p.theta).multiply(Matrix.Translation(p.x, 0, p.z));
        for (let i = 0; i < 16; i++) data.push(m.m[i]!);
      }
    }
    if (data.length > 0) {
      chevMesh.thinInstanceSetBuffer("matrix", new Float32Array(data), 16, true);
      this.track(chevMesh);
    } else chevMesh.dispose();

    let arrowProto: Mesh | null = null;
    for (const [i, turn] of path.turns.entries()) {
      if (turn.kind === "arrive") continue;
      const p = path.point(turn.s);
      const ahead = path.point(Math.min(path.length, turn.s + ARROW_PAST_M));
      const root = new TransformNode(`turn${i}`, scene);

      if (turn.kind === "roundabout") {
        // A ring you go AROUND, plus a spur showing the exit you take. It
        // floats OVER the roundabout itself (not beside the approach, where a
        // player reads it as belonging to the road they are on) and is scaled
        // to read from the approach without filling the screen.
        const ringPiece = net?.pieces.find((q) => q.kind === "ring");
        const cx = ringPiece && ringPiece.kind === "ring" ? ringPiece.cx : ahead.x;
        const cz = ringPiece && ringPiece.kind === "ring" ? ringPiece.cz : ahead.z;
        root.position.set(cx, ROUNDABOUT_MARK_Y, cz);
        root.rotation.y = p.theta;
        root.scaling.setAll(1.7);
        const g = new GeomBuilder();
        const arc = circulationArrow(0.85, 210, -60, -1);
        g.addPrism(arc.loop, arc.tris, -0.07, 0.07);
        const ring = g.build(`turnRing${i}`, scene);
        ring.material = accent;
        ring.rotation.x = Math.PI / 2; // lie the ring up to face the player
        ring.parent = root;

        // Exit spur: where the path leaves the ring, relative to the approach.
        const exitTheta = this.ringExitTheta(path, turn.s);
        const spurRoot = new TransformNode(`turnExit${i}`, scene);
        spurRoot.parent = root;
        spurRoot.rotation.y = wrapAngle(exitTheta - p.theta);
        const sg = new GeomBuilder();
        sg.addPrism(ARROW_LOOP, ARROW_TRIS, -0.07, 0.07);
        const spur = sg.build(`turnExitArrow${i}`, scene);
        spur.material = accent;
        spur.scaling.setAll(0.8);
        spur.position.z = 1.45;
        spur.parent = spurRoot;

        // Exit number as pips beside the ring.
        for (let k = 0; k < (turn.exit ?? 0); k++) {
          const pip = CreateSphere(`turnPip${i}_${k}`, { diameter: 0.17, segments: 6 }, scene);
          pip.material = accent;
          pip.position.set(-1.15, 0.42 - k * 0.32, 0);
          pip.parent = root;
        }
        this.turnArrows.push(root);
        continue;
      }

      root.position.set(ahead.x, ARROW_HEIGHT, ahead.z);
      root.rotation.y = turn.kind === "left" ? p.theta - Math.PI / 2 : p.theta + Math.PI / 2;
      let arrow: TransformNode;
      if (!arrowProto) {
        const g = new GeomBuilder();
        g.addPrism(ARROW_LOOP, ARROW_TRIS, -0.09, 0.09);
        arrowProto = g.build("turnArrow", scene);
        arrowProto.material = accent;
        arrow = arrowProto;
      } else {
        arrow = arrowProto.createInstance(`turnArrow${i}`);
      }
      arrow.parent = root;
      this.turnArrows.push(root);
    }
  }

  /** Travel direction where the delivery path leaves the roundabout. */
  private ringExitTheta(path: DeliveryPath, fromS: number): number {
    let onRing = false;
    for (let s = fromS; s < Math.min(path.length, fromS + 140); s += 1) {
      const pt = path.point(s);
      if (pt.piece === "R") onRing = true;
      else if (onRing) return pt.theta;
    }
    return path.point(Math.min(path.length, fromS + 40)).theta;
  }

  private buildDestination(sim: Simulation): void {
    const scene = this.scene;
    const dest = sim.level.world.destination;
    this.destMarker = this.track(
      CreateCylinder("dest", { diameter: 1.4, height: 0.05, tessellation: 40 }, scene),
    );
    this.destMarker.position.set(dest.x, 0.05, dest.z);
    const destMat = mat(scene, "destMat", DEST);
    destMat.emissiveColor = new Color3(0.1, 0.35, 0.2);
    this.destMarker.material = destMat;

    if (sim.level.mission) {
      // Tall beacon: the customer is visible from the far end of the district.
      const beacon = this.track(
        CreateCylinder("destBeacon", { diameter: 0.25, height: 9, tessellation: 12 }, scene),
      );
      beacon.position.set(dest.x, 4.5, dest.z);
      const beaconMat = mat(scene, "beaconMat", DEST);
      beaconMat.emissiveColor = new Color3(0.15, 0.45, 0.25);
      beaconMat.alpha = 0.45;
      beacon.material = beaconMat;
    } else {
      // Lab levels keep their short post (the research stimulus is unchanged).
      const post = this.track(
        CreateCylinder("destBeacon", { diameter: 0.12, height: 1.6, tessellation: 12 }, scene),
      );
      post.position.set(dest.x, 0.8, dest.z);
      post.material = destMat;
    }
  }

  // ------------------------------------------------------------------
  // Robot rig: body + delivery box held by two hands + corgi tail.

  /**
   * Put the onboard camera where the hardware drawing says it is, parented to
   * the robot so the engine composes the pose — including through recovery.
   */
  private mountOnboardCamera(): void {
    if (!this.onboardCam || !isCameraId(this.cameraView)) return;
    const pose = cameraPoseById(this.cameraView);
    this.onboardCam.parent = this.robotNode;
    this.onboardCam.position.set(pose.local.x, pose.local.y, pose.local.z);
    this.onboardCam.rotation.set(pose.rotX, pose.rotY, pose.rotZ);
    this.fisheye?.setRenderList(this.scene.meshes);

    // ...and put the right one back in charge, since disposing the old robot
    // may have taken the active camera with it.
    if (this.onboardCam && isCameraId(this.cameraView)) {
      this.scene.activeCameras = [this.onboardCam];
      this.scene.activeCamera = this.onboardCam;
    } else {
      this.scene.activeCamera = this.playView === "first" ? this.fpvCam : this.camera;
    }
  }

  /** Fit authored extents, including handles and tail, to the simulation's
   * footprint. Normalize before attaching to a positioned host. */
  private fitAdventureFootprint(actor: AdventureActor, width: number, length: number): void {
    const low = new Vector3(Infinity, Infinity, Infinity), high = new Vector3(-Infinity, -Infinity, -Infinity);
    for (const mesh of actor.meshes) {
      mesh.computeWorldMatrix(true);
      const bounds = mesh.getBoundingInfo().boundingBox;
      low.minimizeInPlace(bounds.minimumWorld); high.maximizeInPlace(bounds.maximumWorld);
    }
    const sx = width / Math.max(.001, high.x - low.x), sz = length / Math.max(.001, high.z - low.z);
    actor.root.scaling.x = sx; actor.root.scaling.z = sz;
    actor.root.position.x = -(high.x + low.x) * .5 * sx;
    actor.root.position.z = -(high.z + low.z) * .5 * sz;
  }

  private buildRobot(sim: Simulation): void {
    const scene = this.scene;
    this.robotNode = new TransformNode("robot", scene);
    this.mountOnboardCamera();
    // Riding the robot node means the view inherits the interpolated pose the
    // body is drawn at; following sim.robot separately is what made the robot
    // shiver against the chase camera (ADR-035).
    this.fpvCam.parent = this.robotNode;
    this.wobbleNode = new TransformNode("robotWobble", scene);
    this.wobbleNode.parent = this.robotNode;
    this.buildFpvGuide(sim.robotProfile.width_m);
    if (this.tod !== "day") {
      // The robot's own headlight: a warm pool thrown ahead on the tarmac.
      // Parented to the robot node so it steers with the body; the maps are
      // flat, so inheriting pitch is not visible.
      const beam = CreateGround("headlightPool", { width: 2.3, height: 3.6 }, scene);
      const bm = transientMaterial(scene, "headlightPoolMat");
      const bt = this.appearance === "neighborhood" && bm.diffuseTexture instanceof Texture ? bm.diffuseTexture : themedTexture(scene, texturePath(scene, "textures/blobshadow.png")!);
      bt.hasAlpha = true;
      bm.diffuseTexture = bt;
      bm.diffuseColor = Color3.Black();
      bm.emissiveColor = new Color3(0.55, 0.52, 0.4);
      bm.specularColor = Color3.Black();
      bm.disableLighting = true;
      bm.useAlphaFromDiffuseTexture = true;
      bm.transparencyMode = 2;
      beam.material = bm;
      beam.isPickable = false;
      beam.parent = this.robotNode;
      beam.position.set(0, 0.07, 2.2);
    }

    const w = sim.robotProfile.width_m;
    const l = sim.robotProfile.length_m;
    if (this.isAdventureBenchmark) {
      const rig = buildAdventureRobot(scene);
      this.fitAdventureFootprint(rig, w, l);
      rig.root.parent = this.wobbleNode;
      this.adventureRobot = rig; this.adventureActors.push(rig);
      this.placeholderParts = [];
      this.addBlobShadow(this.robotNode, w * 2.6, l * 2, .04);
      return;
    }
    if (this.appearance === "neighborhood") {
      const label = texMat(scene, "adventureFoodLabel", "textures/delivery_label.png", 1, [1, 1, 1]);
      const rig = buildNeighborhoodRobot(scene, this.wobbleNode, w, l, (name, color) => mat(scene, name, color), label);
      this.packageBox = rig.carrier; this.armL = rig.armL; this.armR = rig.armR; this.tailNode = rig.tail;
      this.placeholderParts = rig.shell;
      this.addBlobShadow(this.robotNode, w * 2.6, l * 2, .06);
      void this.mountBulldog(sim);
      return;
    }

    const body = CreateBox("robotBody", { width: w, height: 0.46, depth: l }, scene);
    body.position.y = 0.23;
    body.material = mat(scene, "robotMat", [0.2, 0.3, 0.55]);
    body.parent = this.wobbleNode;

    // Front visor: reads the facing direction at a glance.
    const visor = CreateBox("visor", { width: w * 0.7, height: 0.09, depth: 0.03 }, scene);
    visor.position.set(0, 0.33, l / 2 - 0.005);
    visor.material = mat(scene, "visorMat", [0.12, 0.16, 0.22]);
    visor.parent = this.wobbleNode;

    // Delivery box on top.
    this.packageBox = CreateBox("package", { width: 0.36, height: 0.3, depth: 0.44 }, scene);
    this.packageBox.position.set(0, 0.61, 0.02);
    this.packageBox.material = mat(scene, "packageMat", [0.72, 0.58, 0.4]);
    this.packageBox.parent = this.wobbleNode;

    // Two hands/arms gripping the box from the sides.
    const armMat = mat(scene, "armMat", [0.16, 0.24, 0.44]);
    this.armL = CreateBox("armL", { width: 0.07, height: 0.34, depth: 0.12 }, scene);
    this.armL.position.set(-0.22, 0.56, 0.02);
    this.armL.material = armMat;
    this.armL.parent = this.wobbleNode;
    this.armR = CreateBox("armR", { width: 0.07, height: 0.34, depth: 0.12 }, scene);
    this.armR.position.set(0.22, 0.56, 0.02);
    this.armR.material = armMat;
    this.armR.parent = this.wobbleNode;
    const palmL = CreateBox("palmL", { width: 0.09, height: 0.06, depth: 0.2 }, scene);
    palmL.position.set(-0.17, 0.74, 0.02);
    palmL.material = armMat;
    palmL.parent = this.wobbleNode;
    const palmR = CreateBox("palmR", { width: 0.09, height: 0.06, depth: 0.2 }, scene);
    palmR.position.set(0.17, 0.74, 0.02);
    palmR.material = armMat;
    palmR.parent = this.wobbleNode;

    // Corgi tail: a small colored nub on a pivot at the rear.
    this.tailNode = new TransformNode("tailPivot", scene);
    this.tailNode.position.set(0, 0.4, -l / 2 + 0.02);
    this.tailNode.parent = this.wobbleNode;
    const tail = CreateBox("tail", { width: 0.1, height: 0.12, depth: 0.16 }, scene);
    tail.position.set(0, 0.04, -0.08);
    tail.material = mat(scene, "tailMat", [0.85, 0.62, 0.3]);
    tail.parent = this.tailNode;

    // The blocky robot above is now a PLACEHOLDER: it appears on the first
    // frame while the real Bulldog model streams in, and stays as the
    // fallback if the model cannot load. Kept rather than deleted because a
    // robot that pops into existence a beat late is worse than one that
    // upgrades a beat late.
    this.placeholderParts = [body, visor, this.packageBox, this.armL, this.armR, palmL, palmR, tail];
    if (!appearanceTheme(this.appearance).policy.expressiveRobot) {
      for (const part of [this.armL, this.armR, palmL, palmR, tail]) part.setEnabled(false);
    }
    this.addBlobShadow(this.robotNode, w * 2.6, l * 2.0, 0.06);
    void this.mountBulldog(sim);
  }

  /**
   * Swap the placeholder for the real robot (ADR-046).
   *
   * The GLB is the Bulldog from the team's SVL simulator — the actual
   * second-generation delivery robot, 14.6k triangles. Async on purpose, and
   * re-entrant on purpose: buildLevel() rebuilds the robot on every map
   * change, so the container is cached and each rebuild just re-instantiates
   * it. The wobble node stays the parent, so recovery shakes move the real
   * body exactly as they moved the box.
   */
  /**
   * Load (cached) and attach a vehicle GLB under `host`, fitted to a given
   * length (ADR-049). One code path for the robot, the traffic and the parked
   * rows: measure at the origin (bounding boxes come back in world space —
   * the sideways-parked-ghost lesson of ADR-046), scale so the long axis is
   * `lengthM`, drop the wheels to the ground, face +Z, then parent.
   */
  /**
   * A soft contact shadow under `host` (ADR-052).
   *
   * The single biggest "this is not real" tell left in the frame was that
   * nothing touched the ground: robot, cars, bikes and trees all floated on a
   * uniformly lit surface. One alpha-faded quad per thing fixes the grounding
   * for the cost of a texture lookup — no shadow maps, nothing per-light.
   */
  /** True while the sun is casting; blob shadows stand in only when it is
   * not — at night, and on the lite tier, which carries no shadow map. */
  private get sunCasts(): boolean {
    return this.tod !== "night" && this.shadows !== null;
  }

  private addBlobShadow(host: TransformNode, w: number, d: number, y = 0.02): void {
    // Painted blobs were the whole shadow model before the sun existed
    // (ADR-060). Now they double up: a real cast shadow AND an ellipse that
    // ignores where the light is. Kept for night, when nothing casts.
    if (this.sunCasts) return;
    void y;
    // Deliberately NOT parented. A world-space quad following its owner each
    // frame is the exact construction that was proven on screen; the parented
    // version, identical in every probe-able property, never produced a pixel
    // — the one unexplained rendering result in this codebase (ADR-052). The
    // follower list costs one position copy per shadow per frame.
    this.shadowFollowers.push({ host, w, d, mesh: null });
  }

  /** Create lazily (material texture settled), then track the owner. */
  private updateShadowFollowers(): void {
    for (const f of this.shadowFollowers) {
      if (f.host.isDisposed()) {
        f.mesh?.dispose();
        f.mesh = null;
        continue;
      }
      if (!f.mesh) {
        f.mesh = CreateGround(`shadow_${f.host.name}`, { width: f.w, height: f.d }, this.scene);
        f.mesh.material = blobShadowMat(this.scene);
        f.mesh.isPickable = false;
      }
      // A dormant agent (trigger not reached) must not cast: the first sign
      // of this bug was a lone dark blob in the middle of an empty road.
      f.mesh.setEnabled(f.host.isEnabled());
      const p = f.host.getAbsolutePosition();
      f.mesh.position.set(p.x, 0.06, p.z);
      f.mesh.rotation.y = (f.host.rotationQuaternion ? 0 : f.host.rotation.y) || 0;
    }
  }

  private async mountVehicleGlb(
    host: TransformNode,
    model: string,
    lengthM: number,
    onDone?: (root: TransformNode) => void,
  ): Promise<void> {
    const placeholders: Mesh[] = [];
    if (host.getChildMeshes().length === 0) {
      const specs = model.startsWith("bike") || model === "scooter"
        ? bikeSpec(1, appearanceTheme(this.appearance).palette.vehicles!, null)
        : carSpec(appearanceTheme(this.appearance).palette.vehicles!, 0);
      this.buildSpecMeshes(host, specs, `${host.name}_fallback`, new Map(), new Map());
      placeholders.push(...host.getChildMeshes().filter((m): m is Mesh => m instanceof Mesh));
    }
    const path = themeAssetPath(this.appearance, "models", model);
    if (!appearanceTheme(this.appearance).policy.externalModels || !path) return;
    const url = `${import.meta.env.BASE_URL}${path}`;
    const generation = this.buildGeneration;
    const container = await this.themeModel(url);
    if (generation !== this.buildGeneration) return;
    if (!container || host.isDisposed()) return;
    // Real clones, never the container's own nodes. `doNotInstantiate: true`
    // MOVES the source hierarchy into the scene, so the second mount from a
    // cached container strip-mined the first: a car's worth of wheels and
    // lamps was left orphaned at the world origin, sitting in the middle of
    // whatever map happened to contain (0, 0).
    const inst = container.instantiateModelsToScene((n) => n, false);
    const root = inst.rootNodes[0] as TransformNode | undefined;
    if (!root) return;
    const meshes = root.getChildMeshes();
    let min = new Vector3(Infinity, Infinity, Infinity);
    let max = new Vector3(-Infinity, -Infinity, -Infinity);
    root.computeWorldMatrix(true);
    for (const m of meshes) {
      m.computeWorldMatrix(true);
      const bb = m.getBoundingInfo().boundingBox;
      min = Vector3.Minimize(min, bb.minimumWorld);
      max = Vector3.Maximize(max, bb.maximumWorld);
    }
    const size = max.subtract(min);
    const targetLength = this.appearance === "neighborhood" ? (model === "car_vf8" ? 3.65 : model.startsWith("bike") || model === "scooter" ? 1.9 : lengthM) : MODEL_LENGTH_M[model] ?? lengthM;
    const k = targetLength / Math.max(size.x, size.z, 1e-3);
    root.scaling.scaleInPlace(k);
    root.position.y -= min.y * k;
    root.position.x -= ((min.x + max.x) / 2) * k;
    root.position.z -= ((min.z + max.z) / 2) * k;
    if (this.appearance !== "neighborhood") root.rotation = new Vector3(0, Math.PI, 0);
    for (const m of meshes) {
      const pbr = m.material;
      if (!(pbr instanceof PBRMaterial)) continue;
      if (this.appearance === "neighborhood") {
        m.material = neighborhoodModelMaterial(this.scene, pbr, .14, host.name);
        continue;
      }
      // The exports ship PBR and we used to convert it DOWN to Standard,
      // because without an environment texture a metallic-roughness material
      // has nothing to reflect and renders as a black shell. There is a sky
      // to reflect now (ADR-061), so the material is kept and only bounded:
      // showroom-clean paint on a delivery street is its own kind of wrong.
      const rgb = vary(this.scene, [pbr.albedoColor.r, pbr.albedoColor.g, pbr.albedoColor.b], `${host.name}:${pbr.name}`,
        /skin|face/i.test(pbr.name) ? 'skin' : /lamp|brake|indicator|signal/i.test(pbr.name) ? 'functional' : '');
      let cache = MODEL_PBR_MATERIALS.get(this.scene);
      if (!cache) { cache = new Map(); MODEL_PBR_MATERIALS.set(this.scene, cache); }
      const key = `${pbr.uniqueId}:${this.variationSeed}:${rgb.join(',')}`;
      let finish = cache.get(key);
      if (!finish) {
        finish = pbr.clone(`variation_${key}`)!;
        finish.albedoColor = Color3.FromArray(rgb);
        finish.roughness = Math.max(pbr.roughness ?? .5, .42);
        finish.metallic = Math.min(pbr.metallic ?? .3, .7);
        finish.environmentIntensity = .7;
        cache.set(key, finish);
      }
      m.material = finish;
    }
    root.parent = host;
    for (const part of placeholders) part.dispose();
    this.addBlobShadow(host, lengthM * 0.95, lengthM * 1.35, 0.06);
    onDone?.(root);
    // The onboard cube map draws an EXPLICIT mesh list, refreshed when the
    // level builds — but this GLB arrived asynchronously AFTER that refresh,
    // so the sensor camera kept seeing the grey placeholder while every
    // player-facing camera showed the car (ADR-055): a capture pipeline
    // quietly full of grey boxes. Refresh again now the meshes exist.
    if (this.appearance === "neighborhood") for (const mesh of meshes) { mesh.receiveShadows = true; this.shadows?.addShadowCaster(mesh, false); }
    this.fisheye?.setRenderList(this.scene.meshes);
  }

  /**
   * The rigged pedestrian (ADR-056): a real skinned human with a walk clip,
   * in place of the box torso and crossed-quad legs that read as an X from
   * the robot's own camera. Converted out of the team's SVL pedestrian
   * bundle by scripts/unity_skinned_to_glb.py, so it belongs to the same
   * asset family as the VF8 and the Hondas.
   */
  private async mountPersonGlb(vis: AgentVisual, standing: boolean): Promise<void> {
    if (!appearanceTheme(this.appearance).policy.externalModels) return;
    // Four figures out of the SVL pedestrian bundles — man, woman, boy,
    // girl, each at its own real height. One model repeated down a street is
    // a crowd of clones; the id picks the same one every rebuild so a replay
    // shows the same person.
    // Weighted so a street is mostly adults: three groups of people standing
    // around turned out to be three children, which is not what a delivery
    // route looks like at any hour.
    const roll = hashStr(`who${vis.agent.state.id}`);
    const who = this.appearance === "neighborhood" ? (roll < .5 ? "man" : "woman") : roll < 0.42 ? "man" : roll < 0.8 ? "woman" : roll < 0.9 ? "boy" : "girl";
    const path = themeAssetPath(this.appearance, "models", `ped_${who}`);
    if (!path) return;
    const url = `${import.meta.env.BASE_URL}${path}`;
    const generation = this.buildGeneration;
    const container = await this.themeModel(url);
    if (generation !== this.buildGeneration) return;
    if (!container || vis.root.isDisposed()) return;
    const inst = container.instantiateModelsToScene((n) => n, false);
    const glbRoots = inst.rootNodes as TransformNode[];
    if (glbRoots.length === 0) return;
    const holder = new TransformNode(`ped_${vis.agent.state.id}`, this.scene);
    for (const r of glbRoots) r.parent = holder;
    // Each GLB already stands at its own real height; this is only the
    // spread within one kind of person, not a way to make children.
    const k = 0.96 + 0.08 * hashStr(`h${vis.agent.state.id}`);
    holder.scaling.setAll(k);
    // NO yaw flip here. The vehicle mount needs one because those exports
    // face -Z; the pedestrian bundles already face +Z, and copying the flip
    // across turned every walker around: they faced the camera while walking
    // away from it, which reads exactly as walking backwards.
    holder.rotation = new Vector3(0, 0, 0);
    for (const m of holder.getChildMeshes()) {
      const pbr = m.material;
      if (pbr instanceof PBRMaterial) {
        if (this.appearance === "neighborhood") { m.material = neighborhoodModelMaterial(this.scene, pbr, .04, vis.agent.state.id); m.alwaysSelectAsActiveMesh = true; continue; }
        const std = neighborhoodModelMaterial(this.scene, pbr, .04, vis.agent.state.id);
        if (pbr.albedoTexture) std.diffuseTexture = pbr.albedoTexture;
        std.specularColor = new Color3(0.04, 0.04, 0.04);
        std.backFaceCulling = false;
        m.material = std;
      }
      m.alwaysSelectAsActiveMesh = true; // skinned bounds are bind-pose only
    }
    holder.parent = vis.root;
    for (const m of vis.root.getChildMeshes()) {
      if (!m.isDescendantOf(holder)) m.isVisible = false;
    }
    const walk = inst.animationGroups[0] ?? null;
    if (walk) {
      // A standing figure is left on the rest pose the converter normalised
      // every rig to — arms down, legs straight. Starting the clip and
      // pausing it froze people mid-stride instead, so the per-frame update
      // returns the skeleton to rest whenever this person stops.
      vis.skeletons = [
        ...new Set(holder.getChildMeshes().flatMap((m) => (m.skeleton ? [m.skeleton] : []))),
      ];
      if (!standing) {
        walk.start(true, vis.motion.cadence, walk.from, walk.to);
        walk.goToFrame(walk.from + (walk.to - walk.from) * vis.motion.phase / (Math.PI * 2));
        vis.walk = walk;
      }
    }
    // The sensor cube map draws an explicit mesh list captured at level
    // build; this arrived after it.
    this.fisheye?.setRenderList(this.scene.meshes);
  }

  /**
   * A photographic prop, scaled by HEIGHT (ADR-063).
   *
   * `mountVehicleGlb` scales by footprint, which is right for a vehicle whose
   * length is what the sim collides with. A palm or a potted plant is defined
   * by how tall it stands, and these downloads arrive in arbitrary units —
   * the palm is 34.7 units, the scooter 763 — so the caller states the real
   * height in metres and this fits it.
   */
  private async mountDecorGlb(
    host: TransformNode,
    model: string,
    heightM: number,
    yaw = 0,
  ): Promise<void> {
    const stem = CreateCylinder(`${host.name}_stem`, { height: heightM * 0.7, diameter: model === "palm" ? 2 * TREE_TRUNK_RADIUS_M : 0.17, tessellation: 8 }, this.scene);
    stem.parent = host; stem.position.y = heightM * 0.35;
    stem.material = mat(this.scene, "plantStem", [0.37, 0.28, 0.2]);
    const crown = CreateSphere(`${host.name}_crown`, { diameter: heightM * (model === "palm" ? 0.48 : 0.65), segments: 6 }, this.scene);
    crown.parent = host; crown.position.y = heightM * 0.77;
    crown.scaling.y = 0.7;
    crown.material = mat(this.scene, "plantCrown", appearanceTheme(this.appearance).palette.vegetation!);
    const placeholders = [stem, crown];
    const path = themeAssetPath(this.appearance, "models", model);
    if (!appearanceTheme(this.appearance).policy.externalModels || !path) return;
    const url = `${import.meta.env.BASE_URL}${path}`;
    const generation = this.buildGeneration;
    const container = await this.themeModel(url);
    if (generation !== this.buildGeneration) return;
    if (!container || host.isDisposed()) return;
    const inst = container.instantiateModelsToScene((n) => n, false);
    const root = inst.rootNodes[0] as TransformNode | undefined;
    if (!root) return;
    const meshes = root.getChildMeshes();
    let min = new Vector3(Infinity, Infinity, Infinity);
    let max = new Vector3(-Infinity, -Infinity, -Infinity);
    root.computeWorldMatrix(true);
    for (const m of meshes) {
      m.computeWorldMatrix(true);
      const bb = m.getBoundingInfo().boundingBox;
      min = Vector3.Minimize(min, bb.minimumWorld);
      max = Vector3.Maximize(max, bb.maximumWorld);
    }
    const size = max.subtract(min);
    const k = heightM / Math.max(size.y, 1e-3);
    root.scaling.scaleInPlace(k);
    root.position.y -= min.y * k;
    root.position.x -= ((min.x + max.x) / 2) * k;
    root.position.z -= ((min.z + max.z) / 2) * k;
    root.rotation = new Vector3(0, yaw, 0);
    for (const m of meshes) {
      const pbr = m.material;
      if (pbr instanceof PBRMaterial) {
        // Kept as PBR — there is a sky to reflect now (ADR-061). Foliage
        // arrives with alpha cut-outs, which must survive the copy.
        pbr.roughness = Math.max(pbr.roughness ?? 0.5, 0.55);
        pbr.environmentIntensity = 0.7;
        if (pbr.albedoTexture) pbr.albedoTexture.hasAlpha = true;
      }
    }
    root.parent = host;
    for (const part of placeholders) part.dispose();
    for (const m of meshes) this.shadows?.addShadowCaster(m, false);
    this.fisheye?.setRenderList(this.scene.meshes);
  }

  private async mountBulldog(sim: Simulation): Promise<void> {
    if (this.robotModel === "box" || !appearanceTheme(this.appearance).policy.externalModels) return;
    const host = this.wobbleNode;
    const path = themeAssetPath(this.appearance, "models", this.robotModel);
    if (!path) return;
    const url = `${import.meta.env.BASE_URL}${path}`;
    const generation = this.buildGeneration;
    const container = await this.themeModel(url);
    if (generation !== this.buildGeneration) return;
    // The level may have been rebuilt while the download ran; attaching to a
    // disposed node would put an orphan robot at the origin of the new map.
    if (!container || host.isDisposed()) return;
    // The player may have switched models while this one downloaded.
    if (path !== themeAssetPath(this.appearance, "models", this.robotModel)) return;

    // Real clones, never the container's own nodes. `doNotInstantiate: true`
    // MOVES the source hierarchy into the scene, so the second mount from a
    // cached container strip-mined the first: a car's worth of wheels and
    // lamps was left orphaned at the world origin, sitting in the middle of
    // whatever map happened to contain (0, 0).
    const inst = container.instantiateModelsToScene((n) => n, false);
    const root = inst.rootNodes[0] as TransformNode | undefined;
    if (!root) return;

    // Measure BEFORE parenting, while the root sits at the world origin.
    // Bounding boxes come back in world space, and the first version measured
    // them after attaching to the robot — so the "centre the model" offset
    // included the robot's spawn position, and the Bulldog appeared parked
    // several metres to the side of the machine it was supposed to be.
    const meshes = root.getChildMeshes();
    let min = new Vector3(Infinity, Infinity, Infinity);
    let max = new Vector3(-Infinity, -Infinity, -Infinity);
    root.computeWorldMatrix(true);
    for (const m of meshes) {
      m.computeWorldMatrix(true);
      const bb = m.getBoundingInfo().boundingBox;
      min = Vector3.Minimize(min, bb.minimumWorld);
      max = Vector3.Maximize(max, bb.maximumWorld);
    }
    const size = max.subtract(min);
    const s = sim.robotProfile.length_m / Math.max(size.x, size.z, 1e-3);
    root.scaling.scaleInPlace(s);
    // Grounded: wheels on the road, centred on the sim's origin.
    root.position.y -= min.y * s;
    root.position.x -= ((min.x + max.x) / 2) * s;
    root.position.z -= ((min.z + max.z) / 2) * s;
    // glTF forward is -Z under Babylon's left-handed import; the sim drives
    // toward +Z. Verified by eye, not assumed — see ADR-046.
    if (this.appearance !== "neighborhood") root.rotation = new Vector3(0, Math.PI, 0);
    root.parent = host;

    // PBR materials expect an environment map this flat-lit scene does not
    // have and render near-black without one. Standard materials match the
    // world's look and the phones' budget; carry over the albedo and the
    // emissive (the face screen glows) and drop the rest.
    for (const m of meshes) {
      const pbr = m.material;
      if (pbr instanceof PBRMaterial) {
        if (this.appearance === "neighborhood") { m.material = neighborhoodModelMaterial(this.scene, pbr, 0, "robot"); continue; }
        const std = neighborhoodModelMaterial(this.scene, pbr, 0, "robot");
        if (pbr.albedoTexture) std.diffuseTexture = pbr.albedoTexture;
        std.emissiveColor = pbr.emissiveColor.clone();
        if (pbr.emissiveTexture) std.emissiveTexture = pbr.emissiveTexture;
        std.specularColor = Color3.Black();
        // The face is a DECAL: dark glass and eyes in a texture's alpha
        // channel, laid over the white shell. Dropping the alpha settings
        // turned it into an opaque white quad on a white body — an invisible
        // face. Carry the loader's alpha verdicts over wholesale.
        std.alpha = pbr.alpha;
        std.transparencyMode = pbr.transparencyMode;
        std.backFaceCulling = pbr.backFaceCulling;
        if (pbr.albedoTexture && pbr.useAlphaFromAlbedoTexture) {
          std.diffuseTexture!.hasAlpha = true;
          std.useAlphaFromDiffuseTexture = true;
        }
        m.material = std;
      }
    }

    // Wheels, found by the export's own names, so they can spin with the road.
    this.bulldogWheels = root.getChildTransformNodes().filter((n) => /wheel/i.test(n.name));

    for (const part of this.placeholderParts) part.isVisible = false;
    if (this.appearance === "neighborhood") for (const mesh of [...meshes, ...this.packageBox.getChildMeshes(), this.packageBox]) { mesh.receiveShadows = true; this.shadows?.addShadowCaster(mesh, false); }
    this.fisheye?.setRenderList(this.scene.meshes);
  }

  // ------------------------------------------------------------------
  // Agent visuals: reusable procedural modules per type.

  /** Lab pedestrian: body cylinder (origin at its center) with a head child. */
  private buildLabPersonMesh(name: string, rgb: Rgb): Mesh {
    const scene = this.scene;
    if (this.appearance === "neighborhood") {
      const host = new Mesh(name, scene);
      const person = new TransformNode(name + "Adult", scene); person.parent = host; person.position.y = -.85;
      this.buildSpecMeshes(person, personSpec(rgb), name, new Map(), new Map());
      return host;
    }
    const body = CreateCylinder(name, { diameter: PED_RADIUS * 2, height: 1.45, tessellation: 16 }, scene);
    body.material = mat(scene, `${name}Mat`, rgb);
    const head = CreateSphere(`${name}Head`, { diameter: 0.3, segments: 8 }, scene);
    head.position.y = 0.9;
    head.material = mat(scene, `${name}HeadMat`, SKIN);
    head.parent = body;
    return body;
  }

  /** Instantiate a part spec list under `root`; keyed parts/materials go to the maps. */
  private buildSpecMeshes(
    root: TransformNode,
    specs: readonly PartSpec[],
    id: string,
    parts: Map<string, TransformNode>,
    mats: Map<string, StandardMaterial>,
  ): void {
    const scene = this.scene;
    const local = new Map<string, StandardMaterial>();
    const person = specs.some(part => part.key === "legL");
    const vehicle = specs.some(part => part.key?.startsWith("wheel"));
    const roundedActor = person || specs === CAT_SPEC || specs === DOG_SPEC;
    for (const [i, p] of specs.entries()) {
      const name = `${id}_${i}`;
      let m: Mesh;
      if (p.shape === "box" && this.appearance === "neighborhood") m = neighborhoodRoundedBox(name, scene, roundedActor ? .30 : .13);
      else if (p.shape === "box" && this.appearance === "cartoon" && roundedActor) m = CreateSphere(name, { diameter: 1, segments: 6 }, scene);
      else if (p.shape === "box") m = CreateBox(name, { size: 1 }, scene);
      else if (p.shape === "cyl") m = CreateCylinder(name, { diameter: 1, height: 1, tessellation: 12 }, scene);
      else m = CreateSphere(name, { diameter: 1, segments: this.appearance === "neighborhood" ? 16 : 8 }, scene);
      m.scaling.set(p.size[0], p.size[1], p.size[2]);
      m.position.set(p.pos[0], p.pos[1], p.pos[2]);
      if (p.roll) m.rotation.z = p.roll;
      // A material per COLOUR, shared across every agent — except the ones
      // that are animated per agent (brake lights, indicators, headlamps),
      // which must stay private or one car braking lights up the street.
      //
      // Every part used to take a material named after its agent, so forty
      // people meant a couple of hundred near-identical materials and a
      // separate draw call for each. Invisible on a GPU; it cost 2-3 fps on
      // the software rasteriser, which is the closest thing here to a weak
      // phone and the device this all has to work on.
      const mk = p.matKey ?? `${p.shape}|${p.rgb[0]},${p.rgb[1]},${p.rgb[2]}`;
      let material = local.get(mk);
      if (!material) {
        const role = person && p.key === "body" ? "people" : vehicle && i === 0 ? "vehicles" : !person && !vehicle && i === 0 ? "props" : undefined;
        const color = role && this.appearance !== "realism" ? appearanceTheme(this.appearance).palette[role]! : p.rgb;
        material = mat(scene, p.rgb === SKIN ? `${id}_skin` : `${id}_${mk}`, color);
        local.set(mk, material);
        if (p.matKey) mats.set(p.matKey, material);
      }
      m.material = material;
      m.parent = root;
      if (p.key) parts.set(p.key, m);
    }
    if (this.appearance === "neighborhood") {
      const head = parts.get("head");
      if (head && person) {
        for (const side of [-1, 1]) {
          const eye = CreateSphere(`${id}_eye${side}`, { diameter: .016, segments: 8 }, scene);
          eye.position.set(side * .044, .018, .103); eye.material = mat(scene, "adventurePersonEyes", [.10,.13,.12]); eye.parent = root;
          eye.position.addInPlace(head.position);
          const hand = CreateSphere(`${id}_hand${side}`, { diameter: .081, segments: 12 }, scene);
          hand.position.set(side * .21, .98, .005); hand.scaling.y = 1.2; hand.material = mat(scene, `${id}_skin`, SKIN); hand.parent = root;
        }
        const nose = CreateSphere(`${id}_nose`, { diameter: .040, segments: 10 }, scene);
        nose.parent = root; nose.position.copyFrom(head.position).addInPlace(new Vector3(0,-.02,.112)); nose.material = mat(scene, `${id}_skin`, SKIN);
      }
      if (specs === DOG_SPEC || specs === CAT_SPEC) {
        for (const side of [-1,1]) {
          for (const z of [-.13,.13]) {
            const paw = neighborhoodRoundedBox(`${id}_paw${side}_${z}`, scene, .32);
            paw.scaling.set(.055,.13,.07); paw.position.set(side*.072,.07,z); paw.parent = root; paw.material = mat(scene, "adventurePetPaw", [.83,.73,.55]);
          }
          const eye = CreateSphere(`${id}_petEye${side}`, { diameter: .015, segments: 8 }, scene);
          eye.position.set(side*.044, specs === DOG_SPEC ? .35 : .29, .316); eye.parent = root; eye.material = mat(scene, "adventurePetEye", [.09,.10,.09]);
        }
        const nose = CreateSphere(`${id}_petNose`, { diameter: .028, segments: 8 }, scene);
        nose.position.set(0, specs === DOG_SPEC ? .31 : .26, .333); nose.parent = root; nose.material = mat(scene, "adventurePetNose", [.10,.12,.11]);
      }
    }
  }

  private buildAgentVisual(agent: AgentModel, sim: Simulation): AgentVisual {
    const scene = this.scene;
    const s = agent.state;
    const root = new TransformNode(`ag_${s.id}`, scene);
    const parts = new Map<string, TransformNode>();
    const mats = new Map<string, StandardMaterial>();
    const mirror = sim.level.mission?.mirror ?? 1;
    if (this.isAdventureBenchmark && (s.type === 'walker' || s.type === 'standing_person')) {
      const rig = buildAdventurePedestrian(scene, {
        carryingBag: true, loadState: themeState(scene),
        onReady: meshes => {
          for (const mesh of meshes) this.shadows?.addShadowCaster(mesh, false);
          this.fisheye?.setRenderList(this.scene.meshes);
        },
      });
      rig.root.parent = root;
      this.adventureActors.push(rig);
      this.addBlobShadow(root, s.radius * 3, s.radius * 2.5, .03);
      return { agent, type: s.type, root, parts, mats, adventureRig: rig,
        spin: 0, gesture: 0, walk: null, skeletons: [], shown: true,
        motion: actorMotionVariation(this.variationSeed, this.appearance, s.id) };
    }

    if (!/motorbike|motorcycle|car|bus/.test(s.type)) {
      this.addBlobShadow(root, s.radius * 5.0, s.radius * 4.4, 0.055);
    }
    switch (s.type) {
      case "research_ped":
        this.buildSpecMeshes(root, personSpec([0.55, 0.35, 0.3]), s.id, parts, mats);
        break;
      case "walker": {
        const shirt: Rgb =
          s.role === "group"
            ? GROUP_SHIRTS[Math.floor(hashStr(s.id) * GROUP_SHIRTS.length)]!
            : s.role === "oncoming"
              ? [0.4, 0.5, 0.6]
              : [0.58, 0.56, 0.5];
        this.buildSpecMeshes(root, personSpec(shirt, 0.95), s.id, parts, mats);
        break;
      }
      case "standing_person": {
        const shirt: Rgb = hashStr(s.id) < 0.5 ? [0.5, 0.45, 0.55] : [0.6, 0.5, 0.38];
        this.buildSpecMeshes(root, personSpec(shirt), s.id, parts, mats);
        // One arm on a shoulder pivot for the talking gesture.
        const pivot = new TransformNode(`${s.id}_arm`, scene);
        pivot.position.set(.205, 1.46, 0);
        pivot.parent = root;
        const arm = parts.get("armR");
        if (arm) { arm.parent = pivot; arm.position.set(0, -.22, 0); }
        parts.set("arm", pivot);
        break;
      }
      case "cat":
        this.buildSpecMeshes(root, CAT_SPEC, s.id, parts, mats);
        break;
      case "dog":
        this.buildSpecMeshes(root, DOG_SPEC, s.id, parts, mats);
        break;
      case "motorbike": {
        // Spec bike first, real Honda over it (ADR-050). The NPC exports come
        // with a seated rider baked in — áo chống nắng and all — which is why
        // they can carry moving traffic, not just parked rows.
        this.buildSpecMeshes(root, bikeSpec(1, [0.42, 0.46, 0.55], [0.36, 0.45, 0.42]), s.id, parts, mats);
        const bikes = ["bike_hondawave", "bike_hondaab", "bike_hondaactiva"];
        const len = s.body.length > 0 ? Math.max(...s.body) - Math.min(...s.body) + 2 * s.radius : 1.9;
        void this.mountVehicleGlb(root, bikes[Math.floor(hashStr(s.id) * bikes.length)]!, len, (glb) => {
          for (const m of root.getChildMeshes()) if (!m.isDescendantOf(glb)) { if (this.appearance === "neighborhood") m.dispose(); else m.isVisible = false; }
        });
        break;
      }
      case "motorcycle": {
        this.buildSpecMeshes(root, bikeSpec(1.25, [0.25, 0.26, 0.3], [0.3, 0.3, 0.34]), s.id, parts, mats);
        const len = s.body.length > 0 ? Math.max(...s.body) - Math.min(...s.body) + 2 * s.radius : 2.1;
        void this.mountVehicleGlb(root, "bike_exciter150", len, (glb) => {
          for (const m of root.getChildMeshes()) if (!m.isDescendantOf(glb)) { if (this.appearance === "neighborhood") m.dispose(); else m.isVisible = false; }
        });
        break;
      }
      case "car": {
        // Spec boxes appear instantly; the photographic car (Santafe or VF8,
        // by id hash) streams in over them and the boxes are hidden. The
        // brake/indicator light tricks only run on the spec materials, so a
        // real-model car simply does not flash — a smaller lie than a box.
        this.buildSpecMeshes(
          root,
          carSpec(CAR_TINTS[Math.floor(hashStr(s.id) * CAR_TINTS.length)]!, -0.35 * mirror),
          s.id,
          parts,
          mats,
        );
        const len = s.body.length > 0 ? Math.max(...s.body) - Math.min(...s.body) + 2 * s.radius : 4.5;
        // VF8 only: the Santafe's SVL export turned out to have no body mesh
        // at all — eight meshes of lamps and wheels around an empty shell.
        void this.mountVehicleGlb(root, "car_vf8", len, (glb) => {
          // Hide by exclusion AT ARRIVAL TIME, not from a list captured at
          // build time: some spec parts join the node after the capture, and
          // a car that keeps its placeholder wheels floats them through the
          // real bodywork.
          for (const m of root.getChildMeshes()) if (!m.isDescendantOf(glb)) { if (this.appearance === "neighborhood") m.dispose(); else m.isVisible = false; }
        });
        break;
      }
      case "truck":
        this.buildSpecMeshes(
          root,
          truckSpec(TRUCK_TINTS[Math.floor(hashStr(s.id) * TRUCK_TINTS.length)]!),
          s.id,
          parts,
          mats,
        );
        break;
      case "bus":
        // The Phenikaa XBus model is gone (ADR-058): it is a specific
        // branded campus shuttle, and a recognisable vehicle from somewhere
        // else is worse than a plain one. Buses keep the spec body, which is
        // the right shape and the right size and claims to be nobody.
        this.buildSpecMeshes(
          root,
          busSpec(BUS_TINTS[Math.floor(hashStr(s.id) * BUS_TINTS.length)]!),
          s.id,
          parts,
          mats,
        );
        break;
      case "vendor_cart": {
        this.buildSpecMeshes(root, CART_SPEC, s.id, parts, mats);
        const vendor = new TransformNode(`${s.id}_vendor`, scene);
        vendor.position.set(0, 0, -0.75);
        vendor.parent = root;
        this.buildSpecMeshes(vendor, personSpec([0.45, 0.5, 0.55]), `${s.id}_v`, parts, mats);
        parts.set("vendor", vendor);
        break;
      }
      default:
        break;
    }
    const motion = actorMotionVariation(this.variationSeed, this.appearance, s.id);
    const vis: AgentVisual = { agent, type: s.type, root, parts, mats, spin: motion.phase, gesture: 0, walk: null, skeletons: [], shown: true, motion };
    // People get the real rigged figure (ADR-056). The spec boxes stay until
    // it lands, then hide — same "hide by exclusion at arrival" the vehicles
    // use, so a slow load shows a crude person rather than no person.
    if (s.type === "walker" || s.type === "research_ped" || s.type === "standing_person") {
      void this.mountPersonGlb(vis, s.type === "standing_person");
    }
    return vis;
  }

  private buildDebugHelpers(sim: Simulation): void {
    const scene = this.scene;
    const wire = mat(scene, "debugWire", [0.9, 0.2, 0.2]);
    wire.wireframe = true;
    const robotCircle = CreateCylinder(
      "dbgRobotCircle",
      { diameter: sim.shape.effR * 2, height: 0.02, tessellation: 32 },
      scene,
    );
    robotCircle.position.y = 0.05;
    robotCircle.material = wire;
    robotCircle.parent = this.robotNode;
    // Commitment lines exist only on the straight lab levels.
    if (!sim.level.mission) {
      for (const side of [-1, 1]) {
        const line = this.track(
          CreateBox(`dbgCommit${side}`, { width: 0.02, height: 0.01, depth: sim.level.world.alleyLength + 10 }, scene),
        );
        line.position.set(side * 0.3, 0.02, sim.level.world.alleyLength / 2 - 2);
        line.material = wire;
      }
    }
  }

  // ------------------------------------------------------------------

  /** Sync meshes + camera to the sim state; call once per rendered frame. */
  /**
   * Remember where everything is, just before the simulation steps away from
   * it. Paired with `update(sim, alpha)` below — see the note there.
   */
  snapshot(sim: Simulation): void {
    // Mutated in place, never reallocated. This runs once per SIMULATION step
    // — up to three times a frame — and rebuilding a map of twenty poses that
    // often is real garbage on a weak device, which is exactly the device the
    // interpolation is for.
    const r = sim.robot;
    if (this.prevRobot) {
      this.prevRobot.x = r.x;
      this.prevRobot.z = r.z;
      this.prevRobot.heading = r.heading;
    } else {
      this.prevRobot = { x: r.x, z: r.z, heading: r.heading };
    }
    for (const vis of this.agentVisuals) {
      // Only what is on screen: a culled agent is not interpolated, so a pose
      // for it is work thrown away, and most of the cast is culled at any
      // moment. `shown` is last frame's answer, which is the right one — an
      // agent that appears this frame has no previous pose to move from and
      // is drawn at its true position anyway.
      if (!vis.shown) continue;
      const a = vis.agent.state;
      const prev = this.prevAgents.get(a.id);
      if (prev) {
        prev.x = a.x;
        prev.z = a.z;
        prev.heading = a.heading;
      } else {
        this.prevAgents.set(a.id, { x: a.x, z: a.z, heading: a.heading });
      }
    }
  }

  /**
   * Draw the world, interpolated between the last two simulation states.
   *
   * The simulation runs at a fixed 60 Hz; a modern phone draws at 90 or 120.
   * Drawing the raw simulation state on every frame therefore repeats each
   * pose: the step counts per rendered frame come out as `1 0 1 0 1 0`, the
   * robot moves on half the frames and stands still on the others, and what
   * the player sees is a constant shimmer — "giật giật", reported three times
   * across three rounds of work that were all looking for a single dropped
   * frame instead.
   *
   * `alpha` is how far the clock stands between the last step and the next
   * (accumulator / DT), so the rendered pose is where the robot actually is
   * at the instant being drawn. This is the standard companion to a fixed
   * timestep, and without it the timestep's own regularity is what shows.
   *
   * Only things that MOVE are interpolated. The world is static and the
   * camera already smooths itself.
   */
  /** The system-slowed underglow (ADR-065 amendment E): a calm ring under
   * the robot when the corridor or governor, not the player, sets the pace.
   * Edge-triggered — ~2 s on hold onset or tightening, then rest. */
  private updateSlowGlow(sim: Simulation): void {
    if (this.slowGlow === null) {
      const g = new GeomBuilder();
      g.addAnnulus(0, 0, 0.36, 0.56, 0, 28);
      const mesh = g.build("slowGlow", this.scene);
      const mat = new StandardMaterial("slowGlowMat", this.scene);
      mat.emissiveColor = new Color3(0.25, 0.55, 1.0);
      mat.diffuseColor = Color3.Black();
      mat.specularColor = Color3.Black();
      mat.disableLighting = true;
      mat.alpha = 0;
      mat.backFaceCulling = false;
      mesh.material = mat;
      mesh.isPickable = false;
      mesh.parent = this.robotNode;
      mesh.position.y = 0.09;
      this.slowGlow = mesh;
    }
    const cap = sim.corridor?.speedCap ?? Infinity;
    if (sim.systemSlowed && !sim.ended) {
      if (!this.prevSlowed || this.prevCap - cap > 0.6) {
        this.slowGlowUntil = sim.t + 2.2;
      }
    }
    this.prevSlowed = sim.systemSlowed && !sim.ended;
    this.prevCap = cap;
    const want = this.prevSlowed && sim.t < this.slowGlowUntil ? 1 : 0;
    const dtG = Math.min(0.1, this.engine.getDeltaTime() / 1000);
    this.slowGlowA += (want - this.slowGlowA) * (1 - Math.exp(-6 * dtG));
    (this.slowGlow.material as StandardMaterial).alpha =
      this.slowGlowA * (0.3 + 0.07 * Math.sin(sim.t * 3));
    this.slowGlow.setEnabled(this.slowGlowA > 0.02);
  }

  update(sim: Simulation, alpha = 1): void {
    const r = sim.robot;
    const pr = this.prevRobot;
    // A teleport is not motion: the safe-pose restore and the reverse recovery
    // move the robot bodily, and sliding it across the gap over one frame
    // would look far worse than the cut it replaces.
    const a = pr && Math.hypot(r.x - pr.x, r.z - pr.z) < TELEPORT_M ? alpha : 1;
    // THE pose for this frame. Everything that follows the robot — the mesh,
    // the chase camera, the agent culling distance — has to use this one.
    //
    // Drawing the robot interpolated while the camera followed `sim.robot`
    // meant the two disagreed by a fraction of a step on every frame, and
    // since the camera is what the whole picture is relative to, the robot
    // appeared to shiver in place the entire time it was moving. A smooth
    // object viewed from a stepping camera is a shaking object.
    // Free-cam owns the camera outright, and the guard has to sit ahead of BOTH
    // follow branches. Guarding only the smoothed-yaw branch was the bug: the
    // fixed-yaw one kept writing the position every frame, dragging the camera
    // back to the robot as fast as the viewer could move it away — which reads
    // as "it will not go past where the beacon was".
    const drawn = this.drawn;
    drawn.x = pr ? lerp(pr.x, r.x, a) : r.x;
    drawn.z = pr ? lerp(pr.z, r.z, a) : r.z;
    // Which photograph the robot is standing in, this frame.
    if (this.panoWorld) this.updatePanoStation(drawn.x, drawn.z);
    drawn.heading = pr ? lerpAngle(pr.heading, r.heading, a) : r.heading;
    this.robotNode.position.set(drawn.x, 0, drawn.z);
    this.robotNode.rotation.y = drawn.heading;
    // Living Safe Corridor (ADR-065): the painted lane, the waiting bay, and
    // the system-slowed underglow ride the interpolated pose for free (the
    // glow is parented to robotNode; the lane and bay are world-anchored).
    (this.corridorLayer ??= new CorridorLayer(this.scene, this.debug)).update(sim);
    this.updateSlowGlow(sim);
    this.settleIntoPit(sim);
    this.updateShadowFollowers();
    // Keep the sun's ortho box centred on the robot: a fixed box would run
    // out after 34 m and the shadows would simply stop.
    if (this.sun) {
      const d = this.sun.direction;
      this.sun.position.set(sim.robot.x - d.x * 90, -d.y * 90, sim.robot.z - d.z * 90);
    }

    const dt = Math.max(0, sim.t - this.prevT);
    this.prevT = sim.t;

    this.processEvents(sim);
    this.animateRobot(sim);
    this.updateLabPed(sim);
    this.updateAgents(sim, dt, a);

    // Destination pulse (render-only flourish; not part of the sim).
    const pulse = 1 + 0.06 * Math.sin(sim.t * 3);
    this.destMarker.scaling.set(pulse, 1, pulse);
    // Floating turn arrows bob gently.
    for (let i = 0; i < this.turnArrows.length; i++) {
      this.turnArrows[i]!.position.y = ARROW_HEIGHT + 0.15 * Math.sin(sim.t * 2.5 + i);
    }

    if (sim.level.mission) {
      // Follow the CONTROL FRAME yaw (already smoothed by the sim): above and
      // behind along the road direction, looking ahead of the robot.
      // Follow the robot's own heading, smoothed: the view turns because the
      // ROBOT turned, which is what a chase camera should do. While the robot
      // pivots in place the sim freezes its frame and we hold too, so the
      // player sees the robot rotate rather than the world spinning.
      const target = sim.frameFrozen ? this.camYaw : drawn.heading;
      if (!this.camYawInit) {
        this.camYaw = target;
        this.camYawInit = true;
      } else {
        const dt = Math.min(0.1, this.engine.getDeltaTime() / 1000);
        const alpha = 1 - Math.exp(-dt / CAM_TAU);
        this.camYaw = this.camYaw + wrapAngle(target - this.camYaw) * alpha;
      }
      const yaw = this.camYaw;
      const fx = Math.sin(yaw);
      const fz = Math.cos(yaw);
      // Keep the camera out of the buildings: on the roundabout the point
      // 8 m behind the robot along the ring tangent leaves the road, so pull
      // it in (never closer than 4.5 m) until it is over road or sidewalk.
      const net = sim.level.world.network;
      let back = this.isAdventureBenchmark ? 5.5 : CAM_BACK;
      if (net) {
        while (back > 4.5 && net.sdf(drawn.x - fx * back, drawn.z - fz * back) > 1.4) back -= 0.5;
      }
      this.camera.position.set(drawn.x - fx * back, this.isAdventureBenchmark ? 2.8 : CAM_UP, drawn.z - fz * back);
      const lookAhead = this.isAdventureBenchmark ? 4 : LOOK_AHEAD;
      this.camTarget.set(drawn.x + fx * lookAhead, this.isAdventureBenchmark ? .4 : LOOK_UP, drawn.z + fz * lookAhead);
    } else {
      // Fixed-yaw follow camera: above/behind, looking ahead of the robot.
      this.camera.position.set(drawn.x * 0.4, CAM_UP, drawn.z - CAM_BACK);
      this.camTarget.set(drawn.x * 0.4, LOOK_UP, drawn.z + LOOK_AHEAD);
    }
    this.camera.setTarget(this.camTarget);
    // The onboard camera is parented to the robot, so the engine has already
    // placed it; only its cube map needs re-centring.
    this.fisheye?.update();
  }

  private processEvents(sim: Simulation): void {
    for (; this.seenEvents < sim.events.length; this.seenEvents++) {
      const ev = sim.events[this.seenEvents];
      if (!ev) continue;
      if (ev.type === "recovery_started") this.wobbleStartT = sim.t;
      if (ev.type === "safe_pose_restore") {
        this.blinkStartT = sim.t;
        this.wobbleStartT = sim.t;
      }
      if (ev.type === "collision") this.wobbleStartT = sim.t;
      if (ev.type === "feedback") {
        if (ev.data?.["kind"] === "smooth") this.lastSmoothT = sim.t;
        else {
          this.lastAwkwardT = sim.t;
          this.wobbleStartT = sim.t;
        }
      }
      if (ev.type === "resume_began" && ev.data?.["clean"] === 1) {
        this.lastResumeT = sim.t;
      }
    }
  }

  /**
   * Drop and tip the robot into a hole it has fallen into (ADR-043).
   *
   * The cameras are parented to this node, so tipping it tips what the driver
   * and the sensor rig see — which is the entire point. A wheel in a pothole
   * is not an abstract event to be listed in a report; it is the picture
   * lurching down and over and stopping, and that picture is the thing the
   * rare-case footage exists to contain.
   *
   * The motion is a settle, not a bounce: down by the wheel radius, nose
   * first, rolling toward whichever side went in, easing out over PIT_SETTLE_S
   * and then holding. A robot in a hole does not spring back.
   */
  private settleIntoPit(sim: Simulation): void {
    const pit = sim.inPit;
    if (!pit) {
      // Ordinary driving: ride the ground (ADR-058). The body rises onto the
      // footway, noses up the face of a speed bump and drops off the back of
      // it, because the sim now samples a surface under each wheel instead of
      // treating the world as one flat plane.
      const ch = sim.chassis;
      this.robotNode.position.y = ch.height;
      this.robotNode.rotation.x = ch.pitch;
      this.robotNode.rotation.z = ch.roll;
      return;
    }
    const PIT_SETTLE_S = 0.55;
    const k = Math.min(1, Math.max(0, (sim.t - pit.t) / PIT_SETTLE_S));
    // Ease out: fastest at the lip, settling as it comes to rest.
    const e = 1 - (1 - k) * (1 - k);
    this.robotNode.position.y = -PIT_DROP_M * e;
    this.robotNode.rotation.x = PIT_PITCH_RAD * e;
    this.robotNode.rotation.z = pit.roll === null ? 0 : (pit.roll === "left" ? 1 : -1) * PIT_ROLL_RAD * e;
  }

  private animateRobot(sim: Simulation): void {
    if (this.adventureRobot) {
      this.adventureRobot.update(sim.t, sim.robot.speed);
      return;
    }
    // The real model's wheels turn with the road. Radius from the drawing's
    // 142 mm axle height; sign checked by eye against the direction of travel.
    if (this.bulldogWheels.length > 0 && sim.robot.speed > 1e-3) {
      const spin = (sim.robot.speed * this.scene.getEngine().getDeltaTime()) / 1000 / 0.142;
      for (const w of this.bulldogWheels) w.rotation.x += spin;
    }
    // Package wobble (recovery / awkward moments).
    if (this.wobbleStartT !== null) {
      const u = sim.t - this.wobbleStartT;
      if (u > 1.0) {
        this.wobbleStartT = null;
        this.wobbleNode.rotation.z = 0;
        this.packageBox.position.y = this.appearance === "neighborhood" ? .76 : .61;
      } else {
        const decay = Math.exp(-3 * u);
        this.wobbleNode.rotation.z = 0.16 * Math.sin(18 * u) * decay;
        this.packageBox.position.y = (this.appearance === "neighborhood" ? .76 : .61) + 0.05 * Math.abs(Math.sin(14 * u)) * decay;
      }
    }
    // Safe-pose blink.
    if (this.blinkStartT !== null) {
      const u = sim.t - this.blinkStartT;
      if (u > 0.45) {
        this.blinkStartT = null;
        this.wobbleNode.setEnabled(true);
      } else {
        this.wobbleNode.setEnabled(Math.floor(u / 0.09) % 2 === 0);
      }
    }

    // Tail mood: stiff in recovery, raised near the dog (with grip tighten),
    // wagging after a smooth clear, drooping after an awkward one.
    const dog = this.dogAgent?.state;
    const dogNear =
      !!dog && dog.active && Math.hypot(dog.x - sim.robot.x, dog.z - sim.robot.z) < 2.2;
    const t = sim.t;
    let tailX = 0.25; // idle perk
    let tailY = 0;
    let grip = 0;
    if (sim.recoveryMode !== "normal") {
      tailX = 0.02; // stiff
    } else if (dogNear) {
      tailX = 0.95; // raised high
      grip = 1;
    } else if (t - this.lastResumeT < 1.6) {
      // A clean yield-and-resume earns a wag: patience should FEEL right.
      tailX = 0.5;
      tailY = Math.sin(t * 14) * 0.6;
    } else if (t - this.lastSmoothT < 1.5) {
      tailX = 0.4;
      tailY = Math.sin(t * 16) * 0.55; // happy wag
    } else if (t - this.lastAwkwardT < 1.6) {
      tailX = -0.45; // droop
    }
    this.tailNode.rotation.x = this.tailNode.rotation.x * 0.8 + tailX * 0.2;
    this.tailNode.rotation.y = tailY;
    // Grip tighten: arms squeeze inward on the box.
    const armIn = grip ? 0.015 : 0;
    this.armL.position.x = -0.22 + armIn;
    this.armR.position.x = 0.22 - armIn;
  }

  private updateLabPed(sim: Simulation): void {
    if (!this.pedMesh) return;
    const ped = sim.pedModel?.state ?? null;
    if (ped && ped.active) {
      this.pedMesh.setEnabled(true);
      this.pedMesh.position.set(ped.x, 0.85, ped.z);
    } else {
      this.pedMesh.setEnabled(false);
    }
  }

  private updateAgents(sim: Simulation, dt: number, alpha: number): void {
    const r = sim.robot;
    const t = sim.t;
    const cull2 = AGENT_DRAW_DIST * AGENT_DRAW_DIST;
    for (const vis of this.agentVisuals) {
      const s = vis.agent.state;
      const dx = s.x - r.x;
      const dz = s.z - r.z;
      const show = s.active && dx * dx + dz * dz < cull2;
      if (show !== vis.shown) {
        vis.root.setEnabled(show);
        vis.shown = show;
      }
      if (!show) continue;
      const prev = this.prevAgents.get(s.id);
      const pa = prev && Math.hypot(s.x - prev.x, s.z - prev.z) < TELEPORT_M ? alpha : 1;
      if (prev && pa < 1) {
        vis.root.position.set(lerp(prev.x, s.x, pa), 0, lerp(prev.z, s.z, pa));
        vis.root.rotation.y = lerpAngle(prev.heading, s.heading, pa);
      } else {
        vis.root.position.set(s.x, 0, s.z);
        vis.root.rotation.y = s.heading;
      }

      if (vis.adventureRig) {
        vis.adventureRig.update(sim.t, Math.hypot(s.vx, s.vz));
        continue;
      }
      // Cue-phase animations (readable warnings before movement).
      const p = vis.parts;
      const ph = s.cuePhase;
      switch (vis.type) {
        case "motorbike":
        case "motorcycle": {
          const preMove = ph === 1 || ph === 2;
          const head = p.get("head");
          if (head) head.rotation.y = preMove ? -0.9 : 0; // looks over shoulder
          const bars = p.get("bars");
          if (bars) bars.rotation.y = ph === 2 ? 0.35 : 0;
          const leg = p.get("leg");
          // Foot up once rolling (the leg is a unit box scaled to 0.5 * k).
          if (leg) leg.position.y = (ph >= 3 ? 0.96 : 0.7) * leg.scaling.y;
          this.spinWheels(vis, s.vx, s.vz, dt, 0.22, "wheelF", "wheelB");
          const wf = p.get("wheelF");
          if (wf) wf.rotation.y = ph === 2 ? 0.4 : 0;
          this.flashHeadlight(vis, ph === 4, t);
          break;
        }
        case "bus":
        case "truck":
        case "car": {
          const preMove = ph === 1 || ph === 2;
          const reverse = vis.mats.get("reverse");
          if (reverse) {
            const reversing = s.vx * Math.sin(s.heading) + s.vz * Math.cos(s.heading) < -.02;
            reverse.emissiveColor.setAll(reversing || (s.role === "reversing" && preMove) ? .85 : 0);
          }
          const brake = vis.mats.get("brake");
          if (brake) {
            if (preMove) brake.emissiveColor.copyFromFloats(0.85, 0.12, 0.08);
            else brake.emissiveColor.copyFromFloats(0, 0, 0);
          }
          // Turn indicator toward the lane (left in the logical frame) blinks while pulling out.
          const mirror = sim.level.mission?.mirror ?? 1;
          const ind = vis.mats.get(mirror > 0 ? "indL" : "indR");
          if (ind) {
            const on = preMove && Math.floor(t * 3) % 2 === 0;
            if (on) ind.emissiveColor.copyFromFloats(0.9, 0.6, 0.1);
            else ind.emissiveColor.copyFromFloats(0, 0, 0);
          }
          const head = p.get("head");
          if (head) head.rotation.y = ph === 2 ? (mirror > 0 ? -1.0 : 1.0) : 0; // checks the mirror/shoulder
          this.spinWheels(vis, s.vx, s.vz, dt, 0.32, "wheel0", "wheel1", "wheel2", "wheel3", "wheel4", "wheel5");
          this.flashHeadlight(vis, ph === 4, t);
          break;
        }
        case "vendor_cart": {
          const vendor = p.get("vendor");
          if (vendor) {
            vendor.rotation.x = ph >= 1 ? 0.18 : 0; // leans into the push
            vendor.rotation.y = ph >= 4 ? -0.6 : 0; // looks at the robot
          }
          this.spinWheels(vis, s.vx, s.vz, dt, 0.15, "wheelL", "wheelR");
          break;
        }
        case "dog": {
          const tail = p.get("tail");
          if (tail) tail.rotation.y = ph >= 1 ? Math.sin(t * 18) * 0.6 : 0;
          const perk = ph >= 1 ? 1.35 : 1;
          const earL = p.get("earL");
          const earR = p.get("earR");
          if (earL) earL.scaling.y = 0.09 * perk;
          if (earR) earR.scaling.y = 0.09 * perk;
          // Excited hop while playing.
          if (ph === 2) vis.root.position.y = Math.abs(Math.sin(t * 10)) * 0.05;
          break;
        }
        case "cat": {
          const tail = p.get("tail");
          if (ph === 1) {
            vis.root.scaling.y = 0.7; // crouch
            if (tail) tail.rotation.y = Math.sin(t * 14) * 0.5; // tail twitch
          } else if (ph === 2) {
            vis.root.scaling.y = 1;
            vis.root.position.y = Math.abs(Math.sin(t * 14)) * 0.04; // dash bound
            if (tail) tail.rotation.y = 0;
          } else if (ph === 3) {
            vis.root.scaling.y = 0.55; // hiding flat
            if (tail) tail.rotation.y = 0;
          } else {
            vis.root.scaling.y = 1; // sitting
            if (tail) tail.rotation.y = Math.sin(t * 1.5) * 0.15;
          }
          break;
        }
        case "standing_person": {
          // Idle conversation has its own restrained rhythm per person. It is
          // decorative and must not alter any research pedestrian cue.
          const target = idleGesture(t, vis.motion);
          vis.gesture += (target - vis.gesture) * Math.min(1, dt * 10);
          const arm = p.get("arm");
          if (arm) arm.rotation.x = -vis.gesture;
          const head = p.get("head");
          if (head) head.rotation.x = vis.gesture * .12;
          vis.root.rotation.z = Math.sin(t * vis.motion.idleRate + vis.motion.phase) * .008;
          break;
        }
        case "walker":
        case "research_ped": {
          // A walk cycle, paced by how fast this person is actually going
          // rather than by wall-clock: a figure whose legs move at a fixed
          // rate while its body crawls or races reads as sliding, which is
          // the thing legs were added to stop.
          const speed = Math.hypot(s.vx, s.vz);
          if (vis.walk) {
            // The rigged figure's clip is one two-step cycle per second at a
            // 1.4 m/s walk; drive its rate off the speed the agent is really
            // making so the feet do not skate. A STOPPED person goes back to
            // the rest pose — a paused clip left them frozen mid-stride, one
            // foot in the air, "pretending to walk" in the middle of the
            // road while they waited.
            if (speed > 0.08) {
              if (!vis.walk.isPlaying) vis.walk.play(true);
              vis.walk.speedRatio = Math.max(0.35, Math.min(2.2, speed / 1.4 * vis.motion.cadence));
            } else if (vis.walk.isPlaying) {
              vis.walk.pause();
              for (const sk of vis.skeletons) sk.returnToRest();
            }
            break;
          }
          if (speed > 0.05) vis.spin += (speed * dt) / 0.42 * vis.motion.cadence; // stride length
          // Gait fades out with speed so a stopped figure stands with legs
          // together instead of frozen mid-stride.
          const gait = Math.min(1, speed / 0.5);
          const swing = Math.sin(vis.spin) * Math.min(0.55, 0.25 + speed * 0.2) * gait * vis.motion.amplitude;
          const legL = p.get("legL");
          const legR = p.get("legR");
          const armL = p.get("armL");
          const armR = p.get("armR");
          if (legL) legL.rotation.x = swing;
          if (legR) legR.rotation.x = -swing;
          // Arms counter-swing; a walk with both sides in phase looks wrong
          // even to someone who could not say why.
          if (armL) armL.rotation.x = -swing * 0.55;
          if (armR) armR.rotation.x = swing * 0.55;
          const footL = p.get("footL");
          const footR = p.get("footR");
          if (footL) footL.position.z = 0.03 + Math.sin(vis.spin) * 0.16 * gait;
          if (footR) footR.position.z = 0.03 - Math.sin(vis.spin) * 0.16 * gait;
          // The bob rides the same cycle, so it rises on the stride rather
          // than on a timer of its own.
          vis.root.position.y = s.moving ? Math.abs(Math.sin(vis.spin)) * 0.025 : 0;
          if (s.type === "walker") vis.root.rotation.z = speed < .05 ? Math.sin(t * vis.motion.idleRate + vis.motion.phase) * .006 : 0;
          break;
        }
        default:
          break;
      }
    }
  }

  /** Spin wheel parts by the agent's ground speed (render-only integration). */
  private spinWheels(vis: AgentVisual, vx: number, vz: number, dt: number, radius: number, ...keys: string[]): void {
    const speed = Math.hypot(vx, vz);
    if (speed < 1e-3) return;
    vis.spin += (speed * dt) / radius;
    for (const k of keys) {
      const w = vis.parts.get(k);
      if (w) w.rotation.x = vis.spin;
    }
  }

  /** Phase 4 (horn/warning): the headlight flashes. */
  private flashHeadlight(vis: AgentVisual, warning: boolean, t: number): void {
    const m = vis.mats.get("lamp");
    if (!m) return;
    if (warning && Math.floor(t * 8) % 2 === 0) m.emissiveColor.copyFromFloats(0.9, 0.9, 0.7);
    else m.emissiveColor.copyFromFloats(0, 0, 0);
  }

  start(renderCb: () => void): void {
    this.engine.runRenderLoop(() => {
      renderCb();
      this.scene.render();
    });
  }
}
